import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  HostListener,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../confirm-dialog/confirm-dialog.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCalendar } from '@angular/material/datepicker';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ChatService } from 'src/app/services/chat.service';
import { ChatInputSubmitEvent } from '../../chat-input/chat-input.component';
import { ChatScreenComponent } from '../../chat-screen/chat-screen.component';
import { MuxAgentService } from 'src/app/services/mux-agent.service';
import { ModelPoolService } from '../../../services/model-pool.service';
import { MuxCredentialService } from 'src/app/services/mux-credential.service';
import { ClaudeAuthWizardService } from 'src/app/services/claude-auth-wizard.service';
import { MuxSessionService, parsePlaceAddress } from 'src/app/services/mux-session.service';
import { MuxUsageService } from 'src/app/services/mux-usage.service';
import { ConfigService } from 'src/app/services/config.service';
import { WsTicketService } from 'src/app/services/ws-ticket.service';
import { WidgetService } from 'src/app/services/widget.service';
import { SettingsService } from 'src/app/services/settings.service';
import { WidgetContextService } from 'src/app/services/widget-context.service';
import { DynamicWidgetsService } from 'src/app/services/dynamic-widgets.service';
import { KeycloakService } from 'keycloak-angular';
import { TtsFilter, stripToolMarkup, speakableSoFar, isChromeSentence } from './tts-filter';

type NavView = 'dm' | 'channel';

interface HeartbeatMessage {
  id: string;
  content: string;
  timestamp: string;
  direction: 'in' | 'out';
  role: 'user' | 'assistant';
  from?: string;
  read?: boolean;
  delivered?: boolean;
  pinned?: boolean;
  reactions?: { emoji: string; reactor: 'user' | 'assistant'; timestamp: string }[];
  metadata?: Record<string, any>;
  file_path?: string;
}

type Presence = 'online' | 'busy' | 'idle' | 'offline';

type AgentThrottle = 'disabled' | 'enabled' | 'safe';

interface AgentHeartbeatInfo {
  agent: MuxAgentService.Agent;
  status?: 'off' | 'awake' | 'sleeping';
  heartbeatCount?: number;
  lastSleepTime?: string;
  lastWakeTime?: string;
  sleepReason?: string;
  presence?: Presence;
  presenceDetail?: string;
  throttle: AgentThrottle;
  ambientMode?: boolean;
}

@Component({
  selector: 'app-heartbeat-widget',
  templateUrl: './heartbeat-widget.component.html',
  styleUrls: ['../base-widget/base-widget.component.scss', './heartbeat-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeartbeatWidgetComponent extends BaseWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(ChatScreenComponent) chatScreen: ChatScreenComponent;
  @ViewChild('embeddedGraph') embeddedGraph: any;
  @ViewChild(MatCalendar) matCalendar: MatCalendar<Date> | undefined;
  @Output() onOpenAgentSettings = new EventEmitter<{ agentId: string | null }>();
  @Output() onOpenWorkSession = new EventEmitter<{ sessionId: string; agentId: string; title: string; channelId?: string; warmUp?: string }>();

  // --- DM state ---
  agents: AgentHeartbeatInfo[] = [];
  selectedAgentId: string | null = null;
  messages: ChatService.ChatMessage[] = [];
  readMessageIds = new Set<string>();
  loading = false;
  sending = false;
  sidebarOpen = localStorage.getItem('lit-heartbeat-sidebar-open') !== 'false';
  private _sidebarWidth = 220;
  get sidebarWidth(): number { return this._sidebarWidth; }
  set sidebarWidth(v: number) { this._sidebarWidth = v; }

  // --- Input area resize ---
  private _inputAreaHeight: number | null = null; // null = auto
  get inputAreaHeight(): number | null { return this._inputAreaHeight; }
  set inputAreaHeight(v: number | null) { this._inputAreaHeight = v; }
  statusText: string | null = null;
  private streamingMessageIndex: number | null = null;
  private streamingSource: string | null = null;

  // --- Ambient listening / hands-free state ---
  private recognition: any = null;
  private ambientModeSettingsSub?: Subscription;
  private _ttsSynth = typeof window !== 'undefined' ? window.speechSynthesis : null;
  private _ttsSentenceBuffer = '';
  private _ttsActive = false;
  private readonly _ttsFilter = new TtsFilter();
  private _ttsFirstUtterance = true;
  /** "Stop talking" pressed mid-reply: the rest of THIS turn is dropped, not just the queue. */
  private _ttsMuted = false;
  handsFreeActive = false;
  handsFreeState: 'listening' | 'thinking' | 'speaking' | 'paused' | 'replaying' = 'listening';
  handsFreeTranscript = '';
  handsFreeAgentName = '';
  private _lastAssistantMessage = '';
  private _wakeLock: any = null;
  _ttsPausedFrom: 'speaking' | 'replaying' | null = null;
  private readonly _ttsSpeedSteps = [0.8, 1.0, 1.2, 1.5, 2.0];
  private _ttsSpeedIndex = 2; // default 1.2x
  _ttsRate = 1.2;
  handsFreeSpeedToast = '';
  private _speedToastTimeout: any = null;

  // --- Controls state ---
  isSafeModeEnabled = false;
  isInterrupted = false;

  // --- Channel path + header chips ---
  channelPath: string | null = null;

  // --- Agent visibility (per-team tab filter & order) ---
  private static AGENT_VIS_KEY_PREFIX = 'lit-agent-visibility';
  private _agentHidden = new Set<string>();
  private _agentOrder: string[] = [];

  get visibleAgents(): AgentHeartbeatInfo[] {
    const visible = this.agents.filter(a => !this._agentHidden.has(a.agent.id));
    if (this._agentOrder.length === 0) return visible;
    return visible.sort((a, b) => {
      const ai = this._agentOrder.indexOf(a.agent.id);
      const bi = this._agentOrder.indexOf(b.agent.id);
      const aIdx = ai >= 0 ? ai : 9999;
      const bIdx = bi >= 0 ? bi : 9999;
      return aIdx - bIdx;
    });
  }

  get orderedAgents(): AgentHeartbeatInfo[] {
    if (this._agentOrder.length === 0) return this.agents;
    return [...this.agents].sort((a, b) => {
      const ai = this._agentOrder.indexOf(a.agent.id);
      const bi = this._agentOrder.indexOf(b.agent.id);
      const aIdx = ai >= 0 ? ai : 9999;
      const bIdx = bi >= 0 ? bi : 9999;
      return aIdx - bIdx;
    });
  }

  isAgentVisible(agentId: string): boolean {
    return !this._agentHidden.has(agentId);
  }

  toggleAgentVisibility(agentId: string, event: Event): void {
    event.stopPropagation();
    if (this._agentHidden.has(agentId)) {
      this._agentHidden.delete(agentId);
    } else {
      this._agentHidden.add(agentId);
    }
    this._saveAgentVisibility();
    this.cdr.markForCheck();
  }

  moveAgent(agentId: string, direction: number, event: Event): void {
    event.stopPropagation();
    const ids = this.orderedAgents.map(a => a.agent.id);
    const idx = ids.indexOf(agentId);
    if (idx < 0) return;
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= ids.length) return;
    [ids[idx], ids[targetIdx]] = [ids[targetIdx], ids[idx]];
    this._agentOrder = ids;
    this._saveAgentVisibility();
    this.cdr.markForCheck();
  }

  private _agentVisKey(): string {
    const suffix = this.teamName || 'default';
    return `${HeartbeatWidgetComponent.AGENT_VIS_KEY_PREFIX}-${suffix}`;
  }

  private _loadAgentVisibility(): void {
    try {
      const raw = localStorage.getItem(this._agentVisKey());
      if (raw) {
        const data = JSON.parse(raw);
        this._agentHidden = new Set(data.hidden || []);
        this._agentOrder = data.order || [];
      } else {
        this._agentHidden = new Set();
        this._agentOrder = [];
      }
    } catch {
      this._agentHidden = new Set();
      this._agentOrder = [];
    }
  }

  private _saveAgentVisibility(): void {
    const data = {
      hidden: [...this._agentHidden],
      order: this._agentOrder.length > 0 ? this._agentOrder : this.agents.map(a => a.agent.id),
    };
    localStorage.setItem(this._agentVisKey(), JSON.stringify(data));
  }

  // --- Orchestration state ---
  orchestrationPanelOpen = false;
  orchestrationList: any[] = [];
  // Orchestrations the channel agent / app may start via a chat `start_orchestration`
  // action (AICS-260). Gated here because POST /channels/{id}/actions is unauthenticated
  // yet the start runs under the connected user's authenticated session — so we only
  // ever start an approved slug. Add new chat-startable orchestrations here.
  private static readonly CHAT_STARTABLE_ORCHESTRATIONS = ['alms_guide_batch'];
  orchestrationSelectedSlug: string | null = null;
  orchestrationSchema: any = null;
  orchestrationParams: Record<string, any> = {};
  orchestrationStatus: {
    active: boolean;
    name?: string;
    slug?: string;
    state?: string;
    step?: string;
    progress?: string;
    error?: string;
    gate_prompt?: string;
    gate_options?: string[];
  } = { active: false };
  orchestrationGateNotes = '';

  // --- Channel header favorites (same pattern as chat-screen message header) ---
  private static CH_FAVORITES_KEY = 'lit-channel-header-favorites';
  private static CH_ORDER_KEY = 'lit-channel-header-order';
  private static CH_DEFAULT_FAVORITES = ['pins'];
  private static CH_DEFAULT_ORDER = ['files', 'copy_path', 'pins', 'rename', 'archive'];
  private static CH_ICONS: Record<string, string> = {
    files: 'folder_open', copy_path: 'content_copy', pins: 'search',
    rename: 'edit', archive: 'archive',
  };
  private static CH_LABELS: Record<string, string> = {
    files: 'Open project folder', copy_path: 'Copy path', pins: 'Search',
    rename: 'Rename channel', archive: 'Archive channel',
  };

  channelHeaderFavorites: Set<string>;
  channelHeaderOrder: string[];

  isChFavorite(id: string): boolean { return this.channelHeaderFavorites.has(id); }

  toggleChFavorite(id: string, event: Event): void {
    event.stopPropagation();
    if (this.channelHeaderFavorites.has(id)) {
      this.channelHeaderFavorites.delete(id);
    } else {
      this.channelHeaderFavorites.add(id);
    }
    localStorage.setItem(HeartbeatWidgetComponent.CH_FAVORITES_KEY, JSON.stringify([...this.channelHeaderFavorites]));
    this.cdr.markForCheck();
  }

  getChIcon(id: string): string { return HeartbeatWidgetComponent.CH_ICONS[id] || ''; }
  getChLabel(id: string): string { return HeartbeatWidgetComponent.CH_LABELS[id] || ''; }

  isChItemVisible(id: string): boolean {
    switch (id) {
      case 'files': return !!this.selectedChannelFolderPath;
      case 'copy_path': return true;
      case 'pins': return true;
      case 'rename': return true;
      case 'archive': return true;
      default: return false;
    }
  }

  onChMenuAction(id: string): void {
    switch (id) {
      case 'files': this.openChannelFiles(); break;
      case 'copy_path': this.$copyChannelPath(); break;
      case 'pins': this.togglePinsPane(); break;
      case 'rename': this.renameCurrentChannel(); break;
      case 'archive': this.archiveCurrentChannel(); break;
    }
  }

  moveChItem(id: string, direction: number, event: Event): void {
    event.stopPropagation();
    const arr = this.channelHeaderOrder;
    const idx = arr.indexOf(id);
    if (idx < 0) return;
    let targetIdx = idx + direction;
    while (targetIdx >= 0 && targetIdx < arr.length) {
      if (this.isChItemVisible(arr[targetIdx])) break;
      targetIdx += direction;
    }
    if (targetIdx < 0 || targetIdx >= arr.length) return;
    [arr[idx], arr[targetIdx]] = [arr[targetIdx], arr[idx]];
    localStorage.setItem(HeartbeatWidgetComponent.CH_ORDER_KEY, JSON.stringify(arr));
    this.cdr.markForCheck();
  }

  // --- Pinned messages / Knowledge graph ---
  pinsPaneOpen = false;
  pinnedMessages: MuxSessionService.PinnedMessage[] = [];
  get pinnedCount(): number { return this.pinnedMessages.length; }
  private _pinsPanePercent: number = 30;
  get pinsPanePercent(): number { return this._pinsPanePercent; }
  set pinsPanePercent(value: number) { this._pinsPanePercent = Math.max(15, Math.min(50, value)); }
  pinsPaneView: 'graph' | 'messages' | 'search' | 'calendar' | 'calendar-messages' | 'pinned' = 'graph';
  graphSelectedNode: { id: string; type: string; label: string; count: number; messages: { ref: string; excerpt: string; message_id?: string }[] } | null = null;
  graphNodeMessages: { ref: string; excerpt: string; content: string | null; loading: boolean; messageId?: string; channel?: string }[] = [];
  graphNodeMessagesLoading = false;
  graphMessageFilter = '';
  private graphObserver: IntersectionObserver | null = null;
  graphScopeAll = false;

  // --- Text search state ---
  searchQuery = '';
  searchResults: { ref: string; excerpt: string; content: string | null; loading: boolean; messageId?: string; channel?: string }[] = [];
  searchResultCount: number | null = null;
  searchLoading = false;
  searchIsRegex = false;
  private searchObserver: IntersectionObserver | null = null;
  private searchDebounceTimer: any = null;

  // --- Calendar state ---
  calendarDates: { [date: string]: number } = {};
  calendarLoading = false;
  calendarSelectedDate: string | null = null;
  calendarSelectedHour: number | null = null;
  calendarDayMessages: { timestamp: string; from: string; id: string; direction: string; filename: string }[] = [];
  calendarDayLoading = false;
  calendarViewMessages: { ref: string; excerpt: string; content: string | null; loading: boolean; messageId?: string; channel?: string }[] = [];
  private calendarObserver: IntersectionObserver | null = null;

  get filteredGraphNodeMessages(): typeof this.graphNodeMessages {
    if (!this.graphMessageFilter.trim()) return this.graphNodeMessages;
    const q = this.graphMessageFilter.toLowerCase();
    return this.graphNodeMessages.filter(m =>
      m.excerpt.toLowerCase().includes(q) || m.ref.toLowerCase().includes(q) ||
      (m.content && m.content.toLowerCase().includes(q))
    );
  }

  // --- Pagination state ---
  hasMoreMessages = false;
  loadingMoreMessages = false;
  hasMoreChannelMessages = false;
  loadingMoreChannelMessages = false;
  viewingChannelHistory = false;

  // --- Channel state ---
  activeView: NavView = 'dm';
  personalChannels: MuxSessionService.NavChannel[] = [];
  streamingChannels = new Set<string>();
  /** Running background agents per channel id (rows under the channel in the list). */
  backgroundAgents: Record<string, MuxSessionService.BackgroundAgent[]> = {};
  /** The background agent whose stream is open over its channel (read-only), or null. */
  bgAgentView: {
    agent: MuxSessionService.BackgroundAgent;
    channelId: string;
    channelName: string;
    task: string;
    messages: ChatService.ChatMessage[];
    next: number;
    finished: boolean;
    skipped: number;
    error: string;
  } | null = null;
  private _bgAgentTimer: any = null;
  /** The name row open in the channel list for a channel being created, or null.
   *  parentId = the channel it goes under; null = a top-level channel. In the
   *  list, not a browser prompt(): Chrome returned from prompt() without showing
   *  it and the click did nothing (2026-10-01). */
  channelDraft: { parentId: string | null; name: string; saving: boolean; openedAt: number } | null = null;
  channelUnreadResponses = new Set<string>();
  selectedChannelId: string | null = null;
  selectedChannelName: string | null = null;
  selectedChannelDir: string | null = null;
  selectedChannelFolderPath: string | null = null;
  selectedChannelAgentId: string | null = null;
  selectedChannelAgentName: string | null = null;
  channelMessages: ChatService.ChatMessage[] = [];
  channelLoading = false;
  channelLoadingState: 'connecting' | 'generating' | 'stalled' | '' = '';
  // Raw terminal spinner line relayed from the bridge during the pre-body think-gap,
  // shown verbatim in the channel bubble. Cleared once content streams or the turn ends.
  channelSpinnerText = '';
  channelSending = false;
  channelTerminalMode = false;

  /** Last view mode per channel ('terminal' | 'chat'), so switching channels
   *  lands in the mode the user left that channel in. Team-scoped, persisted
   *  beside the selected-channel key so a reload restores it too. */
  private _channelModeKey(): string {
    return this.teamName ? `lit-heartbeat-channel-mode-${this.teamName}` : 'lit-heartbeat-channel-mode';
  }
  private _rememberChannelMode(channelId: string, terminal: boolean): void {
    try {
      const modes = JSON.parse(localStorage.getItem(this._channelModeKey()) || '{}');
      if (terminal) modes[channelId] = 'terminal'; else delete modes[channelId];
      localStorage.setItem(this._channelModeKey(), JSON.stringify(modes));
    } catch { /* storage unavailable — mode is simply not remembered */ }
  }
  private _channelWasInTerminal(channelId: string): boolean {
    try {
      return JSON.parse(localStorage.getItem(this._channelModeKey()) || '{}')[channelId] === 'terminal';
    } catch { return false; }
  }
  channelTmuxCommand = '';
  /** The bridge's screen: a fixed 200×50 grid. The channel terminal must hold at least that. */
  readonly bridgeGrid = { cols: 200, rows: 50 };
  terminalButtonThrob = false;
  expandedSections = new Set(['_personal', '_dm']);
  /** Channels where we've seen messages from another user. */
  private sharedChannels = new Set<string>();
  primaryAgentId: string | null = null;
  usageReport$ = this._muxUsage.usage$;
  usageCache = new Map<string, MuxUsageService.UsageReport>();
  backendModels = new Map<string, { name: string; display_name: string }[]>();
  modelConstraints: Record<string, string[]> = {};
  credentials: MuxCredentialService.Credential[] = [];
  /** Per-agent model override for the currently-open channel (agentId → model). */
  channelModelOverrides: Record<string, string> = {};
  /** Host affinity: the hosts this channel prefers, in order (home implicit last). */
  channelAffinity: string[] = [];
  /** Hosts this node knows (hosts.json) with liveness; empty on a node with no linked hosts → no control shown. */
  affinityHosts: MuxSessionService.AffinityHost[] = [];
  affinityHome = '';
  /** The team's say (rung 6): home-only hides the chip, hosts-only pins it to a computer. */
  affinityPolicy: MuxSessionService.AffinityPolicy = { mode: 'user', linking: 'anyone', default: 'home' };
  private _affinityFor: string | null = null;

  get parentChannels(): MuxSessionService.NavChannel[] {
    return this.personalChannels.filter(c => !c.parent_channel_id);
  }

  get childChannels(): MuxSessionService.NavChannel[] {
    return this.personalChannels.filter(c => !!c.parent_channel_id);
  }

  getChildChannels(parent: MuxSessionService.NavChannel): MuxSessionService.NavChannel[] {
    return this.personalChannels.filter(c => c.parent_channel_id === parent.id);
  }

  private ws: WebSocket | null = null;
  private channelWs: WebSocket | null = null;
  private _telemetryWs: WebSocket | null = null;
  private channelStreamingIndex: number | null = null;

  /**
   * Display order for loaded channel history.
   *
   * 1. A reply is stored when it finishes, after any messages that arrived
   *    during the turn, but it carries the turn's START time. Move each reply
   *    back to its start time so a mid-turn message the agent did not fold in
   *    reads as pending below the reply (the same order the live view shows).
   * 2. A message the agent folded into a reply (`handled_in` metadata) sits
   *    just above that reply, so the user sees it as dealt with.
   * Pure reorder; storage and timestamps are untouched.
   */
  static orderChannelHistory(msgs: ChatService.ChatMessage[]): ChatService.ChatMessage[] {
    const out = [...msgs];
    for (let i = 0; i < out.length; i++) {
      const reply = out[i];
      if (reply.isSelf || !reply.timestamp) continue;
      let j = i;
      while (j > 0 && out[j - 1].isSelf && !!out[j - 1].timestamp && out[j - 1].timestamp! > reply.timestamp) j--;
      if (j < i) {
        out.splice(i, 1);
        out.splice(j, 0, reply);
      }
    }
    for (let i = 0; i < out.length; i++) {
      const msg = out[i];
      if (!msg.isSelf || !msg.handledIn) continue;
      const a = out.findIndex(m => !m.isSelf && m.messageId === msg.handledIn);
      if (a >= 0 && a < i) {
        out.splice(i, 1);
        out.splice(a, 0, msg);
      }
    }
    return out;
  }
  private _channelStreamRaw = '';
  private pollInterval: any = null;
  private _thinkingTimeout: any = null;
  private _stallTimeout: any = null;
  // True once stream_start has been seen for the current turn (cleared on stream_end).
  // Guards the connecting-phase reaper from destroying an already-live stream when a
  // late 'thinking' event arrives mid-turn (which would otherwise re-arm the reaper).
  private _channelStreamStarted = false;
  private destroyed = false;
  private _wsKeepaliveTimer: any = null;
  private _channelWsKeepaliveTimer: any = null;
  private _channelStreamEndTime: number = 0;
  private _visibilityHandler: (() => void) | null = null;

  constructor(
    private http: HttpClient,
    private _wsTickets: WsTicketService,
    private agentService: MuxAgentService,
    private credService: MuxCredentialService,
    private poolService: ModelPoolService,
    private authWizard: ClaudeAuthWizardService,
    private sessionService: MuxSessionService,
    private _muxUsage: MuxUsageService,
    private _widget: WidgetService,
    private _settings: SettingsService,
    private _widgetContext: WidgetContextService,
    private _snackBar: MatSnackBar,
    private _dialog: MatDialog,
    private _dynamicWidgets: DynamicWidgetsService,
    private _ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private keycloak: KeycloakService,
  ) {
    super();
    (<any>window).heartbeat = this;

    const storedChFavs = localStorage.getItem(HeartbeatWidgetComponent.CH_FAVORITES_KEY);
    this.channelHeaderFavorites = new Set(
      storedChFavs ? JSON.parse(storedChFavs) : HeartbeatWidgetComponent.CH_DEFAULT_FAVORITES
    );
    const storedChOrder = localStorage.getItem(HeartbeatWidgetComponent.CH_ORDER_KEY);
    if (storedChOrder) {
      const known = HeartbeatWidgetComponent.CH_DEFAULT_ORDER;
      const parsed: string[] = (JSON.parse(storedChOrder) as string[]).filter(id => known.includes(id));
      const missing = HeartbeatWidgetComponent.CH_DEFAULT_ORDER.filter(id => !parsed.includes(id));
      this.channelHeaderOrder = [...parsed, ...missing];
    } else {
      this.channelHeaderOrder = [...HeartbeatWidgetComponent.CH_DEFAULT_ORDER];
    }
  }

  /** True when running as a gridster tile (not dockview panel) — show our own titlebar. */
  get isGridsterMode(): boolean {
    return this._settings.windowManagerType === 'gridster';
  }

  get isAmbientModeEnabled(): boolean {
    return this._settings.labsAmbientMode;
  }

  ngOnInit(): void {
    // An app asked this team's workspace to show a channel (a directory's "Chat").
    this._openChannelSub = this._widget.openChannel$.subscribe(req => {
      if (req.team !== (this.teamName || 'local')) return;
      this._widget.takePendingChannel(req.team);   // this workspace is here: the request is ours
      void this._openChannelById(req.channelId);
    });
    this.ambientModeSettingsSub = this._settings.labsAmbientMode$.subscribe(enabled => {
      if (!enabled) {
        this.agents.forEach(agent => {
          if (agent.ambientMode) this.stopAmbientListening(agent);
        });
      }
      this.cdr.markForCheck();
    });
    Promise.all([this.loadAgents(), this.loadNavTree()]).then(() => this.restoreSelectedView());

    this._visibilityHandler = () => this._onVisibilityChange();
    document.addEventListener('visibilitychange', this._visibilityHandler);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['teamName'] && !changes['teamName'].firstChange) {
      this.disconnectChannelWs();
      this.selectedChannelId = null;
      this.channelMessages = [];
      // The model pool is team-scoped (policy-filtered per team) — a stale
      // pool from the previous team leaks its providers/credentials into the
      // channel model selector (2026-09-01).
      Promise.all([this.loadAgents(), this.loadNavTree(), this._loadModelPool()]).then(() => this.cdr.markForCheck());
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroyed = true;
    this._openChannelSub?.unsubscribe();
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this.disconnectWs();
    this.disconnectChannelWs();
    this._disconnectTelemetryWs();
    this.teardownGraphContentObserver();
    this.teardownSearchObserver();
    if (this.pollInterval) clearInterval(this.pollInterval);
    if (this._bgAgentTimer) clearTimeout(this._bgAgentTimer);
    this.ambientModeSettingsSub?.unsubscribe();
    if (this.recognition) {
      this.recognition.onend = null;
      this.recognition.stop();
      this.recognition = null;
    }
  }


  get selectedAgent(): AgentHeartbeatInfo | undefined {
    return this.agents.find(a => a.agent.id === this.selectedAgentId);
  }

  get isHeartbeatEnabled(): boolean {
    return this.selectedAgent?.agent?.heartbeat_enabled ?? false;
  }

  get currentBackendId(): string {
    // Channel view first: the channel's agent decides, looked up in THIS
    // widget's team-scoped list. The shared agentService cache holds whichever
    // team loaded last, so with two team widgets open the lookup by id missed
    // and defaulted to 'claude-cli' — no terminal button on an interactive
    // agent (Ben, everyone/games, 2026-09-11).
    if (this.activeView === 'channel') {
      const agentId = this.selectedChannelAgentId || this.primaryAgentId;
      if (agentId) {
        const local = this.agents.find(a => a.agent.id === agentId)?.agent;
        const agent = local ?? this.agentService.getAgents().find(a => a.id === agentId);
        return agent?.backend ?? 'claude-cli';
      }
    }
    if (this.selectedAgent) {
      return this.selectedAgent.agent.backend ?? 'claude-cli';
    }
    const agentId = this.selectedChannelAgentId || this.primaryAgentId;
    if (agentId) {
      const local = this.agents.find(a => a.agent.id === agentId)?.agent;
      const agent = local ?? this.agentService.getAgents().find(a => a.id === agentId);
      return agent?.backend ?? 'claude-cli';
    }
    return 'claude-cli';
  }

  get isInteractiveBackend(): boolean {
    const b = this.currentBackendId;
    return b.endsWith('-interactive');
  }

  /** "Restart Session" (2026-09-10): end this channel's live CLI session; it resumes on the next message.
   *  The server posts a system note and pushes message_persisted — nothing else to render here. */
  async restartChannelSession(): Promise<void> {
    if (!this.selectedChannelId) return;
    // No client-side "still streaming" guard: the server decides from the
    // bridge's real session state (409 if the CLI is busy). The client's view
    // can be wrong — a turn parked with nothing running left the bubble in
    // "waiting" for good, and the guard refused the one action that clears it
    // (lucid, app.jov.ai, 2026-09-16).
    // No confirmation: nothing is destroyed (resume is stashed) and the channel shows a system line.
    try {
      const resp = await this.sessionService.restartChannelSession(this.selectedChannelId, this.teamName);
      const msg = resp?.status === 'no_session'
        ? (resp.detail || 'No live session to restart')
        : 'Session restarted — it resumes on your next message';
      this._snackBar.open(msg, 'OK', { duration: 5000 });
    } catch (e: any) {
      const detail = e?.status === 409 && e?.error?.detail
        ? e.error.detail
        : (e?.error?.detail || 'Could not restart the session — check your connection and try again.');
      this._snackBar.open(detail, 'OK', { duration: 8000 });
    }
  }

  get shortChannelLabel(): string {
    if (this.channelPath) {
      const parts = this.channelPath.replace(/\/+$/, '').split('/');
      return parts[parts.length - 1] || '';
    }
    return '';
  }

  /** True when the agent is actively streaming a response (DM or channel). */
  get isStreaming(): boolean {
    if (this.activeView === 'channel') return this.channelStreamingIndex !== null;
    return this.streamingMessageIndex !== null;
  }

  get cancelState(): 'none' | 'interrupt' | 'cancel' {
    if (this._interruptPending) return 'cancel';
    if (this.isStreaming) return 'interrupt';
    return 'none';
  }

  /** Two-stage cancel: first click = interrupt (hand up), second click = hard cancel (kill process). */
  private _userCancelledStream = false;
  private _interruptPending = false;

  async toggleChannelTerminal(restoring = false): Promise<void> {
    if (this.channelTerminalMode) {
      this.channelTerminalMode = false;
      this.channelTmuxCommand = '';
      if (this.selectedChannelId) this._rememberChannelMode(this.selectedChannelId, false);
      // The chat-screen is *ngIf'd out in terminal mode, so switching back
      // re-creates it fresh at scrollTop=0. Drive it back to the bottom once
      // Angular has re-rendered it (mirrors the channel-load path).
      setTimeout(() => this.chatScreen?.forceScrollToBottom(), 50);
      this.cdr.markForCheck();
      return;
    }

    const username = JSON.parse(localStorage.getItem('me') || '{}')?.username || 'ben';
    const u = username.replace(/[^a-zA-Z0-9_-]/g, '-');

    // Ask the backend for the AUTHORITATIVE attach key — it reads the actual
    // running bridge session name. We must not reconstruct it here: the agent
    // segment is server-resolved routing we can't reliably reproduce, and the
    // old `|| 'claude-interactive'` fallback silently produced a key that never
    // matched a real session, so attach.py exited 3 and the terminal rendered
    // as "disconnected".
    let resolved: { available: boolean; session_key: string | null; reason: string | null; attach_socket?: string | null; host?: string | null; attach_command?: string | null } | null = null;
    try {
      const params = this.selectedChannelId
        ? `?channel_id=${encodeURIComponent(this.selectedChannelId)}`
        : '';
      resolved = await lastValueFrom(
        this.http.get<{ available: boolean; session_key: string | null; reason: string | null; attach_socket?: string | null; host?: string | null; attach_command?: string | null }>(
          `${ConfigService.muxUrl}/channels/interactive-terminal${params}`,
        ),
      );
    } catch (err) {
      if (!restoring) this._snackBar.open('Could not reach the terminal service. Try again.', 'OK', { duration: 5000 });
      return;
    }

    if (resolved?.available && resolved.session_key) {
      // Attach straight to the real session key. No fabricated-key fallback.
      // The socket file name comes from the server when it can say (it carries
      // the LIT_INSTANCE tag on a multi-instance host); older servers omit it.
      const rsSock = resolved.attach_socket || `lit-bridge-rs-${u}.sock.attach`;
      // Session on an affinity host (laptop behind a tunnel): the server hands us
      // the ssh hop + attach line to run there; otherwise attach on this host.
      this.channelTmuxCommand = resolved.attach_command ||
        `ATT="$XDG_RUNTIME_DIR/${rsSock}"; [ -S "$ATT" ] || ATT="/tmp/${rsSock}"; ` +
        `python3 /opt/lit-platform/lit-bridge-rs/scripts/attach.py "$ATT" '${resolved.session_key}'\r`;
      this.channelTerminalMode = true;
      if (this.selectedChannelId) this._rememberChannelMode(this.selectedChannelId, true);
      this.cdr.markForCheck();
      return;
    }

    // Restoring a remembered terminal mode on channel entry: no live session
    // means chat view, quietly — the user did not just ask for a terminal.
    if (restoring) {
      if (this.selectedChannelId) this._rememberChannelMode(this.selectedChannelId, false);
      return;
    }

    // No live native-bridge session — and NO tmux fallback. The platform runs the
    // rust multiplexer, not tmux, so a `tmux attach` here could never connect: it only
    // ever rendered a broken command and made a plain "no session yet" state look like
    // a tmux failure (it did exactly that, and cost a debugging session). The session
    // is spawned on the next message, so the honest, actionable answer is to send one.
    this._snackBar.open(
      resolved?.reason ||
        'No live terminal session for this channel yet — send a message in the channel first, then reopen the terminal.',
      'OK',
      { duration: 6000 },
    );
    this.cdr.markForCheck();
  }

  onChannelTerminalDisconnect(): void {
    this.channelTerminalMode = false;
    this.channelTmuxCommand = '';
    if (this.selectedChannelId) this._rememberChannelMode(this.selectedChannelId, false);
    setTimeout(() => this.chatScreen?.forceScrollToBottom(), 50);
    this.cdr.markForCheck();
  }

  cancelActiveStream(): void {
    const agent = this.activeAgentInfo;
    if (!agent) return;
    const agentId = agent.agent.id;

    if (this._interruptPending) {
      // Second+ click → hard cancel
      this._interruptPending = false;
      this._userCancelledStream = true;
      this.isInterrupted = false;
      this.http.post(`${ConfigService.muxUrl}/agents/${agentId}/cancel`, {}).subscribe({
        error: (err: any) => console.error('Error cancelling agent generation:', err),
      });
      this.http.delete(`${ConfigService.muxUrl}/agents/${agentId}/heartbeat/interrupt`).subscribe();
      this.cdr.markForCheck();
    } else {
      // First click → interrupt (raise hand)
      this._interruptPending = true;
      this.isInterrupted = true;
      this.cdr.markForCheck();
      this.http.post(`${ConfigService.muxUrl}/agents/${agentId}/heartbeat/interrupt`, {
        reason: 'User requested interrupt',
      }).subscribe({
        error: (err: any) => console.error('Error setting interrupt:', err),
      });
    }
  }

  // --- File click handlers (delegate to dockview grid) ---

  /** The computer this channel's files live on: its first affinity host ('' = the home).
   *  A path the agent printed while running there is there; the commander falls back
   *  to the home when it is not. */
  channelHost(): string {
    return this.channelAffinity[0] || '';
  }

  openFileInEditor(path: string): void {
    this._widget.grid?.$editFile({ parentId: null, path, host: this.channelHost() });
  }

  openMarkdownViewer(path: string, title: string): void {
    this._widget.grid?.$viewMarkdown({ parentId: null, path, title: title || path.split('/').pop() || 'markdown', host: this.channelHost() });
  }

  openCsvViewer(path: string, title: string): void {
    this._widget.grid?.$viewCsv({ parentId: null, path, title: title || path.split('/').pop() || 'CSV', host: this.channelHost() });
  }

  openPreviewFile(path: string, previewType: 'image' | 'pdf'): void {
    this._widget.grid?.$previewFile({ parentId: null, path, previewType, host: this.channelHost() });
  }

  onDmMessageDeleted(msg: any): void {
    this.messages = this.messages.filter(m => m.messageId !== msg.messageId);
    this.cdr.markForCheck();
  }

  onChannelMessageDeleted(msg: any): void {
    this.channelMessages = this.channelMessages.filter(m => m.messageId !== msg.messageId);
    this.cdr.markForCheck();
  }

  async onExpandTruncatedMessage(msg: ChatService.ChatMessage): Promise<void> {
    if (!this.selectedChannelId || !msg.messageId) return;
    const full = await this.sessionService.getChannelMessage(this.selectedChannelId, msg.messageId, this.teamName);
    if (!full) return;
    const idx = this.channelMessages.findIndex(m => m.messageId === msg.messageId);
    if (idx < 0) return;
    let rawContent = full.direction === 'in' ? this._stripContextEnvelope(full.content) : full.content;
    // System notices preserved — LINKS regex handles trailing notices.
    const { cleanContent: content, actions } = full.direction === 'out'
      ? this.extractChannelActions(rawContent)
      : { cleanContent: rawContent, actions: [] };
    const { content: finalContent, links } = ChatScreenComponent.extractLinks(content);
    this.channelMessages[idx] = {
      ...this.channelMessages[idx],
      content: finalContent,
      contentTruncated: false,
      contentLength: undefined,
      ...(links.length ? { links } : {}),
      ...(actions.length ? { channelActions: actions } : {}),
    };
    this.channelMessages = [...this.channelMessages];
    this.cdr.markForCheck();
  }

  // --- Data loading ---

  async loadAgents(): Promise<void> {
    if (parsePlaceAddress(this.teamName)) {
      // Remote place: the device's agents, fetched over the pipe. Tabs give
      // deliberate targeting (tab switch assigns the desktop channel's
      // agent); heartbeat/throttle/usage machinery is local-only and skipped.
      const remoteAgents = await this.agentService.loadAgents(this.teamName);
      this.agents = remoteAgents.map(a => ({
        agent: a,
        status: 'awake' as const,
        presence: undefined,
        throttle: 'enabled' as AgentThrottle,
      }));
      this.cdr.markForCheck();
      return;
    }
    this.primaryAgentId = await this.agentService.getPrimaryAgent();
    const agents = await this.agentService.loadAgents(this.teamName);
    this.agents = agents.map(a => ({
      agent: a,
      status: a.heartbeat_enabled ? 'awake' : 'off',
      presence: a.heartbeat_enabled ? undefined : 'offline' as Presence,
      throttle: (a.heartbeat_enabled ? 'enabled' : 'disabled') as AgentThrottle,
    }));

    this._loadAgentVisibility();

    // Fetch live heartbeat status and throttle state for all agents
    for (const info of this.agents) {
      if (info.agent.heartbeat_enabled) {
        this.fetchHeartbeatStatus(info);
      }
      this.fetchAgentThrottle(info);
    }

    // Pre-fetch usage for all unique agents, staggered to avoid burst
    let delay = 0;
    for (const info of this.agents) {
      const backend = info.agent.backend ?? 'claude-cli';
      const agentId = info.agent.id;
      setTimeout(() => {
        this._muxUsage.fetchForCache(backend, agentId).then(report => {
          if (report) {
            this.usageCache.set(agentId, report);
            this.cdr.markForCheck();
          }
        });
      }, delay);
      delay += 2000;
    }

    // Load available models per backend for model selector
    this.agentService.getModels().then(resp => {
      if (resp?.models) {
        for (const [backend, models] of Object.entries(resp.models)) {
          this.backendModels.set(backend, models.map((m: any) => ({
            name: m.name || m,
            display_name: m.display_name || m.name || m,
          })));
        }
        this.modelConstraints = resp.constraints || {};
        this._providerGroupsCache.clear();
        this._ownModelsCache.clear();
        this.cdr.markForCheck();
      }
    });

    // Credentials — needed to map an agent's credential to (vendor, mode) so the model
    // dropdown only offers models that credential can run (parity with the Agents lens).
    this.credService.list().then(creds => {
      this.credentials = creds;
      this._providerGroupsCache.clear();
        this._ownModelsCache.clear(); // groups derive from credentials
      this.cdr.markForCheck();
    }).catch(() => {});
    this._loadModelPool();

    // Restore previously selected agent (only if not restoring a channel view).
    // Skip for first-time users (no savedView) — restoreSelectedView() handles
    // auto-selecting the default channel. Calling selectAgent here would race
    // with selectChannel since selectAgent is fire-and-forget (not awaited).
    const savedView = localStorage.getItem('lit-heartbeat-active-view');
    if (savedView && savedView !== 'channel') {
      const saved = localStorage.getItem('lit-heartbeat-selected-agent');
      if (saved && this.visibleAgents.some(a => a.agent.id === saved)) {
        this.selectAgent(saved);
      } else if (this.primaryAgentId && this.visibleAgents.some(a => a.agent.id === this.primaryAgentId)) {
        this.selectAgent(this.primaryAgentId);
      } else if (this.visibleAgents.length > 0) {
        this.selectAgent(this.visibleAgents[0].agent.id);
      }
    }

    this.cdr.markForCheck();
  }

  async fetchHeartbeatStatus(info: AgentHeartbeatInfo): Promise<void> {
    try {
      const muxUrl = ConfigService.muxUrl;
      const status = await lastValueFrom(
        this.http.get<any>(`${muxUrl}/agents/${info.agent.id}/heartbeat/status`)
      );
      info.status = status.is_sleeping ? 'sleeping' : 'awake';
      info.heartbeatCount = status.heartbeat_count;
      info.presence = status.is_sleeping ? 'idle' : 'online';
      this.cdr.markForCheck();
    } catch {
      // Heartbeat not running — mark as offline
      info.status = 'off';
      info.presence = 'offline';
      this.cdr.markForCheck();
    }
  }

  async selectAgent(agentId: string): Promise<void> {
    this.activeView = 'dm';
    this.selectedAgentId = agentId;
    const viewKey = this.teamName ? `lit-heartbeat-active-view-${this.teamName}` : 'lit-heartbeat-active-view';
    const channelKey = this.teamName ? `lit-heartbeat-selected-channel-${this.teamName}` : 'lit-heartbeat-selected-channel';
    localStorage.setItem('lit-heartbeat-selected-agent', agentId);
    localStorage.setItem(viewKey, 'dm');
    localStorage.removeItem(channelKey);
    this.selectedChannelId = null;
    this.selectedChannelName = null;
    this.selectedChannelAgentId = null;
    this.selectedChannelAgentName = null;
    this.messages = [];
    this.statusText = null;
    this.channelPath = null;
    this.pinnedMessages = [];
    this.pinsPaneOpen = false;
    this.disconnectWs();
    this.disconnectChannelWs();
    this.cdr.markForCheck();

    const info = this.selectedAgent;
    if (!info) return;

    // Start usage polling for this agent's backend
    this._muxUsage.startPolling(this.currentBackendId, undefined, this.selectedChannelAgentId || this.primaryAgentId || undefined);

    // Only show status and connect WS for enabled agents
    if (info.agent.heartbeat_enabled) {
      if (info.presence === 'idle') {
        this.statusText = `Sleeping — ${info.sleepReason || 'idle'}`;
      } else if (info.presence === 'online' || info.status === 'awake') {
        this.statusText = `Awake — heartbeat #${info.heartbeatCount || 0}`;
      }
      this.connectWs(agentId);
      const selected = this.agents.find(a => a.agent.id === agentId);
      if (selected) this.fetchAgentThrottle(selected);
    }

    if (this._settings.labsReactions) {
      this._connectTelemetryWsForDm(agentId);
    }

    await this.loadMessages();
  }

  async loadMessages(): Promise<void> {
    if (!this.selectedAgentId) return;
    this.loading = true;
    this.readMessageIds.clear();
    this.cdr.markForCheck();

    try {
      const muxUrl = ConfigService.muxUrl;
      const resp = await lastValueFrom(
        this.http.get<{ messages: HeartbeatMessage[], channel_dir?: string }>(
          `${muxUrl}/agents/${this.selectedAgentId}/heartbeat/messages`
        )
      );

      const raw = (resp.messages || []).filter(m => m.metadata?.source !== 'reaction');
      this.messages = raw.map(m => this.toDisplayMessage(m));

      // Store channel dir for folder chip
      if (resp.channel_dir) {
        this.channelPath = resp.channel_dir;
      }

      // Populate read receipt state from inbox messages
      for (const m of raw) {
        if (m.direction === 'in' && m.read) {
          this.readMessageIds.add(m.id);
        }
      }

      // Load pinned messages count (non-blocking)
      this.loadPinnedMessages();
    } catch (err) {
      console.error('Failed to load heartbeat messages:', err);
      this.messages = [];
    }

    this.loading = false;
    this.cdr.markForCheck();
    setTimeout(() => this.chatScreen?.forceScrollToBottom(), 50);
  }

  // --- Sending messages ---

  async onSubmit(event: ChatInputSubmitEvent): Promise<void> {
    if (!this.selectedAgentId || !event.content.trim()) return;

    const content = event.content.trim();

    // Optimistically add to UI
    this.messages = [
      ...this.messages,
      {
        isSelf: true,
        content,
        timestamp: new Date().toISOString(),
        isFinal: true,
      },
    ];
    this.sending = true;
    this.cdr.markForCheck();
    this.chatScreen?.forceScrollToBottom();

    try {
      const muxUrl = ConfigService.muxUrl;
      const dmChannelId = `dm-${this.selectedAgentId}`;
      const contentWithContext = this._buildDmContextEnvelope(content, dmChannelId, event.inputModality);
      const resp: any = await lastValueFrom(
        this.http.post(`${muxUrl}/agents/${this.selectedAgentId}/heartbeat/inbox`, { content: contentWithContext })
      );
      // Update the optimistic message with the real ID so read receipts can match
      if (resp?.id) {
        const last = this.messages[this.messages.length - 1];
        if (last?.isSelf) {
          last.messageId = resp.id;
        }
      }
    } catch (err) {
      console.error('Failed to send heartbeat message:', err);
      // Add error indicator
      this.messages = [
        ...this.messages,
        {
          isSelf: false,
          content: 'Failed to send message. Is the API running?',
          isError: true,
          isFinal: true,
        },
      ];
    }

    // If agent is actively streaming, inject a follow-up signal so it can acknowledge the new message
    if (this.streamingMessageIndex !== null && this.selectedAgentId) {
      lastValueFrom(
        this.http.post(`${ConfigService.muxUrl}/agents/${this.selectedAgentId}/signals`, {
          type: 'followup',
          content,
        })
      ).catch(() => {}); // Non-critical — fire and forget
    }

    this.sending = false;
    this.cdr.markForCheck();

    // Refresh usage after send — delay to let backend process
    setTimeout(() => this._muxUsage.refreshAfterSend(this.currentBackendId, this.selectedChannelAgentId || this.primaryAgentId || undefined), 5000);
  }

  // --- Toggle heartbeat ---

  async toggleHeartbeat(): Promise<void> {
    if (!this.selectedAgentId) return;

    const info = this.selectedAgent;
    if (!info) return;

    const newState = !info.agent.heartbeat_enabled;

    try {
      const muxUrl = ConfigService.muxUrl;
      const resp = await lastValueFrom(
        this.http.put<any>(`${muxUrl}/agents/${this.selectedAgentId}/heartbeat/enabled`, {
          enabled: newState,
        })
      );

      // Update local state
      info.agent.heartbeat_enabled = resp.heartbeat_enabled;
      info.status = resp.heartbeat_enabled ? 'awake' : 'off';
      info.presence = resp.heartbeat_enabled ? 'online' : 'offline';
      this.statusText = resp.heartbeat_enabled ? 'Starting...' : null;

      // Reconnect WebSocket if enabling
      if (resp.heartbeat_enabled) {
        this.connectWs(this.selectedAgentId);
      }
    } catch (err) {
      console.error('Failed to toggle heartbeat:', err);
    }

    this.cdr.markForCheck();
  }

  // --- Ambient listening ---

  exitHandsFreeMode(): void {
    this.agents.forEach(a => {
      if (a.ambientMode) this.stopAmbientListening(a);
    });
    this.cdr.markForCheck();
  }

  // --- Hands-free gesture controls ---

  /** Is the agent's voice going right now (live reply or replay)? */
  get isTalking(): boolean {
    return this.handsFreeActive && (this.handsFreeState === 'speaking' || this.handsFreeState === 'replaying');
  }

  /**
   * Stop talking — the one-action way out of a long reply (Ben, 2026-09-24).
   * Drops what is queued AND the rest of the current turn (a cancel() alone
   * was undone by the next stream chunk re-queuing), then goes straight back
   * to listening. Reachable by: the big Stop button, tapping the orb, or
   * Escape on a keyboard.
   */
  stopTalking(): void {
    if (!this.isTalking && !(this.handsFreeState === 'paused' && this._ttsPausedFrom)) return;
    this._ttsMuted = true;
    this._ttsFilter.reset();
    this._ttsSentenceBuffer = '';
    this._ttsPausedFrom = null;
    this._ttsSynth?.cancel();
    if (navigator.vibrate) navigator.vibrate(30);
    this.ttsResumeListening();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isTalking) this.stopTalking();
  }

  onOrbTap(): void { this.onPrimaryAction(); }

  /** The big left button: whatever "the obvious thing" is in this state. */
  get primaryAction(): { label: string; icon: string } | null {
    switch (this.handsFreeState) {
      case 'speaking':
      case 'replaying': return { label: 'Stop', icon: 'stop' };
      case 'listening': return { label: 'Pause', icon: 'pause' };
      case 'paused': return { label: 'Resume', icon: 'mic' };
      default: return null; // thinking: nothing to press
    }
  }

  onPrimaryAction(): void {
    if (!this.handsFreeActive) return;
    switch (this.handsFreeState) {
      case 'speaking':
      case 'replaying': this.stopTalking(); break;
      case 'listening':
        this.handsFreeState = 'paused';
        try { this.recognition?.stop(); } catch (_) {}
        this.cdr.markForCheck();
        break;
      case 'paused':
        this._ttsPausedFrom = null;
        this.ttsResumeListening();
        break;
    }
  }

  /** The big right button: say the last reply again (from the start). */
  get canReplay(): boolean {
    return this.handsFreeActive && !!this._lastAssistantMessage
      && (this.handsFreeState === 'listening' || this.handsFreeState === 'paused');
  }

  replayLast(): void {
    if (!this.canReplay || !this._ttsSynth) return;
    try { this.recognition?.stop(); } catch (_) {}
    this.handsFreeState = 'replaying';
    this.handsFreeTranscript = '';
    this._ttsPausedFrom = null;
    this.cdr.markForCheck();
    // speak() must run in the click's own call stack (Chrome mobile)
    const utterance = new SpeechSynthesisUtterance(this._lastAssistantMessage);
    utterance.rate = this._ttsRate;
    utterance.onend = () => {
      if (this.handsFreeState === 'replaying') this.ttsResumeListening();
    };
    this._ttsSynth.speak(utterance);
  }

  /** Speed chip: cycles 0.8 → 1.0 → 1.2 → 1.5 → 2.0 → 0.8. */
  cycleTtsSpeed(): void {
    const next = (this._ttsSpeedIndex + 1) % this._ttsSpeedSteps.length;
    this.adjustTtsSpeed((next - this._ttsSpeedIndex) as any);
  }

  private adjustTtsSpeed(direction: number): void {
    const newIndex = Math.max(0, Math.min(this._ttsSpeedSteps.length - 1, this._ttsSpeedIndex + direction));
    if (newIndex === this._ttsSpeedIndex) return;
    this._ttsSpeedIndex = newIndex;
    this._ttsRate = this._ttsSpeedSteps[newIndex];
    this.handsFreeSpeedToast = this._ttsRate + 'x';
    if (navigator.vibrate) navigator.vibrate(50);
    // Flush queued utterances so new ones use the updated rate immediately
    if (this._ttsSynth?.speaking || this._ttsSynth?.pending) {
      const wasReplaying = this.handsFreeState === 'replaying';
      this._ttsSynth.cancel();
      // During replay, restart the message at the new speed (delay after cancel for Chrome)
      if (wasReplaying && this._lastAssistantMessage) {
        setTimeout(() => {
          const utterance = new SpeechSynthesisUtterance(this._lastAssistantMessage);
          utterance.rate = this._ttsRate;
          utterance.onend = () => {
            if (this.handsFreeState === 'replaying') this.ttsResumeListening();
          };
          this._ttsSynth!.speak(utterance);
        }, 100);
      }
    }
    this.cdr.markForCheck();
    clearTimeout(this._speedToastTimeout);
    this._speedToastTimeout = setTimeout(() => {
      this.handsFreeSpeedToast = '';
      this.cdr.markForCheck();
    }, 1200);
  }

  toggleAmbientMode(info: AgentHeartbeatInfo): void {
    if (!this.isAmbientModeEnabled) return;
    if (info.ambientMode) {
      this.stopAmbientListening(info);
    } else {
      // Stop any other agent currently in ambient mode
      this.agents.forEach(a => { if (a.ambientMode) this.stopAmbientListening(a); });
      this.startAmbientListening(info);
    }
    this.cdr.markForCheck();
  }

  private startAmbientListening(info: AgentHeartbeatInfo): void {
    // Request wake lock to keep screen on during hands-free mode
    if ('wakeLock' in navigator) {
      (navigator as any).wakeLock.request('screen').then((lock: any) => {
        this._wakeLock = lock;
        console.log('[Ambient] Wake lock acquired');
      }).catch((e: any) => console.warn('[Ambient] Wake lock failed:', e.message));
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn('[Ambient] Web Speech API not supported in this browser');
      return;
    }

    info.ambientMode = true;
    this.handsFreeActive = true;
    this.handsFreeTranscript = '';
    this.handsFreeAgentName = info.agent.name;

    // If a stream is already in progress, start TTS and read what's accumulated so far
    if (this.streamingMessageIndex !== null && this.messages[this.streamingMessageIndex]?.isStreaming) {
      this.handsFreeState = 'speaking';
      this.ttsStartStreaming();
      const accumulated = String(this.messages[this.streamingMessageIndex].content || '');
      if (accumulated) {
        const plain = this.stripMarkdown(accumulated);
        if (plain) {
          const utterance = new SpeechSynthesisUtterance(plain);
          utterance.rate = this._ttsRate;
          this._ttsSynth?.speak(utterance);
        }
      }
    } else {
      this.handsFreeState = 'listening';
    }

    // Seed replay buffer from loaded history if empty
    if (!this._lastAssistantMessage) {
      const inCh = this.activeView === 'channel';
      const msgs = inCh ? this.channelMessages : this.messages;
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (!msgs[i].isSelf && msgs[i].content) {
          const text = this.stripMarkdown(String(msgs[i].content));
          if (text) { this._lastAssistantMessage = text; break; }
        }
      }
    }

    const inChannel = this.activeView === 'channel';

    // Ensure the correct agent is active for whichever view we're in
    if (inChannel) {
      this.$changeChannelAgent(info.agent.id);
    } else {
      this.selectAgent(info.agent.id);
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = false;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';

    let pendingTranscript = '';
    let ambientDebounce: any = null;

    this.recognition.onresult = (event: any) => {
      // continuous=false: only one result entry, updated with interim text
      const result = event.results[0];
      const text = result[0].transcript;

      // Show accumulated + current utterance as live preview
      this.handsFreeTranscript = (pendingTranscript + text).trim();
      this.cdr.markForCheck();

      if (result.isFinal) {
        pendingTranscript += text + ' ';
        // Debounce: accumulate utterances, submit after 1.8s of silence
        clearTimeout(ambientDebounce);
        ambientDebounce = setTimeout(() => {
          const toSend = pendingTranscript.trim();
          pendingTranscript = '';
          if (toSend) {
            console.log(`[Ambient] sending: "${toSend}"`);
            this.handsFreeState = 'thinking';
            this.cdr.markForCheck();
            if (inChannel) {
              this.onChannelSubmit({ content: toSend, inputModality: 'voice' });
            } else {
              this.onSubmit({ content: toSend, inputModality: 'voice' });
            }
          }
        }, 1800);
      }
    };

    this.recognition.onerror = (event: any) => {
      if (event.error !== 'no-speech') {
        console.error('[Ambient] STT error:', event.error);
      }
      if (event.error === 'aborted' || event.error === 'network') {
        // Browser killed recognition — full restart after a brief delay
        if (info.ambientMode) {
          setTimeout(() => {
            if (info.ambientMode) this.restartAmbientRecognition(info);
          }, 500);
        }
      }
    };

    this.recognition.onend = () => {
      if (info.ambientMode && this.handsFreeState !== 'paused') {
        try {
          this.recognition?.start();
        } catch (e) {
          console.warn('[Ambient] restart failed, recreating recognition');
          setTimeout(() => {
            if (info.ambientMode) this.restartAmbientRecognition(info);
          }, 300);
        }
      }
    };

    this.recognition.start();
    console.log(`[Ambient] started for agent ${info.agent.name}`);
  }

  private restartAmbientRecognition(info: AgentHeartbeatInfo): void {
    if (this.recognition) {
      this.recognition.onend = null;
      try { this.recognition.stop(); } catch (_) {}
      this.recognition = null;
    }
    this.startAmbientListening(info);
  }

  private stopAmbientListening(info: AgentHeartbeatInfo): void {
    info.ambientMode = false;
    this.handsFreeActive = false;
    this.ttsHardStop();
    if (this.recognition) {
      this.recognition.onend = null;
      this.recognition.stop();
      this.recognition = null;
    }
    // Release wake lock
    if (this._wakeLock) {
      this._wakeLock.release().catch(() => {});
      this._wakeLock = null;
    }
    console.log(`[Ambient] stopped for agent ${info.agent.name}`);
  }

  private isAnyAgentAmbient(): boolean {
    return this.agents.some(a => a.ambientMode);
  }

  private stripMarkdown(text: string): string {
    return stripToolMarkup(text)
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`[^`]+`/g, match => match.slice(1, -1))
      .replace(/!\[.*?\]\(.*?\)/g, '')
      .replace(/\[([^\]]+)\]\(.*?\)/g, '$1')
      .replace(/(?<=^|\x03)REACT:.*$/gm, '')
      .replace(/^ACK:.*$/gm, '')
      .replace(/^SLEEP_MODE.*$/gm, '')
      .replace(/^LINKS:.*$/gm, '')
      .replace(/^\|.*\|$/gm, '') // markdown tables
      .replace(/^[-=]{3,}$/gm, '') // horizontal rules
      .replace(/^>\s+/gm, '') // blockquotes
      .replace(/[*_~#]+/g, '')
      .replace(/\n{2,}/g, '. ')
      .replace(/\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private ttsStartStreaming(): void {
    if (!this._ttsSynth || !this.isAnyAgentAmbient()) return;
    this._ttsSynth.cancel();
    this._ttsSentenceBuffer = '';
    this._ttsFilter.reset();
    this._ttsFedSpeakable = '';
    this._ttsActive = true;
    this._ttsMuted = false;
    this._ttsFirstUtterance = true;
    // Pause mic while TTS is active
    if (this.recognition) {
      try { this.recognition.onend = null; this.recognition.stop(); } catch (_) {}
    }
  }

  /** Raw-delta feed — superseded by ttsFeedDisplay (kept for the hands-free entry path). */
  private ttsFeedChunk(chunk: string): void {
    // handsFreeActive guard: after Exit, the stream keeps arriving — without
    // this every later chunk re-queued utterances behind the cancel() and the
    // voice carried on until the reply ended (Ben, 2026-09-24, bug 2).
    if (!this._ttsActive || !this._ttsSynth || !this.handsFreeActive || this._ttsMuted) return;
    this.ttsSpeak(this._ttsFilter.feed(chunk), false);
  }

  /**
   * Feed the speech path from the DISPLAY text (chrome-stripped, full content)
   * rather than raw stream deltas: raw frames carry transient terminal chrome
   * that the display strips and later frames replace — feeding deltas spoke
   * it before it vanished. Speaks only the part not fed yet.
   */
  private _ttsFedSpeakable = '';
  private ttsFeedDisplay(display: string): void {
    if (!this._ttsActive || !this._ttsSynth || !this.handsFreeActive || this._ttsMuted) return;
    const speakable = speakableSoFar(display);
    let i = 0;
    const prev = this._ttsFedSpeakable;
    while (i < speakable.length && i < prev.length && speakable[i] === prev[i]) i++;
    // Normal frame: the new text extends what we spoke. Rewrite frame: speak
    // from the first differing character (a few repeated words at worst).
    const delta = speakable.slice(i);
    this._ttsFedSpeakable = speakable;
    if (delta) this.ttsSpeak(delta, false);
  }

  /** Queue complete sentences from newly speakable text; hold the tail. */
  private ttsSpeak(text: string, final: boolean): void {
    if (!this._ttsSynth) return;
    this._ttsSentenceBuffer += text;
    const sentenceBreak = /([.!?]\s+|:\n|\n\n)/;
    let match: RegExpExecArray | null;
    while ((match = sentenceBreak.exec(this._ttsSentenceBuffer)) !== null) {
      const sentence = this._ttsSentenceBuffer.slice(0, match.index + match[0].length);
      this._ttsSentenceBuffer = this._ttsSentenceBuffer.slice(match.index + match[0].length);
      this.ttsQueue(sentence);
    }
    if (final && this._ttsSentenceBuffer.trim()) {
      this.ttsQueue(this._ttsSentenceBuffer);
      this._ttsSentenceBuffer = '';
    }
  }

  private ttsQueue(sentence: string): void {
    // Blank runs left where a tool block was removed become ". " in
    // stripMarkdown and were queued as a one-period "sentence" — spoken as
    // "dot" (Ben, 2026-09-24). Nothing without a letter or digit is worth saying.
    const plain = this.stripMarkdown(sentence).replace(/^[\s.,;:!?-]+/, '');
    if (!plain || plain === 'code block' || !this._ttsSynth) return;
    if (!/[\p{L}\p{N}]/u.test(plain)) return;
    if (isChromeSentence(plain)) return; // a spinner/tip fragment that survived line stripping
    const utterance = new SpeechSynthesisUtterance(plain);
    utterance.rate = this._ttsRate;
    if (this._ttsFirstUtterance) {
      this._ttsFirstUtterance = false;
      utterance.onstart = () => {
        this.handsFreeState = 'speaking';
        this.cdr.markForCheck();
      };
    }
    this._ttsSynth.speak(utterance);
  }

  /** Hard stop: nothing queued, nothing held, nothing more accepted. */
  private ttsHardStop(): void {
    this._ttsActive = false;
    this._ttsFilter.reset();
    this._ttsSentenceBuffer = '';
    this._ttsSynth?.cancel();
  }

  private ttsFlushAndStop(): void {
    if (!this._ttsActive || !this._ttsSynth) return;
    if (!this.handsFreeActive) { this.ttsHardStop(); return; }
    if (this._ttsMuted) {
      // Stopped mid-reply: nothing left to say; already listening.
      this._ttsActive = false;
      this._ttsMuted = false;
      this._ttsSentenceBuffer = '';
      return;
    }
    // Speak any remaining buffered text (the filter releases its held tail)
    this._ttsSentenceBuffer += this._ttsFilter.finish();
    const remaining = this.stripMarkdown(this._ttsSentenceBuffer);
    if (remaining) {
      const utterance = new SpeechSynthesisUtterance(remaining);
      utterance.rate = this._ttsRate;
      utterance.onend = () => this.ttsResumeListening();
      this._ttsSynth.speak(utterance);
    } else if (this._ttsSynth.speaking || this._ttsSynth.pending) {
      // Sentences were already queued mid-stream — wait for them to finish
      const waitForDone = () => {
        if (!this._ttsSynth?.speaking && !this._ttsSynth?.pending) {
          this.ttsResumeListening();
        } else {
          setTimeout(waitForDone, 200);
        }
      };
      setTimeout(waitForDone, 200);
    } else {
      this.ttsResumeListening();
    }
    this._ttsSentenceBuffer = '';
    this._ttsActive = false;
  }

  private ttsResumeListening(): void {
    this.handsFreeState = 'listening';
    this.cdr.markForCheck();
    const activeAgent = this.agents.find(a => a.ambientMode);
    if (activeAgent) this.restartAmbientRecognition(activeAgent);
  }

  private _clearInterruptState(): void {
    if (!this.isInterrupted && !this._interruptPending) return;
    this._interruptPending = false;
    this.isInterrupted = false;
    const agent = this.activeAgentInfo;
    if (agent) {
      this.http.delete(`${ConfigService.muxUrl}/agents/${agent.agent.id}/heartbeat/interrupt`).subscribe();
    }
  }

  // --- Agent throttle (disabled / enabled / safe) ---
  // The composer heart button is retired; the runtime menu opens from a
  // right-click on the agent tab via this hidden cursor-positioned trigger.

  @ViewChild('throttleCtxTrigger') throttleCtxTrigger: MatMenuTrigger | undefined;
  throttleMenuInfo: AgentHeartbeatInfo | null = null;
  throttleMenuX = 0;
  throttleMenuY = 0;

  openThrottleMenu(event: MouseEvent, info: AgentHeartbeatInfo): void {
    event.preventDefault();
    event.stopPropagation();
    this.throttleMenuInfo = info;
    this.throttleMenuX = event.clientX;
    this.throttleMenuY = event.clientY;
    setTimeout(() => this.throttleCtxTrigger?.openMenu());
  }

  async setAgentThrottle(info: AgentHeartbeatInfo, state: AgentThrottle, event?: MouseEvent): Promise<void> {
    if (event) event.stopPropagation();
    if (info.throttle === state) return;

    const muxUrl = ConfigService.muxUrl;
    const agentId = info.agent.id;
    const prev = info.throttle;

    try {
      // --- Tear down previous state ---
      if (prev === 'safe') {
        await lastValueFrom(this.http.post<any>(`${muxUrl}/agents/${agentId}/safe_mode`, { enabled: false }));
      }

      // --- Handle enable/disable transitions ---
      const wasEnabled = prev !== 'disabled';
      const willEnable = state !== 'disabled';

      if (!wasEnabled && willEnable) {
        const resp = await lastValueFrom(
          this.http.put<any>(`${muxUrl}/agents/${agentId}/heartbeat/enabled`, { enabled: true })
        );
        info.agent.heartbeat_enabled = resp.heartbeat_enabled;
        info.status = 'awake';
        info.presence = 'online';
        if (agentId === this.selectedAgentId) {
          this.statusText = 'Starting...';
          this.connectWs(agentId);
        }
      } else if (wasEnabled && !willEnable) {
        const resp = await lastValueFrom(
          this.http.put<any>(`${muxUrl}/agents/${agentId}/heartbeat/enabled`, { enabled: false })
        );
        info.agent.heartbeat_enabled = resp.heartbeat_enabled;
        info.status = 'off';
        info.presence = 'offline';
        if (agentId === this.selectedAgentId) {
          this.statusText = null;
        }
      }

      // --- Set up new state ---
      if (state === 'safe') {
        await lastValueFrom(this.http.post<any>(`${muxUrl}/agents/${agentId}/safe_mode`, { enabled: true }));
      }

      info.throttle = state;
      if (agentId === this.selectedAgentId) {
        this.isSafeModeEnabled = state === 'safe';
      }
    } catch (err) {
      console.error('Failed to set agent throttle:', err);
    }
    this.cdr.markForCheck();
  }

  private async fetchAgentThrottle(info: AgentHeartbeatInfo): Promise<void> {
    if (!info.agent.heartbeat_enabled) {
      info.throttle = 'disabled';
      return;
    }
    try {
      const muxUrl = ConfigService.muxUrl;
      const [safeResp, intResp] = await Promise.all([
        lastValueFrom(this.http.get<any>(`${muxUrl}/agents/${info.agent.id}/safe_mode`)),
        lastValueFrom(this.http.get<any>(`${muxUrl}/agents/${info.agent.id}/heartbeat/interrupt`)),
      ]);
      const safe = safeResp?.safe_mode ?? false;
      const interrupted = intResp?.interrupt_requested ?? false;
      info.throttle = safe ? 'safe' : 'enabled';
      if (info.agent.id === this.selectedAgentId) {
        this.isSafeModeEnabled = safe;
        this.isInterrupted = interrupted;
      }
      this.cdr.markForCheck();
    } catch {
      // Non-critical — default to enabled
    }
  }

  // --- WebSocket for real-time outbox delivery ---

  private async connectWs(agentId: string): Promise<void> {
    this.disconnectWs();

    // Single-use ticket for the user-scoped WebSocket (no bearer in the URL)
    let tokenParam = '';
    const ticket = await this._wsTickets.ticket();
    if (ticket) {
      tokenParam = `&ticket=${encodeURIComponent(ticket)}`;
    }

    // Build WebSocket URL from muxUrl (handles both absolute and relative URLs)
    const muxUrl = ConfigService.muxUrl;
    let wsUrl: string;
    if (muxUrl.startsWith('http://') || muxUrl.startsWith('https://')) {
      const wsProtocol = muxUrl.startsWith('https') ? 'wss' : 'ws';
      const wsHost = muxUrl.replace(/^https?:\/\//, '');
      wsUrl = `${wsProtocol}://${wsHost}/ws/heartbeat?agent_id=${agentId}${tokenParam}`;
    } else {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsPath = muxUrl.startsWith('/') ? muxUrl : `/${muxUrl}`;
      wsUrl = `${wsProtocol}://${window.location.host}${wsPath}/ws/heartbeat?agent_id=${agentId}${tokenParam}`;
    }

    try {
      this.ws = new WebSocket(wsUrl);

      // Keepalive: if no message from server in 40s, assume zombie WS and reconnect.
      // Server sends {"type":"ping"} every ~25s when idle.
      const resetWsKeepalive = () => {
        clearTimeout(this._wsKeepaliveTimer);
        this._wsKeepaliveTimer = setTimeout(() => {
          if (this.ws && !this.destroyed && this.selectedAgentId === agentId) {
            console.warn('⚡ DM WebSocket keepalive timeout — reconnecting');
            this.ws.close();  // Will trigger onclose → reconnect
          }
        }, 40000);
      };
      resetWsKeepalive();

      this.ws.onmessage = (event) => {
        if (this.destroyed) return;
        resetWsKeepalive();
        try {
          const data = JSON.parse(event.data);

          // Server keepalive ping — respond and skip further processing
          if (data.type === 'ping') {
            this.ws?.send(JSON.stringify({ type: 'pong' }));
            return;
          }

          if (data.agent_id !== this.selectedAgentId) return;

          switch (data.type) {
            case 'message': {
              // Agent response from outbox (delivered via poll, not stream)
              // Skip if already loaded via HTTP (prevents duplication on page refresh)
              if (data.id && this.messages.some(m => m.messageId === data.id)) {
                break;
              }
              const content = this.unescapeContent(data.content);
              const styled = this.styleSleepMode(content, data.timestamp);
              const isAutonomous = data.metadata?.source === 'autonomous';
              this.messages = [
                ...this.messages,
                {
                  isSelf: false,
                  content: styled.content,
                  timestamp: data.timestamp,
                  isFinal: true,
                  isMuted: styled.entirelyMuted,
                  messageId: data.id,
                  ...(styled.sleepReason ? { sleepReason: styled.sleepReason } : {}),
                },
              ];
              this.cdr.markForCheck();
              this.chatScreen?.scrollToBottom();
              break;
            }

            case 'status': {
              // Presence + sleep/wake transition
              const info = this.selectedAgent;
              if (info) {
                info.status = data.is_sleeping ? 'sleeping' : 'awake';
                info.heartbeatCount = data.heartbeat_count;
                info.lastSleepTime = data.last_sleep_time;
                info.lastWakeTime = data.last_wake_time;
                info.sleepReason = data.sleep_reason;
                info.presence = data.presence || 'offline';
                info.presenceDetail = data.presence_detail || '';

                // Build status text from presence
                switch (info.presence) {
                  case 'busy':
                    this.statusText = info.presenceDetail || 'Thinking...';
                    break;
                  case 'idle':
                    this.statusText = `Sleeping — ${info.sleepReason || 'idle'}`;
                    break;
                  case 'online':
                    this.statusText = info.presenceDetail || `Awake — heartbeat #${data.heartbeat_count}`;
                    break;
                  default:
                    this.statusText = 'Offline';
                }
                this.cdr.markForCheck();
              }
              break;
            }

            case 'read_receipt':
              // Agent read our messages — update read state on matching messages
              const ids: string[] = data.message_ids || [];
              ids.forEach(id => this.readMessageIds.add(id));
              // Update `read` flag on existing messages for checkmark display
              let readUpdated = false;
              for (const msg of this.messages) {
                if (msg.messageId && ids.includes(msg.messageId) && msg.isSelf && !msg.read) {
                  msg.read = true;
                  if (data.handled_in) { msg.handledIn = data.handled_in; msg.handledAt = data.handled_at; }
                  readUpdated = true;
                }
              }
              if (readUpdated) {
                this.messages = [...this.messages];
              }
              this.cdr.markForCheck();
              break;

            case 'stream_start':
              this.ttsStartStreaming();
              // Start a new streaming message — chat-screen uses isStreaming for rendering mode
              this.streamingSource = data.source || 'autonomous';
              this.loading = true;  // Show animated typing indicator (3 pulsing dots)
              this.messages = [
                ...this.messages,
                {
                  isSelf: false,
                  content: '',
                  timestamp: new Date().toISOString(),
                  isStreaming: true,
                  isMuted: false,
                  sessionId: data.session_id,
                },
              ];
              this.streamingMessageIndex = this.messages.length - 1;
              this.cdr.markForCheck();
              this.chatScreen?.scrollToBottom();
              break;

            case 'stream_replace':
            case 'stream_chunk':
              // Append chunk or replace full content
              if (this.streamingMessageIndex !== null && this.messages[this.streamingMessageIndex]) {
                const newContent = data.type === 'stream_replace'
                  ? (data.content || '')
                  : this.messages[this.streamingMessageIndex].content + (data.content || '');
                this.messages[this.streamingMessageIndex] = {
                  ...this.messages[this.streamingMessageIndex],
                  content: newContent,
                };
                this.messages = [...this.messages];  // Trigger change detection
                this.cdr.markForCheck();
                this.chatScreen?.scrollToBottom();
                this.ttsFeedDisplay(this._stripTrailingChrome(newContent));
              }
              break;

            case 'stream_end':
              // Finalize — switch from streaming to final rendered mode
              if (this.streamingMessageIndex !== null && this.messages[this.streamingMessageIndex]) {
                const msg = this.messages[this.streamingMessageIndex];
                let content = typeof msg.content === 'string' ? msg.content : '';

                // Strip REACT markers before processing
                content = content.replace(/(?<=^|\x03)REACT:\s*\S+\s+on\s+\S+[ \t]*$/gm, '').trim();
                content = content.replace(/^ACK:.*$/gm, '').trim();

                // If only REACT markers + SLEEP_MODE remain, remove the bubble
                const contentWithoutSleep = content.replace(/^SLEEP_MODE.*$/gm, '').trim();
                if (!contentWithoutSleep) {
                  this.messages = this.messages.filter((_, i) => i !== this.streamingMessageIndex);
                } else {
                  const dmCancelledByUser = this._userCancelledStream;
                  const styled = this.styleSleepMode(content, msg.timestamp);
                  const isAutonomous = this.streamingSource === 'autonomous';
                  const { content: cleanContent, links } = ChatScreenComponent.extractLinks(styled.content);

                  this.messages[this.streamingMessageIndex] = {
                    ...msg,
                    content: cleanContent,
                    isStreaming: false,
                    isFinal: true,
                    isMuted: styled.entirelyMuted,
                    durationMs: data.duration_ms,
                    ...(data.fcp_ms != null ? { fcpMs: data.fcp_ms } : {}),
                    ...(data.input_tokens != null ? { inputTokens: data.input_tokens } : {}),
                    ...(data.output_tokens != null ? { outputTokens: data.output_tokens } : {}),
                    ...(data.cache_tokens != null ? { cacheTokens: data.cache_tokens } : {}),
                    ...(data.model ? { model: data.model } : {}),
                    ...(links.length ? { links } : {}),
                    ...(styled.sleepReason ? { sleepReason: styled.sleepReason } : {}),
                    ...(data.error && !this._userCancelledStream ? { isError: true } : {}),
                    ...(dmCancelledByUser ? { isCancelled: true } : {}),
                  };
                  this.messages = [...this.messages];
                }
              }
              // Capture last assistant response for TTS replay (always, not just when ambient is on)
              if (this.streamingMessageIndex !== null && this.messages[this.streamingMessageIndex]) {
                const finalContent = String(this.messages[this.streamingMessageIndex].content || '');
                if (finalContent.trim()) {
                  this._lastAssistantMessage = this.stripMarkdown(finalContent);
                }
              }
              this._userCancelledStream = false;
              this.ttsFlushAndStop();
              this.streamingMessageIndex = null;
              this.streamingSource = null;
              this.loading = false;
              this._clearInterruptState();
              this.cdr.markForCheck();
              this.chatScreen?.scrollToBottom();
              break;

            case 'message_persisted': {
              // Attach file path and transcript path to the last assistant message
              const fp = data.file_path;
              const tp = data.transcript_path;
              if (fp || tp) {
                for (let i = this.messages.length - 1; i >= 0; i--) {
                  if (!this.messages[i].isSelf) {
                    this.messages[i] = { ...this.messages[i], ...(fp ? { turnPath: fp } : {}), ...(tp ? { transcriptPath: tp } : {}) };
                    this.messages = [...this.messages];
                    this.cdr.markForCheck();
                    break;
                  }
                }
              }
              break;
            }
          }
        } catch (e) {
          console.warn('Heartbeat WS parse error:', e);
        }
      };

      this.ws.onclose = () => {
        if (!this.destroyed && this.selectedAgentId === agentId) {
          // Mark interrupted stream with error indicator
          if (this.streamingMessageIndex !== null && this.messages[this.streamingMessageIndex]) {
            this.messages[this.streamingMessageIndex] = {
              ...this.messages[this.streamingMessageIndex],
              isStreaming: false,
              isFinal: true,
              isError: true,
              content: this.messages[this.streamingMessageIndex].content +
                '\n\n*[Connection lost — response may be incomplete]*',
            };
            this.messages = [...this.messages];
            this.streamingMessageIndex = null;
            this.streamingSource = null;
            this.loading = false;
            this.cdr.markForCheck();
          }
          // Reload messages from disk (catches responses that completed while disconnected),
          // then reconnect WS
          setTimeout(() => {
            if (!this.destroyed && this.selectedAgentId === agentId) {
              this.loadMessages().then(() => this.connectWs(agentId));
            }
          }, 3000);
        }
      };

      this.ws.onerror = (err) => {
        console.warn('Heartbeat WS error:', err);
      };
    } catch (err) {
      console.error('Failed to connect heartbeat WS:', err);
    }
  }

  private _onVisibilityChange(): void {
    if (document.hidden || this.destroyed) return;

    const wsIsDead = (ws: WebSocket | null) =>
      !ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING;

    if (this.activeView === 'channel' && this.selectedChannelId) {
      if (wsIsDead(this.channelWs)) {
        console.warn('⚡ Tab resumed — channel WebSocket dead, recovering');
        if (this.channelStreamingIndex !== null && this.channelMessages[this.channelStreamingIndex]) {
          this.channelMessages[this.channelStreamingIndex] = {
            ...this.channelMessages[this.channelStreamingIndex],
            isStreaming: false,
            isFinal: true,
            isError: true,
            content: this.channelMessages[this.channelStreamingIndex].content +
              '\n\n*[Connection lost — response may be incomplete]*',
          };
          this.channelMessages = [...this.channelMessages];
          this.channelStreamingIndex = null;
          this._channelStreamRaw = '';
        }
        this.channelLoading = false;
        this.channelLoadingState = '';
        this.channelSpinnerText = '';
        clearTimeout(this._thinkingTimeout);
        clearTimeout(this._stallTimeout);
        this.channelWs = null;
        this.loadChannelMessages().then(() => this.connectChannelWs(this.selectedChannelId!));
        this.cdr.markForCheck();
      }
    } else if (this.activeView === 'dm' && this.selectedAgentId) {
      if (wsIsDead(this.ws)) {
        console.warn('⚡ Tab resumed — DM WebSocket dead, recovering');
        if (this.streamingMessageIndex !== null && this.messages[this.streamingMessageIndex]) {
          this.messages[this.streamingMessageIndex] = {
            ...this.messages[this.streamingMessageIndex],
            isStreaming: false,
            isFinal: true,
            isError: true,
            content: this.messages[this.streamingMessageIndex].content +
              '\n\n*[Connection lost — response may be incomplete]*',
          };
          this.messages = [...this.messages];
          this.streamingMessageIndex = null;
          this.streamingSource = null;
        }
        this.loading = false;
        this.ws = null;
        this.loadMessages().then(() => this.connectWs(this.selectedAgentId!));
        this.cdr.markForCheck();
      }
    }
  }

  private disconnectWs(): void {
    clearTimeout(this._wsKeepaliveTimer);
    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnect
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Normalized signature of an assistant reply's *displayed* content, used to
   * recognize the same reply arriving through more than one path — a live
   * stream, a reconnect replay, or a persistence catch-up. Strips the pieces
   * that differ between those paths (REACT/SLEEP lines, the trailing LINKS
   * footer, whitespace) so identical replies collapse to one signature.
   * Returns '' for empty/whitespace content (never dedupe on empty).
   */
  private _channelMsgSig(content: unknown): string {
    let s = typeof content === 'string' ? content : String(content ?? '');
    s = s.replace(/(?<=^|\x03)REACT:\s*\S+\s+on\s+\S+[ \t]*$/gm, '');
    s = s.replace(/^ACK:.*$/gm, '');
    s = s.replace(/^SLEEP_MODE.*$/gm, '');
    s = s.replace(/LINKS:\s*(?:\[\[[^\]]+\]\]\s*)+\s*$/i, '');
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  }

  private async connectChannelWs(channelId: string): Promise<void> {
    this.disconnectChannelWs();

    const token = await this._wsTickets.ticket();
    // Stale-connect guard: the user navigated away while the ticket fetch was in
    // flight. Connecting anyway would orphan a live socket whose handlers write
    // into whatever channel is currently displayed (cross-channel bleed).
    if (this.destroyed || this.selectedChannelId !== channelId || this.activeView !== 'channel') return;
    if (token === null) {
      console.warn('Channel WS ticket unavailable; retrying connection');
      setTimeout(() => {
        if (!this.destroyed && this.selectedChannelId === channelId && this.activeView === 'channel') {
          this.connectChannelWs(channelId);
        }
      }, 1500);
      return;
    }

    // A device-qualified team routes the live stream through the presence
    // pipe; the event vocabulary is identical, so everything below is shared.
    const remote = parsePlaceAddress(this.teamName);
    const effectiveTeam = remote ? remote.team : this.teamName;
    const teamParam = effectiveTeam ? `&team=${encodeURIComponent(effectiveTeam)}` : '';
    const tokenParam = token ? `?ticket=${encodeURIComponent(token)}` : '';
    const wsRoute = remote
      ? `/ws/remote/${encodeURIComponent(remote.device)}/channel/${channelId}`
      : `/ws/channel/${channelId}`;

    const muxUrl = ConfigService.muxUrl;
    let wsUrl: string;
    if (muxUrl.startsWith('http://') || muxUrl.startsWith('https://')) {
      const wsProtocol = muxUrl.startsWith('https') ? 'wss' : 'ws';
      const wsHost = muxUrl.replace(/^https?:\/\//, '');
      wsUrl = `${wsProtocol}://${wsHost}${wsRoute}${tokenParam}${teamParam}`;
    } else {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsPath = muxUrl.startsWith('/') ? muxUrl : `/${muxUrl}`;
      wsUrl = `${wsProtocol}://${window.location.host}${wsPath}${wsRoute}${tokenParam}${teamParam}`;
    }

    try {
      const ws = new WebSocket(wsUrl);
      this.channelWs = ws;

      // Keepalive: if no message from server in 40s, assume zombie WS and reconnect.
      // Server sends {"type":"ping"} every ~25s when idle.
      let _lastWsMessage = Date.now();
      let _wsMessageCount = 0;
      let _wsPingCount = 0;
      const resetChannelKeepalive = () => {
        clearTimeout(this._channelWsKeepaliveTimer);
        this._channelWsKeepaliveTimer = setTimeout(() => {
          if (this.channelWs && !this.destroyed && this.selectedChannelId === channelId && this.activeView === 'channel') {
            const silentMs = Date.now() - _lastWsMessage;
            console.warn(`⚡ Channel WS keepalive timeout — no message for ${silentMs}ms (total msgs=${_wsMessageCount}, pings=${_wsPingCount}, readyState=${this.channelWs.readyState}, streaming=${this.channelStreamingIndex !== null})`);
            this.channelWs.close();  // Will trigger onclose → reconnect
          }
        }, 40000);
      };
      resetChannelKeepalive();

      ws.onmessage = (event) => {
        if (this.destroyed) return;
        // Stale-socket guard: this socket belongs to `channelId`. If a later
        // connect superseded it, or the user now views a different channel, its
        // events must never touch view state — that renders channel A's reply
        // inside channel B. Close the orphan; the reply is persisted server-side
        // and loads when the user returns to the origin channel.
        if (this.channelWs !== ws || this.selectedChannelId !== channelId) {
          try { ws.close(); } catch { /* already closing */ }
          return;
        }
        if (this.activeView !== 'channel') return;
        _lastWsMessage = Date.now();
        _wsMessageCount++;
        resetChannelKeepalive();
        try {
          const data = JSON.parse(event.data);

          // Server keepalive ping — respond and skip further processing
          if (data.type === 'ping') {
            _wsPingCount++;
            this.channelWs?.send(JSON.stringify({ type: 'pong' }));
            return;
          }

          const _contentLen = typeof data.content === 'string' ? data.content.length : 0;
          console.log(`[ch-trace] WS recv: type=${data.type}, content_len=${_contentLen}, channelStreamingIndex=${this.channelStreamingIndex}, channelLoading=${this.channelLoading}`);

          switch (data.type) {
            case 'message': {
              // Hide reaction notification messages — they're system plumbing
              if (data.metadata?.source === 'reaction') break;
              // Dialog answers render inside the question card (selected state), not as bubbles
              if (data.metadata?.source === 'dialog-answer') break;
              // Work session archived — refresh nav tree to remove session from sidebar.
              // Fall through to normal message handling so the notice renders immediately.
              if (data.metadata?.type === 'work_session_archived' || data.metadata?.type === 'channel_archived') {
                this.loadNavTree();
              }
              // Persisted message catch-up — dedup against HTTP-loaded messages
              if (data.id && this.channelMessages.some(m => m.messageId === data.id)) {
                break;
              }
              const unescaped = this.unescapeContent(data.content);
              const isOutbound = data.role !== 'user';
              if (isOutbound) this.maybeChannelAuthRecheck(unescaped, data.metadata);
              // Cross-user: inbound message from another user in a shared channel
              const wsMe = JSON.parse(localStorage.getItem('me') || '{}')?.username;
              const isCrossUser = !isOutbound && !!data.from && data.from !== wsMe;
              if (isCrossUser && data.channel_id) {
                this.sharedChannels.add(data.channel_id);
              }
              const msgRaw = isOutbound ? unescaped : this._stripContextEnvelope(unescaped);
              const { cleanContent: msgContent, actions: msgActions } = isOutbound
                ? this.extractChannelActions(msgRaw)
                : { cleanContent: msgRaw, actions: [] };
              // A reply that arrives persisted while an EMPTY placeholder bubble is
              // open (the "connecting" dots from 'thinking') is that turn's reply
              // delivered off-stream — e.g. the channel's WebSocket is attached to a
              // different agent's queue, or the turn ended before any chunk was
              // forwarded (policy refusals, 2026-08-28). Fill the placeholder in
              // place instead of leaving it to the 30s reaper beside a twin.
              const msgSigOf = (c: string) => this._channelMsgSig(c);
              if (isOutbound && this.channelStreamingIndex !== null) {
                const ph = this.channelMessages[this.channelStreamingIndex];
                if (ph && ph.isStreaming && !String(ph.content || '').trim()) {
                  clearTimeout(this._thinkingTimeout);
                  const { content: phContent, links: phLinks } = ChatScreenComponent.extractLinks(msgContent);
                  this.channelMessages[this.channelStreamingIndex] = {
                    ...ph, content: phContent, isStreaming: false, isFinal: true, messageId: data.id,
                    timestamp: data.timestamp || ph.timestamp,
                    ...(msgSigOf(phContent) ? { dedupSig: msgSigOf(phContent) } : {}),
                    ...(phLinks.length ? { links: phLinks } : {}),
                    ...(msgActions.length ? { channelActions: msgActions } : {}),
                  };
                  this.channelMessages = [...this.channelMessages];
                  this.channelStreamingIndex = null;
                  this._channelStreamStarted = false;
                  this.channelLoading = false;
                  this.channelLoadingState = '';
                  this.channelSpinnerText = '';
                  this.cdr.markForCheck();
                  this.chatScreen?.scrollToBottom();
                  break;
                }
              }
              // Idempotent dedup: an assistant reply the live stream already
              // rendered can arrive again as a persistence catch-up (jupiter's
              // SSH persist can land after the old timing window). Match it to
              // the existing bubble by content signature and merge — never
              // append a twin. Replaces the previous fragile 5s time window.
              const msgSig = isOutbound ? this._channelMsgSig(msgContent) : '';
              if (isOutbound && msgSig) {
                // Scan back only through the current turn (stop at the previous
                // user message) and require message-id agreement, so two
                // genuinely-separate but identical replies — e.g. "Done." to two
                // prompts — are never collapsed. Only a redelivery of THIS turn's
                // reply merges into the existing bubble.
                let merged = false;
                let seenReply = false;
                for (let i = this.channelMessages.length - 1; i >= 0; i--) {
                  const m = this.channelMessages[i];
                  // Follow-ups the user sent mid-turn sit BELOW the streamed bubble;
                  // skip them until the bubble is found, then the next user message
                  // is the real turn boundary.
                  if (m.isSelf) { if (seenReply) break; else continue; }
                  seenReply = true;
                  if (m.dedupSig === msgSig && (!m.messageId || !data.id || m.messageId === data.id)) {
                    if (!m.messageId && data.id) m.messageId = data.id;
                    this.channelMessages = [...this.channelMessages];
                    this.cdr.markForCheck();
                    merged = true;
                    break;
                  }
                }
                if (merged) break;
              }
              // My own line echoed back by persistence (raw-command path pushes
              // message_persisted before the HTTP response lands, so the optimistic
              // bubble has no id yet): attach the id, never append a twin (2026-09-02).
              if (!isOutbound && !isCrossUser) {
                const mine = this.channelMessages[this.channelMessages.length - 1];
                if (mine?.isSelf && !mine.messageId && mine.content === msgContent) {
                  if (data.id) mine.messageId = data.id;
                  if (data.timestamp) mine.timestamp = data.timestamp;
                  this.channelMessages = [...this.channelMessages];
                  this.cdr.markForCheck();
                  break;
                }
              }
              // Outbound replies delivered off-stream (organic relay) carry their LINKS
              // footer as text — extract it so the chips render live, as on reload.
              const { content: newContent, links: newLinks } = isOutbound
                ? ChatScreenComponent.extractLinks(msgContent)
                : { content: msgContent, links: [] as string[] };
              const orchName = data.metadata?.orchestration || (data.metadata?.source?.startsWith('orchestration') ? data.metadata.source.replace(/_/g, ' ') : null);
              const newMsg = {
                isSelf: !isOutbound && !isCrossUser,
                content: newContent,
                timestamp: data.timestamp,
                isFinal: true as const,
                messageId: data.id,
                ...(newLinks.length ? { links: newLinks } : {}),
                ...(msgSig ? { dedupSig: msgSig } : {}),
                ...(msgActions.length ? { channelActions: msgActions } : {}),
                ...(isCrossUser ? { agentName: data.from } : {}),
                ...(orchName ? { orchestration: orchName } : {}),
              };
              if (this.viewingChannelHistory) {
                break;
              }
              this.channelMessages = [...this.channelMessages, newMsg];
              this.cdr.markForCheck();
              this.chatScreen?.scrollToBottom();
              break;
            }

            case 'action': {
              if (data.action === 'open_app' && data.app_id) {
                this._openApp(data.app_id, data.layout).then(success => {
                  if (this.channelWs && this.channelWs.readyState === WebSocket.OPEN && data.action_id) {
                    this.channelWs.send(JSON.stringify({
                      type: 'action_result',
                      action_id: data.action_id,
                      action: 'open_app',
                      app_id: data.app_id,
                      success,
                      message: success ? `Opened ${data.app_id}` : `Unknown app: ${data.app_id}`,
                    }));
                  }
                });
              } else if (data.action === 'start_orchestration' && data.slug) {
                this._startOrchestrationFromAction(data);
              }
              break;
            }

            case 'thinking':
              if (this.viewingChannelHistory) break;
              // Agent received the message and is connecting — show the live terminal
              // spinner line verbatim (falls back to throbbing dots if text is absent).
              this.channelLoading = true;
              this.channelLoadingState = 'connecting';
              this.channelSpinnerText = data.text || '';
              // Add placeholder bubble so dots actually render (typing indicator only shows inside a turn)
              if (this.channelStreamingIndex === null) {
                this.channelMessages = [
                  ...this.channelMessages,
                  { isSelf: false, content: '', timestamp: new Date().toISOString(), isStreaming: true },
                ];
                this.channelStreamingIndex = this.channelMessages.length - 1;
              }
              // Safety timeout: clear loading if stream_start never arrives (e.g. service restart).
              // Only arm it during the genuine connecting phase — once the stream has started,
              // a late 'thinking' frame (mid-turn working spinner) must NOT re-arm this reaper,
              // or a long think/tool gap (>30s) would destroy the live bubble and every
              // subsequent content frame would be dropped (channelStreamingIndex=null).
              if (!this._channelStreamStarted) {
                clearTimeout(this._thinkingTimeout);
                this._thinkingTimeout = setTimeout(() => {
                  this.channelLoading = false;
                  this.channelLoadingState = '';
                  this.channelSpinnerText = '';
                  if (this.channelStreamingIndex !== null) {
                    this.channelMessages = this.channelMessages.filter((_, i) => i !== this.channelStreamingIndex);
                    this.channelStreamingIndex = null;
                  }
                  this.cdr.markForCheck();
                }, 30000);
              }
              this.cdr.markForCheck();
              this.chatScreen?.scrollToBottom();
              break;

            case 'stream_start':
              console.log(`[ch-trace] stream_start: channelStreamingIndex_before=${this.channelStreamingIndex}, session_id=${data.session_id}`);
              this.ttsStartStreaming();
              // Reached Claude — switch to blue "generating" dots
              clearTimeout(this._thinkingTimeout);
              this._channelStreamStarted = true;
              this.channelLoading = true;
              this.channelLoadingState = 'generating';
              this._channelStreamRaw = '';
              // If there's already a finalized streaming bubble from a replayed
              // old stream, remove it — the new stream supersedes it.
              if (this.channelStreamingIndex !== null &&
                  this.channelMessages[this.channelStreamingIndex]?.isFinal) {
                this.channelMessages = this.channelMessages.filter(
                  (_, i) => i !== this.channelStreamingIndex
                );
                this.channelStreamingIndex = null;
              }
              if (this.channelStreamingIndex === null) {
                // No placeholder from thinking — create bubble now
                this.channelMessages = [
                  ...this.channelMessages,
                  { isSelf: false, content: '', timestamp: new Date().toISOString(), isStreaming: true, sessionId: data.session_id },
                ];
                this.channelStreamingIndex = this.channelMessages.length - 1;
              } else {
                // Reuse existing placeholder bubble — clear stale content and attach session info
                this.channelMessages[this.channelStreamingIndex] = {
                  ...this.channelMessages[this.channelStreamingIndex],
                  content: '',
                  sessionId: data.session_id,
                };
              }
              // Stall timer: if no content arrives within 30s, turn dots amber
              clearTimeout(this._stallTimeout);
              this._stallTimeout = setTimeout(() => {
                this.channelLoadingState = 'stalled';
                this.cdr.markForCheck();
              }, 30000);
              console.log(`[ch-trace] stream_start: channelStreamingIndex_after=${this.channelStreamingIndex}, msgs_count=${this.channelMessages.length}`);
              this.cdr.markForCheck();
              this.chatScreen?.scrollToBottom();
              break;

            case 'stream_replace':
            case 'stream_chunk':
              // Clear thinking safety timeout — stream is alive, no need to reap
              clearTimeout(this._thinkingTimeout);
              // Reset stall timer on each chunk — dots stay blue through tool calls
              clearTimeout(this._stallTimeout);
              this._stallTimeout = setTimeout(() => {
                this.channelLoadingState = 'stalled';
                this.cdr.markForCheck();
              }, 30000);
              // Safety net: content is arriving but the bubble is gone (reaped by a
              // connecting-phase timeout, lost to a reconnect, etc.). Never drop content —
              // recreate the streaming bubble so the response always lands.
              if (this.channelStreamingIndex === null) {
                this.channelMessages = [
                  ...this.channelMessages,
                  { isSelf: false, content: '', timestamp: new Date().toISOString(), isStreaming: true },
                ];
                this.channelStreamingIndex = this.channelMessages.length - 1;
                this.channelLoading = true;
              }
              if (this.channelStreamingIndex !== null && this.channelMessages[this.channelStreamingIndex]) {
                // Replace: full content each time.  Chunk: append delta.
                if (data.type === 'stream_replace') {
                  this._channelStreamRaw = data.content || '';
                } else {
                  this._channelStreamRaw = (this._channelStreamRaw || '') + (data.content || '');
                }
                // Strip trailing TUI chrome (spinner, separator, prompt, status bar)
                // before rendering — these flicker into view during streaming.
                const stripped = this._stripTrailingChrome(this._channelStreamRaw);
                // Extract LINKS during streaming so tags render in footer immediately
                const { content: display, links } = ChatScreenComponent.extractLinks(stripped);
                // Don't blank the bubble when chrome stripping produces empty content —
                // keep the previous display until substantive content arrives.
                if (!display.trim() && this.channelMessages[this.channelStreamingIndex].content) {
                  break;
                }
                // Real content is flowing — the spinner line gives way to the response.
                if (display.trim()) { this.channelSpinnerText = ''; }
                this.channelMessages[this.channelStreamingIndex] = {
                  ...this.channelMessages[this.channelStreamingIndex],
                  content: display,
                  ...(links.length ? { links } : {}),
                };
                this.channelMessages = [...this.channelMessages];
                this.cdr.markForCheck();
                this.chatScreen?.scrollToBottom();
                this.ttsFeedDisplay(display);
              }
              break;

            case 'stream_end': {
              // A reply from someone else's agent, read as it arrived: clear this
              // user's unread marker for the conversation (the Directory's badge).
              if (HeartbeatWidgetComponent.isGuestChannelId(channelId) && this.selectedChannelId === channelId && !document.hidden) {
                const gc = this.guestChannels.find(c => c.id === channelId);
                if (gc) gc.unread = 0;
                void this.sessionService.markChannelRead(channelId, undefined, this.teamName);
              }
              console.log(`[ch-trace] stream_end ENTRY: channelStreamingIndex=${this.channelStreamingIndex}, has_msg=${this.channelStreamingIndex !== null && !!this.channelMessages[this.channelStreamingIndex]}, data_content_len=${data.content ? data.content.length : 0}, raw_len=${this._channelStreamRaw?.length || 0}`);
              clearTimeout(this._thinkingTimeout);
              clearTimeout(this._stallTimeout);
              this.channelLoadingState = '';
              this.channelSpinnerText = '';
              if (this.channelStreamingIndex !== null && this.channelMessages[this.channelStreamingIndex]) {
                // Prefer authoritative content from stream_end (JSONL-sourced),
                // fall back to raw accumulated TUI content.
                const rawContent = data.content || this._channelStreamRaw || this.channelMessages[this.channelStreamingIndex].content;
                this.maybeChannelAuthRecheck(rawContent, data);
                this._channelStreamRaw = '';
                let streamContent = (typeof rawContent === 'string' ? rawContent : (rawContent as any)?.toString?.() || '');
                streamContent = streamContent.replace(/(?<=^|\x03)REACT:\s*\S+\s+on\s+\S+[ \t]*$/gm, '');
                streamContent = streamContent.replace(/^ACK:.*$/gm, '');
                streamContent = streamContent.replace(/^SLEEP_MODE.*$/gm, '');
                // System notices (Context compacting, Session stopped) are preserved for user visibility.
                // LINKS extraction regex in ChatScreenComponent.extractLinks() handles trailing notices.
                streamContent = streamContent.trim();

                if (!streamContent) {
                  const rawStr = typeof rawContent === 'string' ? rawContent : '';
                  const hadRawContent = rawStr.trim().length > 0;
                  if (hadRawContent) {
                    // Reaction-only / SLEEP_MODE response — the raw had REACT/SLEEP
                    // markers that stripped to empty. Legitimate; remove the bubble.
                    this.channelMessages = this.channelMessages.filter(
                      (_, i) => i !== this.channelStreamingIndex
                    );
                  } else {
                    // Empty turn with NO content at all — the model returned nothing
                    // (safety-flag + model switch, overload, or a dropped stream, and
                    // the CLI's own notice wasn't relayed). Never leave the user with a
                    // silent blank: surface a visible, retryable marker instead of
                    // dropping the bubble, so "the app looks broken" never happens again.
                    this.channelMessages[this.channelStreamingIndex] = {
                      ...this.channelMessages[this.channelStreamingIndex],
                      content: '_No response received — the model may have been flagged and switched, or the service was overloaded. Try resending._',
                      isStreaming: false,
                      isFinal: true,
                      isError: true,
                    };
                    this.channelMessages = [...this.channelMessages];
                  }
                } else {
                  const { cleanContent: actionContent, actions } = this.extractChannelActions(streamContent);
                  const cancelledByUser = this._userCancelledStream;
                  const displayContent = (actionContent || streamContent);
                  const { content: finalContent, links } = ChatScreenComponent.extractLinks(displayContent);
                  const endSig = this._channelMsgSig(finalContent);
                  const finalizedMsg: ChatService.ChatMessage = {
                    ...this.channelMessages[this.channelStreamingIndex],
                    content: finalContent,
                    isStreaming: false,
                    isFinal: true,
                    durationMs: data.duration_ms,
                    ...(endSig ? { dedupSig: endSig } : {}),
                    ...(data.fcp_ms != null ? { fcpMs: data.fcp_ms } : {}),
                    ...(data.input_tokens != null ? { inputTokens: data.input_tokens } : {}),
                    ...(data.output_tokens != null ? { outputTokens: data.output_tokens } : {}),
                    ...(data.cache_tokens != null ? { cacheTokens: data.cache_tokens } : {}),
                    ...(data.model ? { model: data.model } : {}),
                    ...this._liveHeaderFill(finalContent, data),
                    ...(links.length ? { links } : {}),
                    ...(actions.length ? { channelActions: actions } : {}),
                    ...(data.error && !this._userCancelledStream ? { isError: true } : {}),
                    ...(cancelledByUser ? { isCancelled: true } : {}),
                  };
                  this.channelMessages[this.channelStreamingIndex] = finalizedMsg;
                  // Reconnect-replay collapse: if an earlier connection already
                  // finalized this same reply into another bubble, the replayed
                  // stream just rebuilt a second copy here. Drop the older twin(s)
                  // by signature, carrying over a server messageId if this fresh
                  // copy lacks one.
                  // Same-turn twins only: scan back from this bubble to the
                  // previous user message. A reconnect replay rebuilds the reply
                  // within one turn; two separate identical replies are divided
                  // by a user message and must NOT be collapsed.
                  const twins: ChatService.ChatMessage[] = [];
                  if (endSig) {
                    for (let i = this.channelStreamingIndex - 1; i >= 0; i--) {
                      const m = this.channelMessages[i];
                      if (m.isSelf) break;
                      if (m.dedupSig === endSig) twins.push(m);
                    }
                  }
                  if (twins.length) {
                    if (!finalizedMsg.messageId) {
                      const withId = twins.find(t => t.messageId);
                      if (withId) finalizedMsg.messageId = withId.messageId;
                    }
                    this.channelMessages = this.channelMessages.filter(m => !twins.includes(m));
                  } else {
                    this.channelMessages = [...this.channelMessages];
                  }
                }
              }
              // Capture last assistant response for TTS replay (always, not just when ambient is on)
              if (this.channelStreamingIndex !== null && this.channelMessages[this.channelStreamingIndex]) {
                const chFinal = String(this.channelMessages[this.channelStreamingIndex].content || '');
                if (chFinal.trim()) {
                  this._lastAssistantMessage = this.stripMarkdown(chFinal);
                }
              }
              this._userCancelledStream = false;
              this.ttsFlushAndStop();
              this.channelStreamingIndex = null;
              this._channelStreamStarted = false;
              this.channelLoading = false;
              this._channelStreamEndTime = Date.now();
              this._clearInterruptState();
              console.log(`[ch-trace] stream_end EXIT: channelStreamingIndex=${this.channelStreamingIndex}, channelLoading=${this.channelLoading}, msgs_count=${this.channelMessages.length}`);
              this.cdr.markForCheck();
              this.chatScreen?.scrollToBottom();
              break;
            }

            case 'message_persisted': {
              // Attach file path, message ID, and orchestration to the last assistant message
              const chFp = data.file_path;
              const chMsgId = data.message_id;
              const chOrch = data.orchestration;
              if (chFp || chMsgId || chOrch) {
                for (let i = this.channelMessages.length - 1; i >= 0; i--) {
                  if (!this.channelMessages[i].isSelf) {
                    this.channelMessages[i] = {
                      ...this.channelMessages[i],
                      ...(chFp ? { turnPath: chFp } : {}),
                      ...(chMsgId ? { messageId: chMsgId } : {}),
                      ...(chOrch ? { orchestration: chOrch } : {}),
                    };
                    this.channelMessages = [...this.channelMessages];
                    this.cdr.markForCheck();
                    break;
                  }
                }
              }
              break;
            }

            case 'read_receipt': {
              // Agent read channel messages — update checkmarks and reposition
              // any inbound messages that arrived mid-stream (sit below the streaming
              // bubble until acknowledged, then move to chronological position above it).
              const readIds: string[] = data.message_ids || [];
              let msgs = [...this.channelMessages];
              let streamIdx = this.channelStreamingIndex;
              let repositioned = false;
              // Anchor for the fold: the reply the follow-up was handled in (the
              // receipt now arrives after that reply is persisted, so the streaming
              // bubble is usually gone), else the still-streaming bubble.
              const anchorIdx = (): number | null => {
                if (data.handled_in) {
                  const k = msgs.findIndex(m => !m.isSelf && m.messageId === data.handled_in);
                  if (k >= 0) return k;
                  // The reply that just finished may not carry its server id yet
                  // (channel replies get it from the persistence catch-up, which
                  // can race this receipt): the last finalized reply is that reply.
                  for (let j = msgs.length - 1; j >= 0; j--) {
                    const m = msgs[j];
                    if (!m.isSelf && m.isFinal && !m.messageId) { m.messageId = data.handled_in; return j; }
                    if (!m.isSelf && m.messageId) break;
                  }
                }
                return streamIdx;
              };

              for (let i = msgs.length - 1; i >= 0; i--) {
                const msg = msgs[i];
                if (msg.messageId && readIds.includes(msg.messageId) && msg.isSelf) {
                  msg.read = true;
                  if (data.handled_in) { msg.handledIn = data.handled_in; msg.handledAt = data.handled_at; }
                  // A follow-up that arrived during the reply sits below it in arrival
                  // order; once acknowledged it moves up above that reply so the user
                  // sees it as dealt with rather than pending.
                  const a = anchorIdx();
                  if (a !== null && i > a) {
                    msgs.splice(i, 1);
                    msgs.splice(a, 0, msg);
                    if (streamIdx !== null && a <= streamIdx) streamIdx += 1;
                    repositioned = true;
                  }
                }
              }

              if (repositioned) {
                this.channelStreamingIndex = streamIdx;
              }
              this.channelMessages = msgs;
              this.cdr.markForCheck();
              break;
            }

            case 'orchestration_status': {
              this.orchestrationStatus = {
                active: data.state !== 'done' && data.state !== 'error' && data.state !== 'aborted',
                name: data.name,
                slug: data.slug,
                state: data.state,
                step: data.step,
                progress: data.progress,
                error: data.error,
                gate_prompt: data.gate_prompt,
                gate_options: data.gate_options,
              };
              if (data.state === 'done' || data.state === 'aborted') {
                this.orchestrationStatus = { active: false };
                this.orchestrationPanelOpen = false;
              } else if (data.state === 'error') {
                this.orchestrationPanelOpen = true;
              }
              this.cdr.markForCheck();
              break;
            }
          }
        } catch (e) {
          console.warn('Channel WS parse error:', e);
        }
      };

      ws.onclose = (ev) => {
        const silentMs = Date.now() - _lastWsMessage;
        console.warn(`[ch-diag] WS onclose: code=${ev.code}, reason=${ev.reason || '(none)'}, wasClean=${ev.wasClean}, silentMs=${silentMs}, totalMsgs=${_wsMessageCount}, pings=${_wsPingCount}, streaming=${this.channelStreamingIndex !== null}, channelLoading=${this.channelLoading}`);
        // Superseded socket — never mutate state or schedule a reconnect for it.
        if (this.channelWs !== ws) return;
        if (!this.destroyed && this.selectedChannelId === channelId && this.activeView === 'channel') {
          clearTimeout(this._thinkingTimeout);
          clearTimeout(this._stallTimeout);
          this.channelLoadingState = '';
          // Mark interrupted channel stream
          if (this.channelStreamingIndex !== null && this.channelMessages[this.channelStreamingIndex]) {
            const wasCancelled = this._userCancelledStream;
            this._userCancelledStream = false;
            this.channelMessages[this.channelStreamingIndex] = {
              ...this.channelMessages[this.channelStreamingIndex],
              isStreaming: false,
              isFinal: true,
              ...(wasCancelled ? {} : {
                isError: true,
                content: this.channelMessages[this.channelStreamingIndex].content +
                  '\n\n*[Connection lost — response may be incomplete]*',
              }),
            };
            this.channelMessages = [...this.channelMessages];
            this.channelStreamingIndex = null;
            this.channelLoading = false;
            this.cdr.markForCheck();
          } else if (this.channelLoading) {
            // Was in 'thinking' state (no bubble yet) — clear the spinner
            this.channelLoading = false;
            this.cdr.markForCheck();
          }
          // Reload messages from disk (catches responses that completed while disconnected),
          // then reconnect WS
          setTimeout(() => {
            if (!this.destroyed && this.selectedChannelId === channelId && this.activeView === 'channel') {
              this.loadChannelMessages().then(() => this.connectChannelWs(channelId));
            }
          }, 3000);
        }
      };

      ws.onerror = (err) => {
        const silentMs = Date.now() - _lastWsMessage;
        console.warn(`[ch-diag] WS onerror: silentMs=${silentMs}, totalMsgs=${_wsMessageCount}, pings=${_wsPingCount}, readyState=${this.channelWs?.readyState}`, err);
      };
    } catch (err) {
      console.error('Failed to connect channel WS:', err);
    }
  }

  private disconnectChannelWs(): void {
    clearTimeout(this._channelWsKeepaliveTimer);
    if (this.channelWs) {
      this.channelWs.onmessage = null;
      this.channelWs.onclose = null;
      this.channelWs.close();
      this.channelWs = null;
    }
    this.channelStreamingIndex = null;
  }

  private _connectTelemetryWsForDm(agentId: string): void {
    // Always reconnect so the message handler filters for the correct DM channel
    this._disconnectTelemetryWs();
    const dmChannelId = `dm-${agentId}`;
    this._openTelemetryWs((event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'telemetry_batch' && data.events) {
          for (const evt of data.events) {
            if (evt.type === 'reaction' && evt.channel_id === dmChannelId && this._isForMe(evt)) {
              const msg = this.messages.find(m => m.messageId === evt.message_id);
              if (msg) {
                msg.reactions = evt.reactions;
                this.cdr.detectChanges();
              }
            }
            this._handleChannelActivityEvent(evt);
          }
        }
      } catch (e) { /* ignore parse errors */ }
    });
  }

  private _connectTelemetryWs(): void {
    // Always reconnect so the message handler filters for the correct channel
    this._disconnectTelemetryWs();
    this._openTelemetryWs((event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'telemetry_batch' && data.events) {
          for (const evt of data.events) {
            if (evt.type === 'reaction' && evt.channel_id === this.selectedChannelId && this._isForMe(evt)) {
              const msg = this.channelMessages.find(m => m.messageId === evt.message_id);
              if (msg) {
                msg.reactions = evt.reactions;
                this.cdr.detectChanges();
              }
            }
            this._handleChannelActivityEvent(evt);
          }
        }
      } catch (e) { /* ignore parse errors */ }
    });
  }

  private _telemetryGen = 0;

  /** Open the telemetry socket WITH the auth token, so the server tags it with
   *  this user and sends only this user's channel/agent events. Channel ids are
   *  unique per user, not per host: untagged, another user's #tcf turn lit this
   *  user's #tcf dot (app.jov.ai, 2026-09-30). The token fetch is async; a
   *  connect superseded by a newer one is dropped. */
  private _openTelemetryWs(onMessage: (event: MessageEvent) => void): void {
    const gen = ++this._telemetryGen;
    this._wsTickets.ticket().then((token) => {
      if (gen !== this._telemetryGen) return;
      const muxUrl = ConfigService.muxUrl;
      let wsUrl: string;
      if (muxUrl.startsWith('http://') || muxUrl.startsWith('https://')) {
        const wsProtocol = muxUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = muxUrl.replace(/^https?:\/\//, '');
        wsUrl = `${wsProtocol}://${wsHost}/ws/telemetry`;
      } else {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const wsPath = muxUrl.startsWith('/') ? muxUrl : `/${muxUrl}`;
        wsUrl = `${wsProtocol}://${window.location.host}${wsPath}/ws/telemetry`;
      }
      if (token) wsUrl += `?ticket=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      this._telemetryWs = ws;
      ws.onmessage = onMessage;
      ws.onerror = () => {};
      ws.onclose = () => { if (this._telemetryWs === ws) this._telemetryWs = null; };
    }).catch(() => { /* no token — stay disconnected; the next view change retries */ });
  }

  /** Belt and braces beside the server-side scoping: an event that names another
   *  user is never this user's, whatever channel id it carries. */
  private _isForMe(evt: any): boolean {
    if (!evt || !evt.username) return true;
    const me = JSON.parse(localStorage.getItem('me') || '{}')?.username;
    return !me || evt.username === me;
  }

  private _handleChannelActivityEvent(evt: any): void {
    if (!this._isForMe(evt)) return;
    if (evt.type === 'channel_background_agents') {
      // The agent's doing, not the user's: rows appear when a background agent
      // starts and vanish when it finishes. The bridge re-sends every 30 s
      // while any run, which also refreshes the elapsed labels.
      const list = Array.isArray(evt.agents) ? evt.agents : [];
      const next = { ...this.backgroundAgents };
      if (list.length) next[evt.channel_id] = list; else delete next[evt.channel_id];
      this.backgroundAgents = next;
      this.cdr.markForCheck();
      return;
    }
    if (evt.type === 'channel_streaming_start') {
      this.streamingChannels.add(evt.channel_id);
      this.channelUnreadResponses.delete(evt.channel_id);
      this.cdr.markForCheck();
    } else if (evt.type === 'channel_response_complete') {
      this.streamingChannels.delete(evt.channel_id);
      if (evt.channel_id !== this.selectedChannelId || this.activeView !== 'channel') {
        this.channelUnreadResponses.add(evt.channel_id);
      }
      this.cdr.markForCheck();
    } else if (evt.event === 'heartbeat_state') {
      // Update agent status in sidebar for any state change (sleep/wake/busy)
      const info = this.agents.find(a => a.agent.id === evt.agent_id);
      if (info) {
        info.status = evt.sleeping ? 'sleeping' : 'awake';
        info.presence = evt.presence || (evt.sleeping ? 'idle' : 'online');
        info.presenceDetail = evt.presence_detail || evt.reason || '';
        this.cdr.markForCheck();
      }
    }
  }

  private _disconnectTelemetryWs(): void {
    if (this._telemetryWs) {
      this._telemetryWs.close();
      this._telemetryWs = null;
    }
  }

  // --- Helpers ---

  /** Unescape literal \n from double shell→JSON encoding (e.g. agent curl posts).
   *  Skips content with TOOLJSON markup to avoid corrupting tool call JSON. */
  private static RE_CHROME_LINE = /^(?:─{10,}|❯\s*|.*auto-compact.*|.*bypass permissions.*|.*esc to interrupt.*|\s*[⏵▸].*|\s*)$/;

  private _stripTrailingChrome(content: string): string {
    if (!content) return content;
    const lines = content.split('\n');
    let end = lines.length;
    while (end > 0 && HeartbeatWidgetComponent.RE_CHROME_LINE.test(lines[end - 1])) {
      end--;
    }
    return lines.slice(0, end).join('\n');
  }

  private unescapeContent(content: string): string {
    if (typeof content === 'string' && content.includes('\\n') && !content.includes('\x02TOOLJSON')) {
      return content.replace(/\\n/g, '\n');
    }
    return content;
  }

  private toDisplayMessage(msg: HeartbeatMessage): ChatService.ChatMessage {
    const content = msg.direction === 'in'
      ? this._stripContextEnvelope(this.unescapeContent(msg.content))
      : this.unescapeContent(msg.content);
    const base: any = { isSelf: msg.direction === 'in', timestamp: msg.timestamp, isFinal: true as const, messageId: msg.id, pinned: msg.pinned === true, reactions: msg.reactions };

    // Carry file_path, session_id, timing, and agent attribution from persisted messages
    if (msg.file_path) base.turnPath = msg.file_path;
    if (msg.metadata?.session_id) base.sessionId = msg.metadata.session_id;
    if (msg.metadata?.ttfb_ms) base.durationMs = msg.metadata.ttfb_ms;
    if (msg.metadata?.fcp_ms != null) base.fcpMs = msg.metadata.fcp_ms;
    if (msg.metadata?.model) base.model = msg.metadata.model;
    if (msg.metadata?.input_tokens != null) base.inputTokens = msg.metadata.input_tokens;
    if (msg.metadata?.output_tokens != null) base.outputTokens = msg.metadata.output_tokens;
    if (msg.metadata?.cache_tokens != null) base.cacheTokens = msg.metadata.cache_tokens;
    if (msg.metadata?.transcript_path) base.transcriptPath = msg.metadata.transcript_path;
    if (msg.metadata?.orchestration) base.orchestration = msg.metadata.orchestration;
    else if (msg.metadata?.source?.startsWith('orchestration')) base.orchestration = msg.metadata.source.replace(/_/g, ' ');
    if (msg.from && msg.direction === 'out') {
      const match = this.agents.find(a => a.agent.id === msg.from);
      base.agentName = match?.agent.name ?? msg.from;
    }

    if (msg.direction === 'out') {
      const styled = this.styleSleepMode(content, msg.timestamp);
      const isError = this._isInterruptedContent(styled.content);
      const { content: cleanContent, links } = ChatScreenComponent.extractLinks(styled.content);
      return { ...base, content: cleanContent, isMuted: styled.entirelyMuted, ...(links.length ? { links } : {}), ...(styled.sleepReason ? { sleepReason: styled.sleepReason } : {}), ...(isError ? { isError: true } : {}) };
    }

    // User-sent messages: show sender name (as badge, like agent names) + read receipt
    if (msg.from && msg.direction === 'in') base.agentName = msg.from;
    return { ...base, content, read: msg.read === true ? true : false,
             ...(msg.metadata?.handled_in ? { handledIn: msg.metadata.handled_in, handledAt: msg.metadata.handled_at } : {}) };
  }

  /** Parse SLEEP_MODE directives from content.
   *  Returns: content (with directive stripped), entirelyMuted flag, and sleep reason.
   *  Pure sleep → status divider (entirelyMuted). Mixed → text only, moon shown in header via sleepReason. */
  private styleSleepMode(content: string, _timestamp?: string): { content: string; entirelyMuted: boolean; sleepReason?: string } {
    const match = content.match(/^([\s\S]*?)\s*SLEEP_MODE:\s*(.+)$/);
    if (!match) return { content, entirelyMuted: false };

    const main = match[1].trim();
    const reason = match[2].trim();

    if (!main) {
      // Entire message is just SLEEP_MODE — show as status divider
      return { content: '', entirelyMuted: true, sleepReason: reason };
    }

    // Mixed content — strip directive, moon icon shows in header via sleepReason
    return { content: main, entirelyMuted: false, sleepReason: reason };
  }

  getActiveAgentInfo(): AgentHeartbeatInfo | null {
    const id = this.selectedChannelAgentId || this.primaryAgentId;
    return this.agents.find(a => a.agent.id === id) || null;
  }

  getPresenceColor(info: AgentHeartbeatInfo): string {
    switch (info.presence) {
      case 'online': return '#4caf50';
      case 'busy': return '#f44336';
      case 'idle': return '#ff9800';
      case 'offline':
      default:
        // Fall back to status-based coloring if no presence yet
        return info.status === 'awake' ? '#4caf50'
             : info.status === 'sleeping' ? '#ff9800'
             : '#666';
    }
  }

  getStatusIcon(info: AgentHeartbeatInfo): string {
    switch (info.presence) {
      case 'online': return 'circle';
      case 'busy': return 'circle';
      case 'idle': return 'dark_mode';
      case 'offline':
      default:
        return info.agent.heartbeat_enabled ? 'circle' : 'radio_button_unchecked';
    }
  }

  getMostConstrainingQuota(report: MuxUsageService.UsageReport): MuxUsageService.UsageQuota {
    return report.quotas.reduce((worst, q) => q.used > worst.used ? q : worst, report.quotas[0]);
  }

  formatQuotaPercent(used: number): string {
    return MuxUsageService.formatPercentage(used);
  }

  formatFetchedAgo(report: MuxUsageService.UsageReport): string {
    if (!report?.fetched_at) return '';
    const fetchedMs = new Date(report.fetched_at).getTime();
    const diffMs = Date.now() - fetchedMs;
    if (diffMs < 60_000) return 'just now';
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }

  getUsageBarClass(used: number, report: MuxUsageService.UsageReport): string {
    return MuxUsageService.getUsageClass(used, report);
  }

  getUsageBarTooltip(report: MuxUsageService.UsageReport, info?: AgentHeartbeatInfo): string {
    if (!report?.available) return 'Usage data unavailable';
    const quotas = info ? this.getRelevantQuotas(info, report) : report.quotas;
    const lines: string[] = [];
    for (const quota of quotas) {
      const pct = MuxUsageService.formatPercentage(quota.used);
      const reset = quota.resets_at ? ` (resets in ${MuxUsageService.formatResetTime(quota.resets_at)})` : '';
      lines.push(`${quota.name}: ${pct} used${reset}`);
    }
    const c30 = report.estimated_cost_30d;
    const c7 = report.estimated_cost_7d;
    if (c30 != null || c7 != null) {
      lines.push('');
      const fmt = (v: number) => v >= 1000 ? `$${(v/1000).toFixed(1)}k` : v >= 100 ? `$${Math.round(v)}` : `$${v.toFixed(2)}`;
      if (c30 != null) lines.push(`~${fmt(c30)} API value (30d)`);
      if (c7 != null) lines.push(`~${fmt(c7)} API value (7d)`);
    }
    const ago = this.formatFetchedAgo(report);
    if (ago) lines.push(`Updated ${ago}`);
    return lines.join('\n');
  }

  private static SELF_HOSTED_BACKENDS = new Set(['ollama']);

  getPillUsageClass(info: AgentHeartbeatInfo): string {
    const backend = info.agent.backend ?? 'claude-cli';
    // Self-hosted models always show green — no quota to worry about
    if (HeartbeatWidgetComponent.SELF_HOSTED_BACKENDS.has(backend)) return 'usage-normal';
    const report = this.getAgentUsageReport(info);
    if (!report?.available || !report.quotas.length) return '';
    const quota = report.quotas.reduce((worst, q) => q.used > worst.used ? q : worst, report.quotas[0]);
    return MuxUsageService.getUsageClass(quota.used, report);
  }

  async quickSwitchAgent(agentId: string): Promise<void> {
    if (this.activeView === 'channel') {
      await this.$changeChannelAgent(agentId);
      const backendId = this.currentBackendId;
      this._muxUsage.startPolling(backendId, undefined, agentId);
      const cached = this._muxUsage.getCachedReport(backendId, agentId);
      if (cached) { this.usageCache.set(agentId, cached); this.cdr.markForCheck(); }
    } else {
      this.selectAgent(agentId);
    }
  }

  get quickSwitchActiveId(): string | null {
    return this.activeView === 'channel' ? this.selectedChannelAgentId : this.selectedAgentId;
  }

  /** The agent currently in focus — active DM agent or channel's assigned agent. */
  get activeAgentInfo(): AgentHeartbeatInfo | undefined {
    const id = this.activeView === 'channel' ? this.selectedChannelAgentId : this.selectedAgentId;
    return id ? this.agents.find(a => a.agent.id === id) : undefined;
  }

  // --- Agent tab index (maps between agent ID and tab index) ---

  get dmAgentTabIndex(): number {
    const idx = this.visibleAgents.findIndex(a => a.agent.id === this.selectedAgentId);
    return idx >= 0 ? idx : 0;
  }

  /** True once we've seen a message from another user in this channel. */
  get isSharedChannel(): boolean {
    return !!this.selectedChannelId && this.sharedChannels.has(this.selectedChannelId);
  }

  /** True when this widget stands on a remote place ({device}:{team}) — the
   *  device's own channel agent answers, so the composer is the agent-less
   *  one and the local agent strip stays empty. */
  get isRemotePlace(): boolean {
    return !!parsePlaceAddress(this.teamName);
  }

  get channelAgentTabIndex(): number {
    const id = this.selectedChannelAgentId || this.primaryAgentId;
    if (!id) return 0;
    const idx = this.visibleAgents.findIndex(a => a.agent.id === id);
    if (!this.isSharedChannel && !this.isRemotePlace) return idx >= 0 ? idx : 0;
    return idx >= 0 ? idx + 1 : 0; // +1 for the "no agent" tab at index 0
  }

  onDmAgentTabChange(index: number): void {
    const agent = this.visibleAgents[index];
    if (agent) {
      this.selectAgent(agent.agent.id);
    }
  }

  /** "To:" addressee select (2026-09-10, replaces the agent tabs): map the chosen
   *  agent id back onto the hidden tab group's index so every existing
   *  tab-change side effect (agent binding, usage polling, drafts) still runs. */
  /** Agents hidden from the addressee list (the old star/hide state). */
  get hasHiddenAgents(): boolean {
    return this.orderedAgents.length > this.visibleAgents.length;
  }

  /** Footer rows of the "To:" list are actions, not addressees. Returns true when handled. */
  private handleAddresseeAction(value: string): boolean {
    if (value === '__new__') { this.onOpenAgentSettings.emit({ agentId: null }); return true; }
    if (value === '__show_all__') {
      this._agentHidden.clear();
      this._saveAgentVisibility();
      this.cdr.markForCheck();
      return true;
    }
    return false;
  }

  onDmAddresseeChange(agentId: string): void {
    if (this.handleAddresseeAction(agentId)) return;
    this.selectAgent(agentId);
  }

  async onChannelAddresseeChange(agentId: string): Promise<void> {
    if (this.handleAddresseeAction(agentId)) return;
    const offset = (this.isSharedChannel || this.isRemotePlace) ? 1 : 0;
    if (!agentId) { await this.onChannelAgentTabChange(0); return; }
    const idx = this.visibleAgents.findIndex(a => a.agent.id === agentId);
    if (idx >= 0) await this.onChannelAgentTabChange(idx + offset);
  }

  async onChannelAgentTabChange(index: number): Promise<void> {
    if ((this.isSharedChannel || this.isRemotePlace) && index === 0) {
      // "No agent" tab — clear the channel agent assignment
      this.selectedChannelAgentId = '';
      this.cdr.markForCheck();
      return;
    }
    const agentIndex = (this.isSharedChannel || this.isRemotePlace) ? index - 1 : index;
    const agent = this.visibleAgents[agentIndex];
    if (agent) {
      await this.$changeChannelAgent(agent.agent.id);
      if (this.isRemotePlace) return; // usage polling is local-only
      const backendId = this.currentBackendId;
      this._muxUsage.startPolling(backendId, undefined, agent.agent.id);
      const cached = this._muxUsage.getCachedReport(backendId, agent.agent.id);
      if (cached) { this.usageCache.set(agent.agent.id, cached); this.cdr.markForCheck(); }
    }
  }

  /** Submit from a specific agent's DM tab */
  async onDmSubmitForAgent(event: ChatInputSubmitEvent, agentId: string): Promise<void> {
    // Ensure the correct agent is selected before delegating
    if (this.selectedAgentId !== agentId) {
      await this.selectAgent(agentId);
    }
    this.onSubmit(event);
  }

  /** Submit from a specific agent's channel tab */
  async onChannelSubmitForAgent(event: ChatInputSubmitEvent, agentId: string): Promise<void> {
    // Ensure the correct agent is assigned to the channel before sending
    if (this.selectedChannelAgentId !== agentId) {
      await this.$changeChannelAgent(agentId);
    }
    this.onChannelSubmit(event);
  }

  /** Submit from the "no agent" tab — human-only, no AI dispatch */
  onChannelSubmitNoAgent(event: ChatInputSubmitEvent): void {
    // Clear agent targeting so the message goes out without dispatch
    this.selectedChannelAgentId = '';
    this.onChannelSubmit(event);
  }

  /** Open DM for a specific agent (used by per-tab controls) */
  async openDmForAgent(agentId: string): Promise<void> {
    await this.selectAgent(agentId);
  }

  /** Open settings for a specific agent (used by per-tab controls) */
  openAgentSettingsFor(agentId: string): void {
    console.log("openAgentSettingsFor", agentId)
    this.onOpenAgentSettings.emit({ agentId });
  }

  /** Usage report for a specific agent. The service cache is written by every
   *  poll (5 min) and by the after-send refresh; the widget map only holds the
   *  page-load prefetch, so it is the fallback, never the first choice
   *  (2026-09-12: the pill showed "Updated 20h ago" on a tab left open). */
  getAgentUsageReport(info: AgentHeartbeatInfo): MuxUsageService.UsageReport | null {
    const backend = info.agent.backend ?? 'claude-cli';
    return this._muxUsage.getCachedReport(backend, info.agent.id)
      ?? this.usageCache.get(info.agent.id)
      ?? null;
  }

  /**
   * Filter quotas to only those relevant to the agent's active model.
   *
   * Claude: "Session" and "Weekly" always shown; a model-scoped quota ("Fable",
   * "Opus", "Sonnet") only if the agent's model contains that name.
   * Gemini: only the quota whose name matches the agent's model (e.g. "gemini-2.5-flash").
   * ChatGPT: show all (rate-limit windows are plan-wide, not model-specific).
   * Fallback: show all quotas.
   */
  getRelevantQuotas(info: AgentHeartbeatInfo, report: MuxUsageService.UsageReport): MuxUsageService.UsageQuota[] {
    if (!report?.quotas?.length) return [];
    const model = (this.effectiveModel(info) || info.agent.model || '').toLowerCase();
    const backend = info.agent.backend ?? 'claude-cli';

    if (backend === 'claude-cli' || backend === 'claude' || backend === 'claude-interactive') {
      // Session + Weekly are plan-wide; anything else is a model-scoped weekly
      // limit, shown only when this agent runs that model.
      return report.quotas.filter(q => {
        const name = q.name.toLowerCase();
        if (name === 'session' || name === 'weekly') return true;
        return model.includes(name);
      });
    }

    if (backend === 'gemini') {
      // Match quota to agent model via exact or variant-suffix match.
      // If no quota exists for this model (e.g. Pro on free tier), show nothing
      // rather than falling back to unrelated quotas.
      if (!model) return report.quotas;
      const match = report.quotas.filter(q => {
        const name = q.name.toLowerCase();
        if (name === model) return true;
        // Allow variant suffixes like -preview, -exp on either side
        if (name.startsWith(model + '-') || model.startsWith(name + '-')) {
          const [longer, shorter] = name.length > model.length ? [name, model] : [model, name];
          const suffix = longer.slice(shorter.length);
          return /^-(preview|exp)$/.test(suffix);
        }
        return false;
      });
      return match;
    }

    return report.quotas;
  }

  /** Get available models for an agent's backend */
  readonly effortLevels = [
    { value: '', label: 'Auto (default)' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'max', label: 'Max' },
  ];

  /** Ollama agents: thinking is on or off. Off is stored as effort "low", which the backend sends as think=false. */
  readonly thinkingLevels = [
    { value: '', label: 'On (model default)' },
    { value: 'low', label: 'Off' },
  ];

  getEffortLabel(effort: string, backend?: string): string {
    if (backend === 'ollama') return effort === 'low' ? 'No thinking' : '';
    const found = this.effortLevels.find(e => e.value === effort);
    return found ? found.label : effort;
  }

  async onModelOrEffortChange(info: AgentHeartbeatInfo, value: string): Promise<void> {
    if (value.startsWith('effort:')) {
      const effort = value.substring(7);
      await this.onEffortChange(info, effort);
      // Force mat-select to re-sync back to the model value
      const model = info.agent.model;
      info.agent.model = '';
      this.cdr.detectChanges();
      info.agent.model = model;
      this.cdr.detectChanges();
      return;
    }
    let entry: ModelPoolService.PoolEntry | null = null;
    for (const g of this.poolGroups) {
      const e = g.models.find(x => x.key === value);
      if (e) { entry = e; break; }
    }
    if (!entry) { await this.onModelChange(info, value); return; }
    const own = info.agent.backend ?? 'claude-cli';
    const twin = (entry.backend === 'claude-cli' || entry.backend === 'claude-interactive') &&
                 (own === 'claude-cli' || own === 'claude-interactive');
    // Own backend keeps the plain-model override semantics; cross-provider
    // picks store the "backend:model" wire format the dispatcher resolves.
    await this.onModelChange(info, (entry.backend === own || twin) ? entry.model : entry.overrideValue);
  }

  async onEffortChange(info: AgentHeartbeatInfo, effort: string): Promise<void> {
    const newEffort = effort || null;
    if ((info.agent.effort || null) === newEffort) return;
    try {
      await this.agentService.updateAgent(info.agent.id, {
        ...info.agent,
        effort: newEffort as any,
      });
      info.agent.effort = newEffort ?? undefined;
      if (info.agent.backend === 'ollama') {
        this._snackBar.open(newEffort === 'low' ? 'Thinking off' : 'Thinking on', '', { duration: 2000 });
      } else {
        const label = newEffort ? this.getEffortLabel(newEffort) : 'Auto';
        this._snackBar.open(`Effort set to ${label}`, '', { duration: 2000 });
      }
      this.cdr.markForCheck();
    } catch (e) {
      console.error('Failed to update effort level:', e);
      this._snackBar.open('Failed to change effort level', 'OK', { duration: 3000 });
    }
  }

  getModelsForAgent(info: AgentHeartbeatInfo): { name: string; display_name: string }[] {
    const backend = info.agent.backend ?? 'claude-cli';
    const cacheKey = `${backend}|${info.agent.credentials_id || ''}`;
    const cached = this._ownModelsCache.get(cacheKey);
    if (cached) return cached;
    let list = this.backendModels.get(backend) || [];
    // Restrict to what the agent's bound credential (vendor, mode) supports — same
    // constraint the Agents lens applies — so invalid picks (e.g. gpt-5.4 on a ChatGPT
    // Team login) aren't offered here either.
    const cred = this.credentials.find(c => (c.id || '') === (info.agent.credentials_id || ''));
    const allow = cred ? this.modelConstraints[`${cred.vendor}:${cred.mode}`] : undefined;
    if (allow) list = list.filter(m => allow.includes(m.name));
    this._ownModelsCache.set(cacheKey, list);
    return list;
  }

  /** Get display name for current model, using API display_name when available */
  getModelDisplayName(model: string): string {
    if (!model) return '?';
    // Pool tuple first: the trigger reads "Model · credential" when the
    // override matches a pool entry (the tuple grammar).
    for (const g of this.poolGroups) {
      const e = g.models.find(x => x.overrideValue === model);
      if (e) return `${e.displayName} · ${e.credentialName ?? e.provider}`;
    }
    // Cross-provider override values are "backend:model" — show the model's
    // display name with a provider tag. The prefix only counts as a backend
    // when we know it (ollama model names contain colons: "qwen3.5:9b").
    if (model.includes(':')) {
      const idx = model.indexOf(':');
      const prefix = model.slice(0, idx);
      if (this.backendModels.has(prefix)) {
        const rest = model.slice(idx + 1);
        const found = (this.backendModels.get(prefix) || []).find(m => m.name === rest);
        return `${found?.display_name ?? rest} · ${this.providerLabel(prefix)}`;
      }
    }
    // Search all backends for a matching display_name
    for (const models of this.backendModels.values()) {
      const found = models.find(m => m.name === model);
      if (found) return found.display_name;
    }
    // Fallback: just return the raw model id
    return model;
  }

  /** Friendly provider label for a backend id. */
  providerLabel(backendId: string): string {
    const map: Record<string, string> = {
      'claude-cli': 'Claude', 'claude-interactive': 'Claude',
      'claude': 'Claude API', 'codex': 'Codex', 'chatgpt': 'ChatGPT',
      'gemini-cli': 'Gemini', 'gemini': 'Gemini API',
      'antigravity': 'Antigravity', 'ollama': 'Ollama (local)',
    };
    return map[backendId] || backendId;
  }

  /** Pool-backed picker groups — provider-labeled groups whose ROWS carry the
   *  credential as a muted suffix after the model name (the (model, credential)
   *  tuple grammar, 2026-08-13 design session). Sourced from ModelPoolService;
   *  the agent's own (backend, credential) rows are excluded — the top group
   *  covers those. MEMOIZED per agent key: template-bound fresh arrays froze
   *  the app once (see hard-won rules). */
  poolGroups: ModelPoolService.PoolGroup[] = [];
  private readonly _noProviderGroups: { provider: string; models: ModelPoolService.PoolEntry[] }[] = [];
  private _providerGroupsCache = new Map<string, { provider: string; models: ModelPoolService.PoolEntry[] }[]>();

  // Type-to-filter for the pool panel. Event-driven: caches are cleared on
  // input so the memoized builders recompute once — template identities stay
  // stable between keystrokes (the *ngFor fresh-array rule).
  poolFilter = '';
  private _ownModelsCache = new Map<string, { name: string; display_name: string }[]>();

  private _poolFilterTimer: any = null;

  onPoolFilter(value: string): void {
    this.poolFilter = value;
    clearTimeout(this._poolFilterTimer);
    this._poolFilterTimer = setTimeout(() => {
      this._providerGroupsCache.clear();
      this._ownModelsCache.clear();
      // Overlay-embedded view: force a synchronous pass so the open panel
      // re-renders while the user types.
      this.cdr.detectChanges();
    }, 120);
  }

  onPoolFilterKeydown(e: KeyboardEvent): void {
    // The filter input owns printable keys (mat-select typeahead would steal
    // them). Escape with text = clear the filter, keep the panel open;
    // Escape with an empty box falls through and closes the panel.
    if (e.key === 'Escape') {
      if (this.poolFilter) {
        e.stopPropagation();
        e.preventDefault();
        clearTimeout(this._poolFilterTimer);
        this.poolFilter = '';
        this._providerGroupsCache.clear();
        this._ownModelsCache.clear();
        this.cdr.detectChanges();
      }
      return;
    }
    e.stopPropagation();
  }

  onPoolPanelToggle(open: boolean): void {
    if (open) {
      // Type-to-filter should just work: focus the filter box once the CDK
      // overlay has rendered (only one panel is ever open).
      setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>('.model-select-panel .pool-filter input');
        el?.focus();
      });
    } else if (this.poolFilter) {
      this.onPoolFilter('');
    }
  }

  private _poolMatch(...parts: (string | null | undefined)[]): boolean {
    const q = this.poolFilter.trim().toLowerCase();
    if (!q) return true;
    return parts.some(t => (t || '').toLowerCase().includes(q));
  }

  otherProviderGroups(info: AgentHeartbeatInfo): { provider: string; models: ModelPoolService.PoolEntry[] }[] {
    if (!this._inChannelContext) return this._noProviderGroups;
    // ONE grammar: the full pool, own credential included — the panel no
    // longer has a separate bare own-backend group.
    const cacheKey = `all|${this.poolFilter}`;
    const cached = this._providerGroupsCache.get(cacheKey);
    if (cached) return cached;
    const groups: { provider: string; models: ModelPoolService.PoolEntry[] }[] = [];
    for (const g of this.poolGroups) {
      const models = g.models.filter(e =>
        this._poolMatch(e.displayName, e.credentialName, e.provider));
      if (models.length) groups.push({ provider: g.provider, models });
    }
    const result = groups.length ? groups : this._noProviderGroups;
    this._providerGroupsCache.set(cacheKey, result);
    return result;
  }

  /** The pool key for the agent's effective model — prefers the tuple bound to
   *  the agent's own credential, falls back to the first (backend, model) hit. */
  poolKeyFor(info: AgentHeartbeatInfo): string {
    const v = this.effectiveModel(info) || '';
    let backend = info.agent.backend ?? 'claude-cli';
    let model = v;
    const idx = v.indexOf(':');
    if (idx > 0 && this.backendModels.has(v.slice(0, idx))) {
      backend = v.slice(0, idx);
      model = v.slice(idx + 1);
    }
    const ownCred = info.agent.credentials_id || '';
    let fallback = '';
    for (const g of this.poolGroups) {
      for (const e of g.models) {
        const twin = (e.backend === 'claude-cli' || e.backend === 'claude-interactive') &&
                     (backend === 'claude-cli' || backend === 'claude-interactive');
        if ((e.backend === backend || twin) && e.model === model) {
          if ((e.credentialId || '') === ownCred) return e.key;
          if (!fallback) fallback = e.key;
        }
      }
    }
    return fallback;
  }

  private async _loadModelPool(): Promise<void> {
    try {
      // The pool is team-scoped: ask for THIS workspace's team, not the team the URL
      // selected (2026-10-26: a workspace on team everyone showed no models because
      // the pool was fetched for the URL's team, which had no credentials).
      this.poolGroups = await this.poolService.groups(true, this.teamName || undefined);
      this._providerGroupsCache.clear();
        this._ownModelsCache.clear();
      this.cdr.markForCheck();
      await this._reconcilePolicySelections();
    } catch (e) {
      console.warn('model pool load failed:', e);
    }
  }

  /** Channel selections the team policy no longer allows are switched, not left
   *  to be refused at dispatch: if an agent's effective (model, credential) for
   *  this channel has no entry in the policy-filtered pool, pick the closest
   *  allowed entry (same backend + own credential → same backend → anything)
   *  and set it as this channel's override, saying why. Only in a channel, only
   *  when a policy exists, once per (channel, agent) per load. */
  private _policySwitched = new Set<string>();
  private async _reconcilePolicySelections(): Promise<void> {
    if (!this._inChannelContext || !this.poolService.lastPolicy?.exists || !this.poolGroups.length) return;
    const channelId = this.selectedChannelId!;
    // Decide only from this channel's STORED overrides. Until selectChannel's
    // load has run for this channel there is nothing to decide from (it calls
    // back here when done); and every agent in the roster needs its own
    // override fetched, not just the channel's agent — otherwise an agent whose
    // override was set on a previous visit looks like it still runs its default
    // and gets "switched" again on every page load (2026-10-16).
    if (!this._overridesFor || this._overridesFor.channelId !== channelId) return;
    await this._fetchChannelOverrides(channelId, this.visibleAgents.map(i => i.agent.id));
    if (this.selectedChannelId !== channelId) return;
    for (const info of this.visibleAgents) {
      const mark = `${channelId}|${info.agent.id}`;
      if (this._policySwitched.has(mark) || this.poolKeyFor(info)) continue;
      const own = info.agent.backend ?? 'claude-cli';
      const ownCred = info.agent.credentials_id || '';
      const isTwin = (b: string) => (b === 'claude-cli' || b === 'claude-interactive') && (own === 'claude-cli' || own === 'claude-interactive');
      const all = this.poolGroups.flatMap(g => g.models);
      // An agent bound to a credential the policy doesn't let this user bill
      // (no pool entry carries it) can't dispatch here whatever the model —
      // leave it for the roster to hide rather than "fixing" its model.
      if (ownCred && !all.some(e => (e.credentialId || '') === ownCred)) continue;
      // Same provider only: an agent whose provider the policy switched off
      // can't dispatch at all — that's the roster's problem (hide it), not a
      // model switch. Never "switch" a Gemini agent to Claude.
      const entry =
        all.find(e => (e.backend === own || isTwin(e.backend)) && (e.credentialId || '') === ownCred) ||
        all.find(e => e.backend === own || isTwin(e.backend));
      if (!entry) continue;
      this._policySwitched.add(mark);
      const before = this.getModelDisplayName(this.effectiveModel(info));
      const value = (entry.backend === own || isTwin(entry.backend)) ? entry.model : entry.overrideValue;
      try {
        await this.sessionService.setChannelModelOverride(channelId, info.agent.id, value === info.agent.model ? '' : value, this.teamName);
        if (value === info.agent.model) delete this.channelModelOverrides[info.agent.id];
        else this.channelModelOverrides[info.agent.id] = value;
        this._snackBar.open(
          `Team policy: ${before} isn't available to you in ${this.teamName} — switched ${info.agent.name || info.agent.id} to ${entry.displayName} for this channel`,
          '', { duration: 6000 });
        this.cdr.markForCheck();
      } catch (e) {
        console.warn('policy reconcile: could not switch model', e);
      }
    }
  }

  /** Change agent model via API */
  /** The model actually in effect for an agent in the current context: a channel override
   *  when a channel is open, otherwise the agent's default. Drives the selector's value. */
  /** Header fields for a bubble that just finished streaming. The buffer-replay path's
   *  stream_end carries no model and the live bubble never had an agent name, so until a
   *  reload re-read them from the stored message the header showed only a timestamp. The
   *  agent is the channel's; the model is the event's, else the one a budget fallback note
   *  names in the reply, else the agent's effective model. */
  private _liveHeaderFill(content: string, data: any): { agentName?: string; model?: string } {
    const out: { agentName?: string; model?: string } = {};
    const agentId = this.selectedChannelAgentId || this.primaryAgentId;
    const info = agentId ? this.agents.find(a => a.agent.id === agentId) : undefined;
    const name = this.selectedChannelAgentName || info?.agent.name;
    if (name) out.agentName = name;
    if (!data?.model) {
      const noted = (content || '').match(/this reply ran on `([^`]+)`/);
      const model = noted?.[1] || (info ? this.effectiveModel(info) : '');
      if (model) out.model = model;
    }
    return out;
  }

  effectiveModel(info: AgentHeartbeatInfo): string {
    const inChannel = this.activeView === 'channel' && !!this.selectedChannelId;
    return (inChannel && this.channelModelOverrides[info.agent.id]) || info.agent.model;
  }

  private get _inChannelContext(): boolean {
    return this.activeView === 'channel' && !!this.selectedChannelId;
  }

  async onModelChange(info: AgentHeartbeatInfo, newModel: string): Promise<void> {
    if (newModel === this.effectiveModel(info)) return;
    // In a channel: set a per-(channel, agent) override (persists for THIS channel only).
    // Picking the agent's default clears the override. Outside a channel: change the
    // agent's default model (all channels without an override follow it).
    if (this._inChannelContext) {
      const channelId = this.selectedChannelId!;
      const clearing = newModel === info.agent.model;
      try {
        await this.sessionService.setChannelModelOverride(channelId, info.agent.id, clearing ? '' : newModel, this.teamName);
        if (clearing) delete this.channelModelOverrides[info.agent.id];
        else this.channelModelOverrides[info.agent.id] = newModel;
        const label = this.getModelDisplayName(newModel);
        this._snackBar.open(
          clearing ? `Reverted to the agent default (${label}) for this channel`
                   : `Using ${label} in this channel — other channels are unaffected`,
          '', { duration: 3000 });
        this.cdr.markForCheck();
      } catch (e) {
        console.error('Failed to set channel model override:', e);
        this._snackBar.open('Failed to change model for this channel', 'OK', { duration: 3000 });
      }
      return;
    }
    try {
      await this.agentService.updateAgent(info.agent.id, {
        ...info.agent,
        model: newModel,
      });
      info.agent.model = newModel;
      this.cdr.markForCheck();
    } catch (e) {
      console.error('Failed to update agent model:', e);
      this._snackBar.open('Failed to change model', 'OK', { duration: 3000 });
    }
  }

  /** Which channel the overrides map belongs to and which agents' entries have
   *  been fetched for it. The GET route answers one agent at a time. */
  private _overridesFor: { channelId: string; agents: Set<string> } | null = null;

  /** Load this channel's model overrides (the channel's agent plus every agent
   *  in the roster) so the selector shows the effective model. */
  private async loadChannelModelOverrides(channelId: string, agentId: string | null): Promise<void> {
    void this.loadChannelAffinity(channelId);
    this.channelModelOverrides = {};
    this._overridesFor = { channelId, agents: new Set() };
    const ids = new Set<string>();
    if (agentId) ids.add(agentId);
    for (const info of this.visibleAgents) ids.add(info.agent.id);
    await this._fetchChannelOverrides(channelId, [...ids]);
    this.cdr.markForCheck();
    await this._reconcilePolicySelections();
  }

  /** Load this channel's host affinity and the node's host list (with liveness). */
  private async loadChannelAffinity(channelId: string): Promise<void> {
    this._affinityFor = channelId;
    this.channelAffinity = [];
    const [aff, hosts] = await Promise.all([
      this.sessionService.getChannelAffinity(channelId, this.teamName),
      this.sessionService.listAffinityHosts(this.teamName),
    ]);
    if (this._affinityFor !== channelId) return;
    this.channelAffinity = aff.hosts;
    this.affinityHome = aff.home || hosts.home || '';
    this.affinityHosts = hosts.hosts || [];
    this.affinityPolicy = hosts.policy || { mode: 'user', linking: 'anyone', default: 'home' };
    this.cdr.markForCheck();
  }

  /** The chip's text: the first host and its state (awake, or asleep for how long), or the home. */
  affinityLabel(): string {
    const first = this.channelAffinity[0];
    if (!first) return this.affinityHome || 'home';
    const h = this.affinityHosts.find(x => x.name === first);
    if (!h || h.alive) return first;
    const since = this.hostAsleepFor(h);
    return since ? `${first} · asleep ${since}` : `${first} (offline)`;
  }

  /** "2h" / "3d" since a hands computer's pipe last closed (its last hello when no close
   *  was seen: the home restarted, or the client is older than today's). */
  hostAsleepFor(h: MuxSessionService.AffinityHost): string {
    const at = h.last_closed || h.last_hello;
    if (!at) return '';
    const s = Math.max(0, (Date.now() - new Date(at).getTime()) / 1000);
    if (s < 90) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
  }

  /** The option's tooltip: when the computer woke or fell asleep, in local time. */
  hostStatus(h: MuxSessionService.AffinityHost): string {
    const fmt = (iso: string) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    if (h.alive) return h.last_hello ? `awake since ${fmt(h.last_hello)}` : 'reachable';
    const at = h.last_closed || h.last_hello;
    return at ? `asleep since ${fmt(at)}` : 'offline';
  }

  /** One pick, like the model selector: a host, or the home. The home is also the
   *  fallback whenever the chosen host is offline (the agent says so in its reply). */
  async onAffinityChange(value: string): Promise<void> {
    if (!this.selectedChannelId) return;
    const next = value && value !== '__home__' ? [value] : [];
    try {
      const saved = await this.sessionService.setChannelAffinity(this.selectedChannelId, next, this.teamName);
      this.channelAffinity = saved.hosts;
      this._snackBar.open(saved.hosts.length
        ? `This channel now runs on ${saved.hosts[0]} (home ${this.affinityHome || 'here'} if it's offline). Takes effect on the next message.`
        : `This channel now runs at the home (${this.affinityHome || 'this server'}).`, '', { duration: 4000 });
    } catch (e: any) {
      this._snackBar.open(e?.error?.detail || 'Could not save the host affinity.', 'OK', { duration: 6000 });
    }
    this.cdr.markForCheck();
  }

  /** Fetch the stored override for each agent not yet fetched for this channel.
   *  Results are dropped if the channel changed while the requests were out. */
  private async _fetchChannelOverrides(channelId: string, agentIds: string[]): Promise<void> {
    const loaded = this._overridesFor;
    if (!loaded || loaded.channelId !== channelId) return;
    const todo = agentIds.filter(id => !loaded.agents.has(id));
    if (!todo.length) return;
    const results = await Promise.all(todo.map(async id =>
      [id, await this.sessionService.getChannelModelOverride(channelId, id, this.teamName)] as const));
    if (this._overridesFor !== loaded || this.selectedChannelId !== channelId) return;
    for (const [id, model] of results) {
      loaded.agents.add(id);
      if (model) this.channelModelOverrides[id] = model;
      else delete this.channelModelOverrides[id];
    }
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
    localStorage.setItem('lit-heartbeat-sidebar-open', String(this.sidebarOpen));
    this.cdr.markForCheck();
  }

  openAgentSettings(): void {
    this.onOpenAgentSettings.emit({ agentId: this.activeAgentInfo?.agent.id ?? this.selectedAgentId });
  }

  async openActiveAgentDm(): Promise<void> {
    const agentId = this.activeAgentInfo?.agent.id;
    if (!agentId || this.activeView === 'dm') return;
    await this.selectAgent(agentId);
  }

  /** Drag to resize sidebar — mirrors chat-widget pattern */
  private _sidebarDragging = false;

  onSidebarDragStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this._sidebarDragging = true;

    const container = (event.target as HTMLElement).closest('.heartbeat-container') as HTMLElement;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();

    // Disable pointer events on iframes during drag
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach(iframe => (iframe.style.pointerEvents = 'none'));

    const onMouseMove = (e: MouseEvent) => {
      if (!this._sidebarDragging) return;
      const mouseX = e.clientX - containerRect.left;
      // Snap closed when dragged below threshold
      if (mouseX < 80) {
        this.sidebarOpen = false;
        cleanup();
        this.cdr.detectChanges();
        return;
      }
      this.sidebarWidth = Math.max(150, Math.min(400, mouseX));
      this.cdr.detectChanges();
    };

    const cleanup = () => {
      this._sidebarDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', cleanup);
      window.removeEventListener('blur', cleanup);
      iframes.forEach(iframe => (iframe.style.pointerEvents = ''));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', cleanup);
    window.addEventListener('blur', cleanup);
  }

  /** Drag to resize pins pane — percentage-based */
  private _pinsPaneDragging = false;

  onPinsPaneDragStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this._pinsPaneDragging = true;

    const container = (event.target as HTMLElement).closest('.heartbeat-container') as HTMLElement;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();

    const iframes = document.querySelectorAll('iframe');
    iframes.forEach(iframe => (iframe.style.pointerEvents = 'none'));

    const onMouseMove = (e: MouseEvent) => {
      if (!this._pinsPaneDragging) return;
      const mouseX = e.clientX - containerRect.left;
      const pct = ((containerRect.width - mouseX) / containerRect.width) * 100;
      this.pinsPanePercent = pct;
      this.cdr.detectChanges();
    };

    const cleanup = () => {
      this._pinsPaneDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', cleanup);
      window.removeEventListener('blur', cleanup);
      iframes.forEach(iframe => (iframe.style.pointerEvents = ''));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', cleanup);
    window.addEventListener('blur', cleanup);
  }

  /** Drag to resize input area height — vertical version of sidebar resize */
  private _inputDragging = false;

  onInputDragStart(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this._inputDragging = true;

    const mainArea = (event.target as HTMLElement).closest('.main-area') as HTMLElement;
    if (!mainArea) return;

    // Fixed minimum: tabs(32) + controls(36) + input(56) + margin(5) ≈ 130px
    const minHeight = 130;

    const iframes = document.querySelectorAll('iframe');
    iframes.forEach(iframe => (iframe.style.pointerEvents = 'none'));

    const onMouseMove = (e: MouseEvent) => {
      if (!this._inputDragging) return;
      const mainRect = mainArea.getBoundingClientRect();
      const inputHeight = mainRect.bottom - e.clientY;
      this.inputAreaHeight = Math.max(minHeight, Math.min(mainRect.height * 0.7, inputHeight));

      // Clear CDK autosize inline height so flex layout controls the textarea size
      const textareas = mainArea.querySelectorAll('.input-area textarea');
      textareas.forEach((ta: Element) => (ta as HTMLElement).style.height = '');

      this.cdr.detectChanges();
    };

    const cleanup = () => {
      this._inputDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', cleanup);
      window.removeEventListener('blur', cleanup);
      iframes.forEach(iframe => (iframe.style.pointerEvents = ''));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', cleanup);
    window.addEventListener('blur', cleanup);
  }

  // --- Channel navigation ---

  toggleSection(section: string): void {
    if (this.expandedSections.has(section)) {
      this.expandedSections.delete(section);
    } else {
      this.expandedSections.add(section);
    }
    this.cdr.markForCheck();
  }

  // ── Conversations with other people's agents (standing orders) ──────────
  // An entry here is this user's window onto a channel that lives in the
  // host's store; its id is `trust~host~agent`. It opens like any channel and
  // the ordinary channel routes serve it, but everything that manages an agent
  // (model, sign-in, terminal, orchestrations) is the host's and stays hidden.
  guestChannels: MuxSessionService.NavChannel[] = [];
  guestDraft = '';
  private _openChannelSub?: Subscription;

  static isGuestChannelId(id: string | null | undefined): boolean {
    return !!id && id.startsWith('trust~');
  }

  /** The guest conversation on screen, if that is what the selected channel is. */
  get guestChannel(): MuxSessionService.NavChannel | null {
    if (this.activeView !== 'channel' || !HeartbeatWidgetComponent.isGuestChannelId(this.selectedChannelId)) return null;
    return this.guestChannels.find(c => c.id === this.selectedChannelId) || null;
  }

  get guestOpen(): boolean {
    return this.activeView === 'channel' && HeartbeatWidgetComponent.isGuestChannelId(this.selectedChannelId);
  }

  /** Show one channel by id: a guest conversation an app just opened, or any
   *  channel of this team. Reloads the list once if the id is not in it yet. */
  private async _openChannelById(channelId: string): Promise<void> {
    const find = () => this.guestChannels.find(c => c.id === channelId) || this.personalChannels.find(c => c.id === channelId);
    let channel = find();
    if (!channel) {
      const nav = await this.sessionService.loadNavigation(this.teamName);
      this.personalChannels = nav.personal_channels || [];
      this.guestChannels = nav.guest_channels || [];
      channel = find();
    }
    if (channel) await this.selectChannel(channel);
  }

  onGuestKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.sendGuestMessage();
    }
  }

  /** Send to someone else's agent. The server refuses with a sentence when the
   *  host has paused or ended the conversation; show that sentence. */
  async sendGuestMessage(): Promise<void> {
    const channelId = this.selectedChannelId;
    const content = this.guestDraft.trim();
    if (!channelId || !content || this.channelSending) return;
    this.channelSending = true;
    this.guestDraft = '';
    this.channelMessages = [...this.channelMessages, { isSelf: true, content, timestamp: new Date().toISOString() }];
    this.cdr.markForCheck();
    this.chatScreen?.scrollToBottom();
    try {
      const resp = await lastValueFrom(this.http.post<{ id?: string }>(
        `${ConfigService.muxUrl}/channels/${encodeURIComponent(channelId)}/messages`,
        { content, team: this.teamName || 'local', source: 'channel-ui' }));
      const last = this.channelMessages[this.channelMessages.length - 1];
      if (last?.isSelf && resp?.id) last.messageId = resp.id;
      if (this.channelStreamingIndex === null && this.selectedChannelId === channelId) {
        this.channelLoading = true;
        this.channelLoadingState = 'connecting';
        this.channelMessages = [
          ...this.channelMessages,
          { isSelf: false, content: '', timestamp: new Date().toISOString(), isStreaming: true },
        ];
        this.channelStreamingIndex = this.channelMessages.length - 1;
        clearTimeout(this._thinkingTimeout);
        this._thinkingTimeout = setTimeout(() => {
          this.channelLoading = false;
          this.channelLoadingState = '';
          if (this.channelStreamingIndex !== null) {
            this.channelMessages = this.channelMessages.filter((_, i) => i !== this.channelStreamingIndex);
            this.channelStreamingIndex = null;
          }
          this.cdr.markForCheck();
        }, 180000);   // another person's agent may be starting a session from cold
        this.chatScreen?.scrollToBottom();
      }
    } catch (err: any) {
      const last = this.channelMessages[this.channelMessages.length - 1];
      if (last?.isSelf) last.isError = true;
      const detail = err?.error?.detail || 'Message failed to send. Check your connection and try again.';
      this._snackBar.open(typeof detail === 'string' ? detail : 'Message failed to send.', 'OK', { duration: 8000 });
      if (err?.status === 423 || err?.status === 403) void this.loadNavTree();   // paused or ended: refresh the list's state
    } finally {
      this.channelSending = false;
      this.cdr.markForCheck();
    }
  }

  async loadNavTree(): Promise<void> {
    const nav = await this.sessionService.loadNavigation(this.teamName);
    this.personalChannels = nav.personal_channels || [];
    this.guestChannels = nav.guest_channels || [];
    this.cdr.markForCheck();
    const requested = this._widget.takePendingChannel(this.teamName || 'local');
    if (requested) void this._openChannelById(requested);
    // First paint of the background-agent rows; telemetry keeps them current.
    this.sessionService.getBackgroundAgents().then((byChannel) => {
      this.backgroundAgents = byChannel;
      this.cdr.markForCheck();
    });
  }

  getBackgroundAgents(channel: MuxSessionService.NavChannel): MuxSessionService.BackgroundAgent[] {
    return this.backgroundAgents[channel.id] || [];
  }

  /** "52m", "1h 2m" — how long this run has been going. */
  backgroundAgentElapsed(a: MuxSessionService.BackgroundAgent): string {
    const mins = Math.max(0, Math.floor((Date.now() - a.started_ms) / 60000));
    if (mins < 1) return 'now';
    return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
  }

  backgroundAgentTooltip(a: MuxSessionService.BackgroundAgent): string {
    const owner = a.agent_id ? ` · started by ${a.agent_id}` : '';
    return `Background agent (${a.type})${owner} · running ${this.backgroundAgentElapsed(a)}`;
  }

  /** The stream view is showing: its channel is still the one on screen. */
  get bgAgentOpen(): boolean {
    return !!this.bgAgentView && this.activeView === 'channel'
      && this.selectedChannelId === this.bgAgentView.channelId;
  }

  /** Step into a background agent, as ↓ + Enter does in the terminal: a
   *  read-only view of what it is doing, laid over the channel it belongs to.
   *  The channel underneath keeps its stream, composer and draft. */
  async openBackgroundAgent(a: MuxSessionService.BackgroundAgent, channel: MuxSessionService.NavChannel): Promise<void> {
    if (this.activeView !== 'channel' || this.selectedChannelId !== channel.id) {
      await this.selectChannel(channel);
    }
    this.closeBackgroundAgent();
    this.bgAgentView = {
      agent: a, channelId: channel.id, channelName: channel.name,
      task: '', messages: [], next: 0, finished: false, skipped: 0, error: '',
    };
    this.cdr.markForCheck();
    this._pollBackgroundAgent();
  }

  closeBackgroundAgent(): void {
    if (this._bgAgentTimer) { clearTimeout(this._bgAgentTimer); this._bgAgentTimer = null; }
    if (this.bgAgentView) {
      this.bgAgentView = null;
      this.cdr.markForCheck();
    }
  }

  /** The agent's transcript grows a row per finished step (a tool call, its
   *  result, a paragraph); read what is new every 2 s until it finishes. */
  private async _pollBackgroundAgent(): Promise<void> {
    const view = this.bgAgentView;
    if (!view) return;
    if (this.destroyed || !this.bgAgentOpen) { this.closeBackgroundAgent(); return; }
    try {
      const r = await this.sessionService.getBackgroundAgentTranscript(view.agent.id, view.next);
      if (this.bgAgentView !== view) return;
      view.error = '';
      if (r.prompt && !view.task) view.task = r.prompt;
      if (r.skipped) view.skipped = r.skipped;
      if (r.finished !== null) view.finished = r.finished;
      view.next = r.next;
      if (r.content || r.finished !== null) {
        const prev = view.messages[0];
        view.messages = [{
          isSelf: false,
          content: ((prev?.content as string) || '') + (r.content || ''),
          agentName: view.agent.description || view.agent.type,
          timestamp: prev?.timestamp || new Date(view.agent.started_ms).toISOString(),
          isStreaming: !view.finished,
          isFinal: view.finished,
        }];
      }
    } catch (e: any) {
      if (this.bgAgentView !== view) return;
      view.error = e?.status === 404
        ? 'This agent has no transcript on disk.'
        : (e?.error?.detail || 'Could not read the agent\'s transcript — retrying.');
      if (e?.status === 404) view.finished = true;
    }
    this.cdr.markForCheck();
    if (this.bgAgentView === view && !view.finished) {
      this._bgAgentTimer = setTimeout(() => this._pollBackgroundAgent(), 2000);
    }
  }

  /** Still listed as running by the bridge (the row is in the channel list). */
  bgAgentRunning(): boolean {
    const v = this.bgAgentView;
    return !!v && !v.finished && (this.backgroundAgents[v.channelId] || []).some(a => a.id === v.agent.id);
  }

  private restoreSelectedView(): void {
    const viewKey = this.teamName ? `lit-heartbeat-active-view-${this.teamName}` : 'lit-heartbeat-active-view';
    const channelKey = this.teamName ? `lit-heartbeat-selected-channel-${this.teamName}` : 'lit-heartbeat-selected-channel';
    const savedView = localStorage.getItem(viewKey);
    if (savedView === 'channel') {
      try {
        const saved = JSON.parse(localStorage.getItem(channelKey) || '');
        if (saved?.id) {
          const channel = this.personalChannels.find(c => c.id === saved.id)
            || this.guestChannels.find(c => c.id === saved.id);
          if (channel) {
            this.selectChannel(channel);
            return;
          }
        }
      } catch {}
    }
    // First-time user: no saved view at all — auto-select first channel
    if (!savedView) {
      const first = this.parentChannels[0];
      if (first) {
        this.selectChannel(first);
        return;
      }
    }
    // Remote place with nothing restorable: stay on the channel view. The
    // DM/agent-select surface is local-only and reads as broken ("No agents
    // configured") when reached over the pipe.
    if (this.isRemotePlace) {
      this.activeView = 'channel';
      this.cdr.markForCheck();
      return;
    }
    // Fall back to saved agent (already handled in loadAgents)
  }

  // YOUR DEVICES tree + remote-channel scaffolding retired 2026-07-27:
  // remote places open through the palette / starred rail as ordinary team
  // chats (the {device}:{team} address seam in mux services).

  /** Pre-emptive credential check banner for the selected channel's agent —
   *  an expired Claude login is surfaced BEFORE the user types (every message
   *  would bounce with "Login expired"). Advisory: never blocks chat. */
  channelAuthBanner: { kind: 'warn' | 'error'; text: string; credentialsId?: string | null } | null = null;

  /** A reply that IS the Claude usage-limit notice names the credential the
   *  agent is on and offers to move it to another credential of the same
   *  vendor in this team (app.jov.ai 2026-10-12: every agent of a user was on
   *  his maxed personal credential; re-authenticating the team one changed
   *  nothing because nothing said which credential the agent used). */
  channelLimitBanner: {
    credentialsId: string | null; resets: string | null;
    options: { id: string; name: string }[]; chosen: string | null;
    state: 'idle' | 'switching' | 'done'; text: string;
  } | null = null;

  private async showLimitHit(req: { credentials_id?: string | null; team?: string; resets?: string | null }): Promise<void> {
    const cid = req.credentials_id ?? null;
    const agentName = this.channelAgentDisplayName();
    const team = this.teamName || 'local';
    const forChannel = this.selectedChannelId;
    let creds: MuxCredentialService.Credential[] = [];
    try { creds = await this.credService.list(team); } catch { creds = []; }
    if (this.selectedChannelId !== forChannel) return;
    const options = creds
      .filter(c => c.vendor === 'anthropic' && !!c.id && c.id !== cid && c.status !== 'expired' && c.status !== 'unconfigured')
      .map(c => ({ id: c.id as string, name: c.name || (c.id as string) }));
    const where = cid ? `credential "${cid}"` : 'its credential';
    const when = req.resets ? ` (resets ${req.resets})` : '';
    const text = options.length
      ? `${agentName} is on ${where}, which has hit its usage limit${when}. Switch it to:`
      : `${agentName} is on ${where}, which has hit its usage limit${when}. No other Claude credential in this team — add one under Credentials, or wait for the reset.`;
    this.channelLimitBanner = { credentialsId: cid, resets: req.resets ?? null, options, chosen: options[0]?.id ?? null, state: 'idle', text };
    this.cdr.markForCheck();
  }

  async switchAgentCredential(): Promise<void> {
    const b = this.channelLimitBanner;
    const agentId = this.selectedChannelAgentId || this.primaryAgentId;
    if (!b || !b.chosen || !agentId || b.state === 'switching') return;
    b.state = 'switching';
    this.cdr.markForCheck();
    try {
      // The registration route preserves every field the body omits; the
      // credential change itself kills the live session, so the next message
      // spawns under the new credential's CLI profile.
      await this.agentService.updateAgent(agentId, { id: agentId, credentials_id: b.chosen }, this.teamName || undefined);
      b.state = 'done';
      b.text = `${this.channelAgentDisplayName()} now runs on "${b.chosen}". Send your message again.`;
      setTimeout(() => { if (this.channelLimitBanner === b) { this.channelLimitBanner = null; this.cdr.markForCheck(); } }, 10000);
    } catch (e: any) {
      b.state = 'idle';
      b.text = `Could not switch: ${e?.error?.detail || e?.message || 'unknown error'}`;
    }
    this.cdr.markForCheck();
  }

  dismissLimitBanner(): void {
    this.channelLimitBanner = null;
    this.cdr.markForCheck();
  }

  /** An agent reply that IS the auth failure surfaces the re-auth banner right
   *  under it (Ben, 2026-09-11). The banner only renders when the status
   *  endpoint confirms the login is dead, so transient failures stay text-only. */
  private static readonly AUTH_ERROR_RE = /^\s*Error: (OAuth token expired|Couldn't refresh the Claude login)/;
  /** An auth-failure reply → the re-auth card, inline, one click. Prefer the
   *  server's structured `auth_required` (names the credential the turn
   *  actually used); fall back to the reply text, which still shows the card
   *  even when the status probe of the agent's credential looks healthy — the
   *  two disagreed on 2026-09-16 (legacy flat agent, no credential) and the
   *  user got a bare error line with nothing to click. */
  private maybeChannelAuthRecheck(content: unknown, meta?: any): void {
    const req = meta?.auth_required;
    if (req && typeof req === 'object') {
      this.showAuthRequired(req);
      return;
    }
    const lim = meta?.limit_hit;
    if (lim && typeof lim === 'object') {
      void this.showLimitHit(lim);
      return;
    }
    if (meta && this.channelLimitBanner && this.channelLimitBanner.state !== 'done') {
      this.channelLimitBanner = null;  // a reply that is not a limit notice: the limit is over
    }
    if (typeof content === 'string' && HeartbeatWidgetComponent.AUTH_ERROR_RE.test(content)) {
      void this.checkChannelAgentAuth(true);
    }
  }

  private showAuthRequired(req: { credentials_id?: string | null; team?: string; backend?: string }): void {
    const cid = req.credentials_id ?? null;
    const agentName = this.channelAgentDisplayName();
    this.channelAuthBanner = cid
      ? { kind: 'error', credentialsId: cid,
          text: `Claude login for credential "${cid}" expired — re-authenticate to reconnect ${agentName}.` }
      : { kind: 'error', credentialsId: null,
          text: `${agentName} has no Claude credential bound — sign in to bind one.` };
    this.authReauthState = 'idle';
    this.cdr.markForCheck();
  }

  private channelAgentDisplayName(): string {
    const agentId = this.selectedChannelAgentId || this.primaryAgentId;
    const agent = agentId ? this.agentService.getAgents().find(a => a.id === agentId) : null;
    return agent?.name || agentId || 'this agent';
  }

  private async checkChannelAgentAuth(fromErrorReply = false): Promise<void> {
    this.channelAuthBanner = null;
    this.channelLimitBanner = null;
    const agentId = this.selectedChannelAgentId || this.primaryAgentId;
    const agent = agentId ? this.agentService.getAgents().find(a => a.id === agentId) : null;
    const backend = (agent?.backend || '').toLowerCase();
    if (!agent || (backend !== 'claude-interactive' && backend !== 'claude-cli')) {
      this.cdr.markForCheck();
      return;
    }
    const checkedFor = this.selectedChannelId;
    const status = await this.credService.status(
      agent.credentials_id ?? null, 'anthropic', this.teamName || 'local');
    if (this.selectedChannelId !== checkedFor) return; // channel switched mid-check
    if (status?.auth_status === 'token_expired') {
      this.channelAuthBanner = {
        kind: 'error', credentialsId: agent.credentials_id ?? null,
        text: 'Claude login expired — re-authenticate to reconnect this agent.',
      };
    } else if (fromErrorReply) {
      // The reply itself said the login is dead; believe it even if the probe
      // of this credential is green (the turn may have used another).
      this.channelAuthBanner = {
        kind: 'error', credentialsId: agent.credentials_id ?? null,
        text: `${agent.name || agent.id} reported an expired Claude login — re-authenticate to reconnect it.`,
      };
    } else if (status?.warning) {
      this.channelAuthBanner = { kind: 'warn', text: status.warning };
    }
    this.cdr.markForCheck();
  }

  /** Inline re-auth flow state (expands the auth banner into the OAuth wizard
   *  right in the chat area — in-app authentication, never a terminal). */
  authReauthState: 'idle' | 'starting' | 'code' | 'submitting' | 'done' | 'error' = 'idle';
  authOauthUrl: string | null = null;
  authReauthCode = '';
  authReauthError: string | null = null;

  async beginInlineReauth(): Promise<void> {
    this.authReauthState = 'starting';
    this.authReauthError = null;
    this.authReauthCode = '';
    this.cdr.markForCheck();
    try {
      const agentId = this.selectedChannelAgentId || this.primaryAgentId;
      const agent = agentId ? this.agentService.getAgents().find(a => a.id === agentId) : null;
      // The card names the credential the failing turn used; that wins over the
      // agent's configured one (they differ when a legacy definition answered).
      const cid = this.channelAuthBanner?.credentialsId !== undefined
        ? this.channelAuthBanner?.credentialsId
        : agent?.credentials_id;
      const session = await this.authWizard.startAuth(cid || undefined);
      this.authOauthUrl = session.oauth_url;
      if (session.oauth_url) window.open(session.oauth_url, '_blank');
      this.authReauthState = 'code';
    } catch {
      this.authReauthError = 'Could not start sign-in.';
      this.authReauthState = 'error';
    }
    this.cdr.markForCheck();
  }

  openReauthUrl(): void {
    if (this.authOauthUrl) window.open(this.authOauthUrl, '_blank');
  }

  async submitReauthCode(): Promise<void> {
    const code = this.authReauthCode.trim();
    if (!code) return;
    this.authReauthState = 'submitting';
    this.cdr.markForCheck();
    try {
      const r = await this.authWizard.submitCode(code);
      if (!r.success) throw new Error(r.error || 'failed');
      this.authReauthState = 'done';
      this.cdr.markForCheck();
      setTimeout(() => {
        this.channelAuthBanner = null;
        this.authReauthState = 'idle';
        void this.checkChannelAgentAuth();
      }, 5000);
    } catch {
      this.authReauthError = "That code didn't work. Try again.";
      this.authReauthState = 'code';
    }
    this.cdr.markForCheck();
  }

  cancelInlineReauth(): void {
    void this.authWizard.cancelAuth().catch(() => {});
    this.authReauthState = 'idle';
    this.authReauthError = null;
    this.cdr.markForCheck();
  }

  async selectChannel(channel: MuxSessionService.NavChannel): Promise<void> {
    // Clicking a channel leaves any background-agent stream open over it.
    this.closeBackgroundAgent();
    // Terminal mode is per channel: the attach command targets the channel's
    // own bridge session, so it must never carry over to another channel.
    if (this.channelTerminalMode) {
      if (this.selectedChannelId && this.selectedChannelId !== channel.id) {
        this._rememberChannelMode(this.selectedChannelId, true);
      }
      this.channelTerminalMode = false;
      this.channelTmuxCommand = '';
    }
    this.activeView = 'channel';
    this.selectedChannelId = channel.id;
    this.selectedChannelName = channel.name;
    this.selectedChannelFolderPath = channel.folder_path || null;
    this.channelUnreadResponses.delete(channel.id);
    this.streamingChannels.delete(channel.id);
    this.selectedAgentId = null;
    // selectedChannelAgentId = explicit assignment only (what's in channel.json)
    this.selectedChannelAgentId = channel.agent_id || null;
    // Someone else's agent: its model, sign-in, usage, terminal and orchestrations
    // are the host's. Only the conversation is shown.
    const guest = HeartbeatWidgetComponent.isGuestChannelId(channel.id);
    if (guest) {
      this.selectedChannelAgentName = channel.trust?.agent_name || channel.name;
      this.channelAuthBanner = null;
      this.channelLimitBanner = null;
      this.guestDraft = '';
      channel.unread = 0;
      void this.sessionService.markChannelRead(channel.id, undefined, this.teamName);
    } else if (this.selectedChannelAgentId) {
      const cached = this.agentService.getAgents().find(a => a.id === this.selectedChannelAgentId);
      this.selectedChannelAgentName = cached?.name || this.selectedChannelAgentId;
    } else {
      this.selectedChannelAgentName = null;
    }
    if (!guest) {
      // Load this channel's per-agent model overrides so the selector shows the effective model.
      this.loadChannelModelOverrides(channel.id, this.selectedChannelAgentId || this.primaryAgentId);
      void this.checkChannelAgentAuth();
      // Start usage polling using the effective agent (explicit or primary)
      const effectiveAgentId = this.selectedChannelAgentId || this.primaryAgentId;
      if (effectiveAgentId) {
        this._muxUsage.startPolling(this.currentBackendId, undefined, effectiveAgentId);
      }
    }
    const viewKey = this.teamName ? `lit-heartbeat-active-view-${this.teamName}` : 'lit-heartbeat-active-view';
    const channelKey = this.teamName ? `lit-heartbeat-selected-channel-${this.teamName}` : 'lit-heartbeat-selected-channel';
    localStorage.setItem(viewKey, 'channel');
    localStorage.setItem(channelKey, JSON.stringify({ id: channel.id, name: channel.name }));
    this.disconnectWs();
    this.disconnectChannelWs();
    this.statusText = null;
    this.channelMessages = [];
    this.channelStreamingIndex = null;
    this.pinnedMessages = [];
    this.pinsPaneOpen = false;
    this.orchestrationStatus = { active: false };
    this.orchestrationPanelOpen = false;
    this.cdr.markForCheck();
    await this.loadChannelMessages();
    // Connect WS only after HTTP load completes — prevents race where WS
    // stream_start sets channelStreamingIndex, then HTTP response replaces
    // channelMessages, leaving the index pointing at the wrong bubble.
    if (this.selectedChannelId === channel.id && this.activeView === 'channel') {
      this.connectChannelWs(channel.id);
      this._connectTelemetryWs();
      if (!guest) {
        this.restoreOrchestrationStatus(channel.id);
        if (this._channelWasInTerminal(channel.id)) {
          await this.toggleChannelTerminal(true);
        }
      }
    }
  }

  private async restoreOrchestrationStatus(channelId: string): Promise<void> {
    try {
      const status = await lastValueFrom(
        this.http.get<any>(`${ConfigService.muxUrl}/channels/${channelId}/orchestrations/status`)
      );
      if (status?.active) {
        this.orchestrationStatus = {
          active: true,
          name: status.name,
          slug: status.slug,
          state: status.state,
          step: status.step,
          progress: status.progress,
          error: status.error,
          gate_prompt: status.gate_prompt,
          gate_options: status.gate_options,
        };
        this.orchestrationPanelOpen = true;
        this.cdr.markForCheck();
      }
    } catch {
      // No active orchestration or endpoint unavailable — leave panel closed
    }
  }

  async loadChannelMessages(): Promise<void> {
    const channelId = this.selectedChannelId;
    if (!channelId) return;
    this.channelLoading = true;
    this.viewingChannelHistory = false;
    this.cdr.markForCheck();

    try {
      const muxUrl = ConfigService.muxUrl;
      const remote = parsePlaceAddress(this.teamName);
      const messagesUrl = remote
        ? `${muxUrl}/remote/${encodeURIComponent(remote.device)}/channels/${encodeURIComponent(channelId)}/messages?team=${encodeURIComponent(remote.team)}&limit=50`
        : `${muxUrl}/channels/${channelId}/messages?limit=50${this.teamName ? '&team=' + encodeURIComponent(this.teamName) : ''}`;
      const resp = await lastValueFrom(
        this.http.get<{ messages: MuxSessionService.ChannelMessage[], channel_dir?: string }>(messagesUrl)
      );
      // Stale-channel guard: user switched channels while HTTP was in flight
      if (this.selectedChannelId !== channelId) return;

      const rawAll = (resp.messages || []).filter(m => m.metadata?.source !== 'reaction');
      // Dialog answers render inside the question card (selected state), not as bubbles
      const raw = rawAll.filter(m => m.metadata?.source !== 'dialog-answer');
      // A conversation whose last reply was an auth failure opens with the card up.
      const lastOut = [...rawAll].reverse().find(m => m.direction === 'out');
      if (lastOut?.metadata?.auth_required) this.showAuthRequired(lastOut.metadata.auth_required);
      else if (lastOut?.metadata?.limit_hit) void this.showLimitHit(lastOut.metadata.limit_hit);
      this.channelMessages = raw.map(m => {
        let rawContent = m.direction === 'in' ? this._stripContextEnvelope(m.content) : m.content;
        // System notices (Context compacting, Session stopped) preserved — LINKS regex handles them.
        const { cleanContent: content, actions } = m.direction === 'out'
          ? this.extractChannelActions(rawContent)
          : { cleanContent: rawContent, actions: [] };
        const { content: finalContent, links } = ChatScreenComponent.extractLinks(content);
        const isError = m.direction === 'out' && this._isInterruptedContent(rawContent);
        const isCancelled = m.direction === 'out' && (this._isCancelledContent(rawContent) || m.metadata?.cancelled === true);
        return {
          isSelf: m.direction === 'in',
          content: finalContent,
          timestamp: m.timestamp,
          isFinal: true as const,
          messageId: m.id,
          pinned: m.pinned === true,
          reactions: m.reactions,
          ...(links.length ? { links } : {}),
          ...(actions.length ? { channelActions: actions } : {}),
          ...(m.direction === 'in' ? { read: m.read === true, handledIn: m.metadata?.handled_in, handledAt: m.metadata?.handled_at } : {}),
          ...(isError ? { isError: true } : {}),
          ...(isCancelled ? { isCancelled: true } : {}),
          ...(m.file_path ? { turnPath: m.file_path } : {}),
          ...(m.metadata?.session_id ? { sessionId: m.metadata.session_id } : {}),
          ...(m.metadata?.ttfb_ms ? { durationMs: m.metadata.ttfb_ms } : {}),
          ...(m.metadata?.fcp_ms != null ? { fcpMs: m.metadata.fcp_ms } : {}),
          ...(m.metadata?.model ? { model: m.metadata.model } : {}),
          ...(m.metadata?.input_tokens != null ? { inputTokens: m.metadata.input_tokens } : {}),
          ...(m.metadata?.output_tokens != null ? { outputTokens: m.metadata.output_tokens } : {}),
          ...(m.metadata?.cache_tokens != null ? { cacheTokens: m.metadata.cache_tokens } : {}),
          ...(m.metadata?.transcript_path ? { transcriptPath: m.metadata.transcript_path } : {}),
          ...(m.metadata?.orchestration ? { orchestration: m.metadata.orchestration } : {}),
          ...(m.from && m.direction === 'out' ? { agentName: this.agents.find(a => a.agent.id === m.from)?.agent.name ?? m.from } : {}),
          ...(m.from && m.direction === 'in' ? { agentName: m.from } : {}),
          ...(m.content_truncated ? { contentTruncated: true, contentLength: m.content_length } : {}),
        };
      });
      this.channelMessages = HeartbeatWidgetComponent.orderChannelHistory(this.channelMessages);

      // Attach each dialog answer to the question card it answered: the nearest
      // preceding assistant message. The card renders it as the selected option.
      for (let ai = 0; ai < rawAll.length; ai++) {
        if (rawAll[ai].metadata?.source !== 'dialog-answer') continue;
        const label = this._stripContextEnvelope(rawAll[ai].content).trim();
        for (let i = ai - 1; i >= 0; i--) {
          if (rawAll[i].direction === 'out') {
            const target = this.channelMessages.find(cm => cm.messageId === rawAll[i].id);
            if (target) target.presetAnswer = label;
            break;
          }
        }
      }

      // Detect shared channel: any inbound message from a *different* user
      const me = JSON.parse(localStorage.getItem('me') || '{}')?.username;
      if (me && raw.some(m => m.direction === 'in' && m.from && m.from !== me)) {
        this.sharedChannels.add(channelId);
      }

      if (resp.channel_dir) {
        this.selectedChannelDir = resp.channel_dir;
      }

      this.loadPinnedMessages();
    } catch (err: any) {
      console.error('Failed to load channel messages:', err?.message || err, err);
      console.error('Channel:', channelId, 'URL:', `${ConfigService.muxUrl}/channels/${channelId}/messages?limit=50`);
      // Don't wipe existing messages on transient errors — preserve what the user can see
      if (this.selectedChannelId !== channelId) return;
    }

    this.channelLoading = false;
    this.cdr.markForCheck();
    setTimeout(() => this.chatScreen?.forceScrollToBottom(), 50);
  }

  /**
   * User clicked an option on an interactive AskUserQuestion in the channel.
   * Post the chosen label tagged `source: dialog-answer` — the mux monitor
   * matches it against the pending question and keys it into the terminal
   * picker. No chat bubble: the card itself renders the selection, so the
   * label is stamped on the streaming turn (presetAnswer) to survive the
   * streaming→final component swap.
   */
  async onChannelQuestionAnswered(e: { question: string; answer: string }): Promise<void> {
    if (!e?.answer?.trim() || !this.selectedChannelId) return;
    if (this.channelStreamingIndex !== null && this.channelMessages[this.channelStreamingIndex]) {
      this.channelMessages[this.channelStreamingIndex] = {
        ...this.channelMessages[this.channelStreamingIndex],
        presetAnswer: e.answer,
      };
      this.channelMessages = [...this.channelMessages];
      this.cdr.markForCheck();
    }
    await this.sessionService.postChannelMessage(this.selectedChannelId, e.answer, this.teamName, 'dialog-answer');
  }

  async onChannelSubmit(event: ChatInputSubmitEvent): Promise<void> {
    if (!this.selectedChannelId || !event.content.trim()) return;

    const content = event.content.trim();

    // Handle slash commands
    if (content === '/evict') {
      const channel = this.personalChannels.find(c => c.id === this.selectedChannelId);
      const agentId = channel?.agent_id
        ?? this.agents.find(a => a.agent.heartbeat_enabled)?.agent.id;
      console.log('[evict] content:', JSON.stringify(content), 'channelId:', this.selectedChannelId, 'channel:', channel, 'agentId:', agentId);
      if (!agentId) {
        console.warn('[evict] No agentId found for channel — aborting');
        return;
      }
      try {
        await lastValueFrom(
          this.http.post(
            `${ConfigService.muxUrl}/agents/${agentId}/heartbeat/channels/${this.selectedChannelId}/evict-session`,
            {}
          )
        );
        this.channelMessages = [
          ...this.channelMessages,
          { isSelf: false, content: 'Session evicted.', isFinal: true, timestamp: new Date().toISOString() },
        ];
      } catch (err) {
        this.channelMessages = [
          ...this.channelMessages,
          { isSelf: false, content: 'Failed to evict session.', isError: true, isFinal: true, timestamp: new Date().toISOString() },
        ];
      }
      this.cdr.markForCheck();
      this.chatScreen?.forceScrollToBottom();
      return;
    }

    // If viewing history, jump back to latest before sending
    if (this.viewingChannelHistory) {
      await this.jumpToLatestMessages();
    }

    // Optimistically add to UI
    this.channelMessages = [
      ...this.channelMessages,
      {
        isSelf: true,
        content,
        timestamp: new Date().toISOString(),
        isFinal: true,
        read: false,
      },
    ];
    this.channelSending = true;
    this.cdr.markForCheck();
    this.chatScreen?.forceScrollToBottom();

    // Slash commands go straight to the channel's CLI session, like typing in the
    // terminal (Lais, 2026-09-02: /loop and /goal from the chat, so the work lands
    // in the channel transcript). The reply comes back through the organic relay.
    // Only the interactive backend has a live CLI to type into.
    // `/on <host> …` is routing for the home (run this turn there), not a CLI command.
    if (content.startsWith('/') && !/^\/on\s/i.test(content)) {
      const dropOptimistic = () => {
        const last = this.channelMessages[this.channelMessages.length - 1];
        if (last?.isSelf && last.content === content) this.channelMessages = this.channelMessages.slice(0, -1);
      };
      if (this.currentBackendId !== 'claude-interactive') {
        dropOptimistic();
        const ref = this._snackBar.open('Slash commands require terminal mode', 'OK', { duration: 5000 });
        ref.afterDismissed().subscribe(() => {
          this.terminalButtonThrob = true;
          this.cdr.markForCheck();
          setTimeout(() => { this.terminalButtonThrob = false; this.cdr.markForCheck(); }, 3000);
        });
        this.channelSending = false;
        this.cdr.markForCheck();
        return;
      }
      if (this.channelStreamingIndex !== null) {
        // A reply relayed while the heartbeat is mid-turn on this channel is skipped
        // by the organic path — don't race it.
        dropOptimistic();
        this._snackBar.open('Wait for the current reply to finish, then run the command.', 'OK', { duration: 5000 });
        this.channelSending = false;
        this.cdr.markForCheck();
        return;
      }
      try {
        const resp = await this.sessionService.sendRawCommand(this.selectedChannelId, content, this.teamName);
        const last = this.channelMessages[this.channelMessages.length - 1];
        if (last?.isSelf && resp?.message_id) last.messageId = resp.message_id; // dedupe the persisted echo
      } catch (e: any) {
        const detail = e?.error?.detail || 'Command not sent — check your connection and try again.';
        const last = this.channelMessages[this.channelMessages.length - 1];
        if (last?.isSelf) {
          last.isError = true;
          last.content = `${content}\n\n_(${detail})_`;
        }
        this._snackBar.open(detail, 'OK', { duration: 8000 });
      } finally {
        this.channelSending = false;
        this.cdr.markForCheck();
      }
      return;
    }

    const contentWithContext = this._buildChannelContextEnvelope(content);
    // The send POST can fail two ways: the service throws, OR (its actual behavior)
    // it swallows the HTTP error and returns null. BOTH must flag the message and
    // surface a snackbar — and `finally` must ALWAYS re-enable input, or the send
    // button stays disabled and the user is silently locked out (had to fall back to
    // the terminal). This handler covers both, and bails before the "connecting"
    // bubble so a message that never reached the server can't leave a stuck spinner.
    const markSendFailed = () => {
      const last = this.channelMessages[this.channelMessages.length - 1];
      if (last?.isSelf) {
        last.isError = true;
        last.content = `${content}\n\n_(failed to send — press send to retry)_`;
      }
      this._snackBar.open('Message failed to send — check your connection and try again.', 'OK', { duration: 6000 });
    };
    try {
      const resp = await this.sessionService.postChannelMessage(this.selectedChannelId, contentWithContext, this.teamName);
      if (!resp?.id) {
        // postChannelMessage returns null on any POST failure (offline, overload, 5xx).
        console.warn('Channel send failed: no id returned from postChannelMessage');
        markSendFailed();
        return; // `finally` still resets channelSending; skip the connecting bubble
      }
      const last = this.channelMessages[this.channelMessages.length - 1];
      if (last?.isSelf) {
        last.messageId = resp.id;
      }

      // If agent is actively streaming, inject a follow-up signal so it can acknowledge the new message
      if (this.channelStreamingIndex !== null && this.selectedAgentId) {
        lastValueFrom(
          this.http.post(`${ConfigService.muxUrl}/agents/${this.selectedAgentId}/signals`, {
            type: 'followup',
            content,
          })
        ).catch(() => {}); // Non-critical — fire and forget
      }

      // Show "connecting" dots immediately after send so the user sees feedback
      // before the WebSocket delivers the thinking/stream_start events.
      // The thinking handler guards on channelStreamingIndex === null, so no double-bubble.
      if (this.channelStreamingIndex === null && !this.viewingChannelHistory) {
        this.channelLoading = true;
        this.channelLoadingState = 'connecting';
        this.channelMessages = [
          ...this.channelMessages,
          { isSelf: false, content: '', timestamp: new Date().toISOString(), isStreaming: true },
        ];
        this.channelStreamingIndex = this.channelMessages.length - 1;
        clearTimeout(this._thinkingTimeout);
        this._thinkingTimeout = setTimeout(() => {
          this.channelLoading = false;
          this.channelLoadingState = '';
          if (this.channelStreamingIndex !== null) {
            this.channelMessages = this.channelMessages.filter((_, i) => i !== this.channelStreamingIndex);
            this.channelStreamingIndex = null;
          }
          this.cdr.markForCheck();
        }, 30000);
        this.chatScreen?.scrollToBottom();
      }
    } catch (err) {
      // Unexpected throw (a different await in the flow). Same treatment.
      console.error('Channel send failed:', err);
      markSendFailed();
    } finally {
      this.channelSending = false;
      this.cdr.markForCheck();
    }

    // Refresh usage after send — delay to let backend process
    setTimeout(() => this._muxUsage.refreshAfterSend(this.currentBackendId, this.selectedChannelAgentId || this.primaryAgentId || undefined), 5000);
  }

  private _stripContextEnvelope(content: string): string {
    return content
      .replace(/<context>[\s\S]*?<\/context>\s*/g, '')
      .trim();
  }

  /** Returns true if the message content ends with an interrupted/connection-lost suffix. */
  private _isInterruptedContent(content: string): boolean {
    return /\*\[(Response interrupted|Connection lost)[^\]]*\]\*\s*$/.test(content);
  }

  private _isCancelledContent(content: string): boolean {
    return /\*\[Cancelled\]\*\s*$/.test(content);
  }

  private async _openApp(appId: string, layout?: string): Promise<boolean> {
    const grid = this._widget.grid;
    if (!grid) { console.warn('[open_app] No grid registered'); return false; }
    await this._dynamicWidgets.getWidgets(grid.teamName);
    let dynWidget = this._dynamicWidgets.getWidget(appId);
    if (!dynWidget) {
      for (const prefix of ['team-', 'everyone-']) {
        dynWidget = this._dynamicWidgets.getWidget(prefix + appId);
        if (dynWidget) break;
      }
    }
    const docked = layout === 'docked' || layout === 'side-by-side';
    const dockedValues = docked ? { position: { referencePanel: this.id, direction: 'right' } } : {};
    const dockedSpace = docked ? { x: 30, y: 0, rows: 35, cols: 25 } : { x: 0, y: 0, rows: 35, cols: 55 };
    if (dynWidget && dynWidget.type === 'iframe' && dynWidget.url) {
      this._ngZone.run(() => {
        (grid as any)._addWidget('IframeWidget', { title: dynWidget!.title, ...dockedValues }, {
          baseInputs: { url: dynWidget!.url, title: dynWidget!.title, icon: dynWidget!.icon || 'web' },
          defaultSpace: dockedSpace,
        });
      });
      return true;
    } else if (dynWidget && dynWidget.type === 'component' && dynWidget.element) {
      const app = {
        id: dynWidget.id, manifest: {
          name: dynWidget.name, title: dynWidget.title, icon: dynWidget.icon,
          element: dynWidget.element, scripts: dynWidget.scripts || [],
          styles: dynWidget.styles || [], config: dynWidget.config,
        }, source: dynWidget.source, basePath: dynWidget.basePath, loaded: false,
      };
      this._ngZone.run(() => {
        (grid as any)._addWidget('ExternalAppWidget', { title: dynWidget!.title, ...dockedValues }, {
          baseInputs: { app, config: {}, basePath: null, title: dynWidget!.title },
          defaultSpace: docked ? { x: 30, y: 0, rows: 25, cols: 25 } : { rows: 25, cols: 40 },
        });
      });
      return true;
    } else if ((appId + 'Widget') in WidgetService.REGISTRY) {
      this._ngZone.run(() => grid.$openWidget((appId + 'Widget') as WidgetService.WidgetName));
      return true;
    } else {
      console.warn(`[open_app] Unknown app: ${appId}`);
      return false;
    }
  }

  private _buildChannelContextEnvelope(userMessage: string): string {
    const trigger: any = { type: 'user_message', channel_id: this.selectedChannelId };
    // Tag the message with the agent the user selected in the input toolbar
    const targetId = this.quickSwitchActiveId;
    if (targetId) {
      trigger.target_agent_id = targetId;
    }
    const envelope: any = { trigger };

    if (this._settings.labsReactions && this.selectedChannelId) {
      const recentWithIds = this.channelMessages.slice(-10).filter(m => m.messageId);
      envelope.messages = recentWithIds.map(m => {
        const entry: any = {
          id: m.messageId,
          role: m.isSelf ? 'user' : 'assistant',
          preview: typeof m.content === 'string'
            ? m.content.substring(0, 80).replace(/\n/g, ' ')
            : ''
        };
        if (m.reactions?.length) {
          entry.reactions = m.reactions.map((r: any) => `${r.emoji}(${r.reactor})`);
        }
        return entry;
      });
    }

    // Widget context (what apps are showing)
    const widgetContexts = this._widgetContext.getContexts();
    if (widgetContexts.length > 0) {
      envelope.widgets = widgetContexts;
    }

    const contextBlock = `<context>\n${JSON.stringify(envelope, null, 2)}\n</context>`;
    return `${contextBlock}\n\n${userMessage}`;
  }

  private _buildDmContextEnvelope(userMessage: string, dmChannelId: string, inputModality?: string): string {
    const trigger: any = { type: 'user_message', channel_id: dmChannelId };
    if (inputModality === 'voice') {
      trigger.input_modality = 'voice';
    }
    const envelope: any = { trigger };

    if (this._settings.labsReactions) {
      const recentWithIds = this.messages.slice(-10).filter(m => m.messageId);
      envelope.messages = recentWithIds.map(m => {
        const entry: any = {
          id: m.messageId,
          role: m.isSelf ? 'user' : 'assistant',
          preview: typeof m.content === 'string'
            ? m.content.substring(0, 80).replace(/\n/g, ' ')
            : ''
        };
        if (m.reactions?.length) {
          entry.reactions = m.reactions.map((r: any) => `${r.emoji}(${r.reactor})`);
        }
        return entry;
      });
    }

    const contextBlock = `<context>\n${JSON.stringify(envelope, null, 2)}\n</context>`;
    return `${contextBlock}\n\n${userMessage}`;
  }

  createNewChannel(): void {
    this.startChannelDraft(null);
  }

  async openFolder(): Promise<void> {
    const { FolderPickerDialogComponent } = await import('../../folder-picker-dialog/folder-picker-dialog.component');
    const dialogRef = this._dialog.open(FolderPickerDialogComponent, {
      width: '480px',
      autoFocus: false,
    });
    const picked: { path: string; host: string | null } | null = await lastValueFrom(dialogRef.afterClosed());
    if (!picked?.path) return;

    try {
      const channel = await this.sessionService.openFolder(picked.path, undefined, this.teamName, picked.host || undefined);
      if (channel) {
        await this.loadNavTree();
        this.selectChannel(channel);
      }
    } catch (e: any) {
      this._snackBar.open(e.message || 'Failed to open folder', 'OK', { duration: 5000 });
    }
  }

  /** An in-app dialog, not the browser's confirm()/prompt(): Chrome returned
   *  from those without showing them, so Rename, Archive and New Channel did
   *  nothing (2026-10-01). Resolves false when cancelled or dismissed; for a
   *  text question, the trimmed text (possibly empty). */
  private async _ask(data: ConfirmDialogData): Promise<boolean | string> {
    const ref = this._dialog.open(ConfirmDialogComponent, { data, width: '420px', autoFocus: 'first-tabbable' });
    const result = await lastValueFrom(ref.afterClosed(), { defaultValue: false });
    return result ?? false;
  }

  async deleteChannel(channel: MuxSessionService.NavChannel, event: Event): Promise<void> {
    event.stopPropagation();
    const answer = await this._ask({
      title: `Delete #${channel.name}?`,
      message: 'This cannot be undone. Delete removes the channel and its messages. '
        + 'Delete permanently also removes the agent\'s transcript and memory copies of this '
        + 'channel on the server, and writes an audit entry naming everything removed '
        + '(for a client deletion obligation).',
      confirmText: 'Delete',
      altText: 'Delete permanently',
    });
    if (!answer) return;
    const purge = answer === 'alt';

    let ok = false;
    try {
      ok = await this.sessionService.deleteChannel(channel.id, this.teamName, purge);
    } catch (error: any) {
      this._snackBar.open(error?.error?.detail || 'Could not delete the channel', 'OK', { duration: 8000 });
      return;
    }
    if (ok) {
      if (this.selectedChannelId === channel.id) {
        this.activeView = 'dm';
        this.selectedChannelId = null;
        this.selectedChannelName = null;
        this.selectedChannelAgentId = null;
        this.selectedChannelAgentName = null;
        this.channelMessages = [];
      }
      await this.loadNavTree();
    }
    this.cdr.markForCheck();
  }

  async renameCurrentChannel(): Promise<void> {
    if (!this.selectedChannelId || !this.selectedChannelName) return;
    const channelId = this.selectedChannelId;
    const oldName = this.selectedChannelName;
    const newName = await this._ask({
      title: `Rename #${oldName}`,
      message: '',
      confirmText: 'Rename',
      input: { value: oldName, placeholder: 'channel name' },
    });
    if (typeof newName !== 'string' || !newName || newName === oldName) return;
    const ok = await this.sessionService.renameChannel(channelId, newName, this.teamName);
    if (ok) {
      if (this.selectedChannelId === channelId) this.selectedChannelName = newName;
      const channel = this.personalChannels.find(c => c.id === channelId);
      if (channel) channel.name = newName;
    } else {
      this._snackBar.open(`Could not rename #${oldName}`, 'OK', { duration: 5000 });
    }
    this.cdr.markForCheck();
  }

  async archiveCurrentChannel(): Promise<void> {
    if (!this.selectedChannelId || !this.selectedChannelName) return;
    const channelId = this.selectedChannelId;
    if (!await this._ask({
      title: `Archive #${this.selectedChannelName}?`,
      message: 'The channel will be hidden but its messages are preserved.',
      confirmText: 'Archive',
    })) return;

    // Check if this is a child channel so we can navigate to its parent after archiving
    const currentChannel = this.personalChannels.find(c => c.id === channelId);
    const parentId = currentChannel?.parent_channel_id;

    const ok = await this.sessionService.archiveChannel(channelId, this.teamName);
    if (ok) {
      await this.loadNavTree();

      // Navigate to parent channel if this was a child, otherwise go to DM view
      if (parentId) {
        const parent = this.personalChannels.find(c => c.id === parentId);
        if (parent) {
          this.selectChannel(parent);
        } else {
          this._clearChannelView();
        }
      } else {
        this._clearChannelView();
      }
    }
    this.cdr.markForCheck();
  }

  private _clearChannelView(): void {
    const firstChannel = this.parentChannels[0];
    if (firstChannel) {
      this.selectChannel(firstChannel);
    } else {
      this.activeView = 'channel';
      this.selectedChannelId = null;
      this.selectedChannelName = null;
      this.selectedChannelFolderPath = null;
      this.selectedChannelAgentId = null;
      this.selectedChannelAgentName = null;
      this.channelMessages = [];
    }
  }

  async $changeChannelAgent(agentId: string | null): Promise<void> {
    if (!this.selectedChannelId) return;
    const ok = await this.sessionService.updateChannelAgent(this.selectedChannelId, agentId, this.teamName);
    if (!ok) return;
    this.selectedChannelAgentId = agentId;
    if (agentId) {
      const cached = this.agents.find(i => i.agent.id === agentId)?.agent
        || this.agentService.getAgents().find(a => a.id === agentId);
      this.selectedChannelAgentName = cached?.name || agentId;
    } else {
      this.selectedChannelAgentName = null;
    }
    // Update the cached NavChannel so sidebar stays in sync
    const ch = this.personalChannels.find(c => c.id === this.selectedChannelId);
    if (ch) ch.agent_id = agentId;
    this.cdr.markForCheck();
  }

  // --- Work Sessions ---

  /** Parse and strip ```channel-action``` blocks from assistant message content. */
  private extractChannelActions(content: string): { cleanContent: string; actions: { type: string; title: string; summary?: string; warm_up?: string }[] } {
    const actions: { type: string; title: string; summary?: string; warm_up?: string }[] = [];
    const cleanContent = content.replace(/```channel-action\n([\s\S]*?)```/g, (_match, json) => {
      try {
        const action = JSON.parse(json.trim());
        if (action.type) actions.push(action);
      } catch {}
      return '';
    }).trim();
    return { cleanContent, actions };
  }

  /** Handle a channel-action card click (e.g. "Start work session →"). */
  onChannelAction(action: { type: string; title: string; warm_up?: string; session_id?: string; agent_id?: string }): void {
    if (action.type === 'reopen-session') {
      const agentId = action.agent_id || (this.agents.find(a => a.agent.heartbeat_enabled) || this.agents[0])?.agent.id;
      if (!agentId) {
        this._snackBar.open('No agents available', 'OK', { duration: 3000 });
        return;
      }
      this.onOpenWorkSession.emit({
        sessionId: action.session_id!,
        agentId,
        title: action.title,
        // No channelId — session is already archived, archive chip should not show
      });
      return;
    }

    if (action.type === 'unarchive-channel') {
      const channelId = (action as any).channel_id;
      if (!channelId) return;
      this.sessionService.unarchiveChannel(channelId, this.teamName).then(async ok => {
        if (ok) {
          await this.loadNavTree();
          const channel = this.personalChannels.find(c => c.id === channelId);
          if (channel) this.selectChannel(channel);
          this.cdr.markForCheck();
        }
      });
      return;
    }

    if (action.type !== 'work-session') return;

    // Create a child channel instead of a work session — renders inline in this widget,
    // no new panel needed. The warm_up becomes the first message, pointing to the parent.
    const parentChannelId = this.selectedChannelId!;
    this.sessionService.createChannel(action.title, parentChannelId, this.teamName).then(async result => {
      if (result) {
        await this.loadNavTree();
        this.cdr.markForCheck();
        const channel: MuxSessionService.NavChannel = {
          id: result.id,
          name: result.name,
          type: result.type,
          team_id: result.team_id,
          agent_id: result.agent_id,
          parent_channel_id: result.parent_channel_id,
        };
        await this.selectChannel(channel);

        // Auto-send the warm_up as the first message, with a pointer to the parent channel
        const warmUpParts = [
          `This channel was started from #${parentChannelId}.`,
          `Before starting, read the recent messages from the parent channel to understand context.`,
          `Messages are at \`~/.lit/data/local/channels/${parentChannelId}/messages/\` — read the most recent few.`,
        ];
        if (action.warm_up) warmUpParts.push(`\n\n${action.warm_up}`);
        await this.onChannelSubmit({ content: warmUpParts.join(' '), inputModality: 'text' });
      }
    });
  }

  /** Hover "+" on a channel row: the user makes a channel under this one. This
   *  replaces "Start work session" and the agent-proposed work-session card as
   *  the way child channels come to exist (2026-10-01). */
  startChildChannel(parent: MuxSessionService.NavChannel, event?: Event): void {
    event?.stopPropagation();
    this.startChannelDraft(parent.id);
  }

  startChannelDraft(parentId: string | null): void {
    // The row lives in the channel list: show the list if it is collapsed.
    if (!this.sidebarOpen) this.toggleSidebar();
    this.channelDraft = { parentId, name: '', saving: false, openedAt: Date.now() };
    this.cdr.markForCheck();
    this._focusChannelDraft();
  }

  /** The row is rendered by the change above; from the header menu, the menu
   *  first closes and hands focus back to its button. Focus after both. */
  private _focusChannelDraft(): void {
    setTimeout(() => (document.querySelector('.nav-channel-draft input') as HTMLInputElement | null)?.focus(), 150);
  }

  /** Leaving the field: text = create; empty = cancel — except right after
   *  opening, when the closing menu takes focus once (take it back). */
  onChannelDraftBlur(parent?: MuxSessionService.NavChannel): void {
    const draft = this.channelDraft;
    if (!draft || draft.saving) return;
    if (draft.name.trim()) { this.commitChannelDraft(parent); return; }
    if (Date.now() - draft.openedAt < 600) { this._focusChannelDraft(); return; }
    this.cancelChannelDraft();
  }

  cancelChannelDraft(): void {
    if (this.channelDraft?.saving) return;
    this.channelDraft = null;
    this.cdr.markForCheck();
  }

  async commitChannelDraft(parent?: MuxSessionService.NavChannel): Promise<void> {
    const draft = this.channelDraft;
    if (!draft || draft.saving) return;
    const name = draft.name.trim();
    if (!name) { this.cancelChannelDraft(); return; }
    draft.saving = true;

    const result = await this.sessionService.createChannel(name, parent?.id, this.teamName);
    this.channelDraft = null;

    if (result) {
      await this.loadNavTree();
      this.cdr.markForCheck();
      const channel: MuxSessionService.NavChannel = {
        id: result.id,
        name: result.name,
        type: result.type,
        team_id: result.team_id,
        agent_id: result.agent_id,
        parent_channel_id: result.parent_channel_id,
      };
      this.selectChannel(channel);
    } else {
      this.cdr.markForCheck();
      const where = parent ? ` under #${parent.name}` : '';
      this._snackBar.open(`Could not create "${name}"${where}`, 'OK', { duration: 5000 });
    }
  }

  getActiveWorkSessions(channel: MuxSessionService.NavChannel): MuxSessionService.WorkSession[] {
    return (channel.work_sessions || []).filter(ws => ws.status === 'active');
  }

  openWorkSession(ws: MuxSessionService.WorkSession, channel: MuxSessionService.NavChannel): void {
    this.onOpenWorkSession.emit({
      sessionId: ws.session_id,
      agentId: ws.agent_id,
      title: ws.title,
      channelId: channel.id,
    });
  }

  // --- Pagination ---

  async onLoadMoreMessages(): Promise<void> {
    // TODO: implement DM message pagination (load older messages)
  }

  async onLoadMoreChannelMessages(): Promise<void> {
    // TODO: implement channel message pagination (load older messages)
  }

  async jumpToLatestMessages(): Promise<void> {
    this.viewingChannelHistory = false;
    await this.loadChannelMessages();
  }

  // --- Pins ---

  togglePinsPane(): void {
    this.pinsPaneOpen = !this.pinsPaneOpen;
    if (this.pinsPaneOpen) {
      this.pinsPaneView = 'graph';
      this.graphSelectedNode = null;
      this.graphNodeMessages = [];
    }
    this.cdr.markForCheck();
  }

  onGraphNodeSelected(node: { id: string; type: string; label: string; count: number; messages: { ref: string; excerpt: string; message_id?: string }[] }): void {
    this.graphSelectedNode = node;
    this.pinsPaneView = 'messages';
    this.graphMessageFilter = '';
    this.graphNodeMessages = node.messages.map(m => ({
      ref: m.ref,
      excerpt: m.excerpt || '',
      content: null,
      loading: false,
      messageId: m.message_id,
      channel: m.ref.split('/')[0],
    }));
    this.graphNodeMessagesLoading = false;
    this.cdr.markForCheck();
    this.setupGraphContentObserver();
  }

  graphPaneBack(): void {
    this.pinsPaneView = 'graph';
    this.graphSelectedNode = null;
    this.graphNodeMessages = [];
    this.graphMessageFilter = '';
    this.teardownGraphContentObserver();
    this.cdr.markForCheck();
  }

  private setupGraphContentObserver(): void {
    this.teardownGraphContentObserver();
    setTimeout(() => {
      const container = document.querySelector('.pins-list') as HTMLElement;
      if (!container) return;
      this.graphObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          const idx = parseInt(el.getAttribute('data-graph-msg-idx') || '-1', 10);
          if (idx < 0 || idx >= this.graphNodeMessages.length) continue;
          const msg = this.graphNodeMessages[idx];
          if (msg.content !== null || msg.loading) continue;
          msg.loading = true;
          this.cdr.markForCheck();
          const url = `${ConfigService.muxUrl}/knowledge-graph/message?ref=${encodeURIComponent(msg.ref)}&team=${encodeURIComponent(this.teamName || 'local')}`;
          this.http.get<{ content: string | null; message_id?: string; channel?: string }>(url).subscribe({
            next: (res) => {
              msg.content = res.content || null;
              msg.loading = false;
              if (res.message_id) msg.messageId = res.message_id;
              if (res.channel) msg.channel = res.channel;
              this.cdr.markForCheck();
            },
            error: () => {
              msg.content = null;
              msg.loading = false;
              this.cdr.markForCheck();
            }
          });
          this.graphObserver?.unobserve(el);
        }
      }, { root: container, rootMargin: '200px' });
      container.querySelectorAll('[data-graph-msg-idx]').forEach(el => this.graphObserver!.observe(el));
    }, 50);
  }

  private teardownGraphContentObserver(): void {
    if (this.graphObserver) {
      this.graphObserver.disconnect();
      this.graphObserver = null;
    }
  }

  onGraphFilterChange(): void {
    this.cdr.markForCheck();
    this.setupGraphContentObserver();
  }

  // --- Text search ---

  switchToSearchTab(): void {
    this.pinsPaneView = 'search';
    this.cdr.markForCheck();
    setTimeout(() => {
      const input = document.querySelector('.search-pane-input') as HTMLInputElement;
      input?.focus();
    }, 50);
  }

  switchToGraphTab(): void {
    this.pinsPaneView = 'graph';
    this.teardownSearchObserver();
    this.cdr.markForCheck();
  }

  onSearchInput(): void {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    if (!this.searchQuery.trim()) {
      this.searchResults = [];
      this.searchResultCount = null;
      this.searchLoading = false;
      this.cdr.markForCheck();
      return;
    }
    this.searchDebounceTimer = setTimeout(() => this.executeSearch(), 400);
  }

  async executeSearch(): Promise<void> {
    if (!this.searchQuery.trim() || !this.selectedChannelId) return;
    this.searchLoading = true;
    this.searchResults = [];
    this.searchResultCount = null;
    this.cdr.markForCheck();

    const url = `${ConfigService.muxUrl}/channels/${this.selectedChannelId}/messages/search?q=${encodeURIComponent(this.searchQuery)}&regex=${this.searchIsRegex}&team=${encodeURIComponent(this.teamName || 'local')}`;
    try {
      const resp = await lastValueFrom(
        this.http.get<{ results: { ref: string; excerpt: string; message_id?: string }[]; count: number }>(url)
      );
      this.searchResults = (resp.results || []).map(r => ({
        ref: r.ref,
        excerpt: r.excerpt || '',
        content: null,
        loading: false,
        messageId: r.message_id,
        channel: r.ref.split('/')[0],
      }));
      this.searchResultCount = resp.count ?? this.searchResults.length;
      this.searchLoading = false;
      this.cdr.markForCheck();
      this.setupSearchObserver();
    } catch (err) {
      this.searchLoading = false;
      this.searchResultCount = 0;
      this.cdr.markForCheck();
    }
  }

  private setupSearchObserver(): void {
    this.teardownSearchObserver();
    setTimeout(() => {
      const container = document.querySelector('.search-results-list') as HTMLElement;
      if (!container) return;
      this.searchObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          const idx = parseInt(el.getAttribute('data-search-idx') || '-1', 10);
          if (idx < 0 || idx >= this.searchResults.length) continue;
          const msg = this.searchResults[idx];
          if (msg.content !== null || msg.loading) continue;
          msg.loading = true;
          this.cdr.markForCheck();
          const url = `${ConfigService.muxUrl}/knowledge-graph/message?ref=${encodeURIComponent(msg.ref)}&team=${encodeURIComponent(this.teamName || 'local')}`;
          this.http.get<{ content: string | null; message_id?: string; channel?: string }>(url).subscribe({
            next: (res) => {
              msg.content = res.content || null;
              msg.loading = false;
              if (res.message_id) msg.messageId = res.message_id;
              if (res.channel) msg.channel = res.channel;
              this.cdr.markForCheck();
            },
            error: () => {
              msg.content = null;
              msg.loading = false;
              this.cdr.markForCheck();
            }
          });
          this.searchObserver?.unobserve(el);
        }
      }, { root: container, rootMargin: '200px' });
      container.querySelectorAll('[data-search-idx]').forEach(el => this.searchObserver!.observe(el));
    }, 50);
  }

  private teardownSearchObserver(): void {
    if (this.searchObserver) {
      this.searchObserver.disconnect();
      this.searchObserver = null;
    }
  }

  get graphEffectiveChannel(): string | null {
    return this.graphScopeAll ? null : this.selectedChannelId;
  }

  toggleGraphScope(): void {
    this.graphScopeAll = !this.graphScopeAll;
    this.cdr.markForCheck();
    setTimeout(() => {
      if (this.embeddedGraph?.reloadGraph) {
        this.embeddedGraph.reloadGraph();
      }
    }, 0);
  }

  // ============= Calendar =============

  switchToCalendarTab(): void {
    this.pinsPaneView = 'calendar';
    this.cdr.markForCheck();
    if (Object.keys(this.calendarDates).length === 0) {
      this.loadCalendarData();
    }
  }

  async loadCalendarData(): Promise<void> {
    if (!this.selectedChannelId) return;
    this.calendarLoading = true;
    this.cdr.markForCheck();
    const url = `${ConfigService.muxUrl}/channels/${this.selectedChannelId}/messages/calendar?team=${encodeURIComponent(this.teamName || 'local')}`;
    try {
      const resp = await lastValueFrom(this.http.get<{ dates: { [key: string]: number } }>(url));
      this.calendarDates = resp.dates || {};
      this.calendarLoading = false;
      this.cdr.markForCheck();
      setTimeout(() => this.matCalendar?.updateTodaysDate(), 50);
    } catch {
      this.calendarLoading = false;
      this.cdr.markForCheck();
    }
  }

  get calendarSelectedDateObj(): Date | null {
    if (!this.calendarSelectedDate) return null;
    return new Date(this.calendarSelectedDate + 'T00:00:00');
  }

  calendarDateClass = (date: Date): string => {
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const count = this.calendarDates[key] || 0;
    if (count === 0) return '';
    const level = this.calendarHeatLevel(count);
    return `cal-heat-${level}`;
  };

  onCalendarDateSelected(date: Date): void {
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    this.calendarSelectDate(key);
  }

  onCalendarMonthChanged(_date: Date): void {
    // mat-calendar handles navigation; data is already loaded for all dates
  }

  calendarMaxCount(): number {
    const counts = Object.values(this.calendarDates);
    return counts.length > 0 ? Math.max(...counts) : 1;
  }

  calendarHeatLevel(count: number): number {
    if (count === 0) return 0;
    const max = this.calendarMaxCount();
    if (max <= 1) return 1;
    return Math.ceil((count / max) * 4);
  }

  async calendarSelectDate(date: string): Promise<void> {
    if (!this.selectedChannelId) return;
    this.calendarSelectedDate = date;
    this.calendarSelectedHour = null;
    this.calendarDayMessages = [];
    this.calendarDayLoading = true;
    this.cdr.markForCheck();

    const url = `${ConfigService.muxUrl}/channels/${this.selectedChannelId}/messages/calendar?date=${date}&team=${encodeURIComponent(this.teamName || 'local')}`;
    try {
      const resp = await lastValueFrom(this.http.get<{ messages: any[] }>(url));
      this.calendarDayMessages = resp.messages || [];
      this.calendarDayLoading = false;
      this.cdr.markForCheck();
    } catch {
      this.calendarDayLoading = false;
      this.cdr.markForCheck();
    }
  }

  get calendarHourCounts(): number[] {
    const counts = new Array(24).fill(0);
    for (const msg of this.calendarDayMessages) {
      if (msg.timestamp) {
        try {
          const hour = new Date(msg.timestamp).getHours();
          counts[hour]++;
        } catch {}
      }
    }
    return counts;
  }

  calendarSelectHour(hour: number): void {
    this.calendarSelectedHour = hour;
    const hourMsgs = this.calendarDayMessages.filter(m => {
      try { return new Date(m.timestamp).getHours() === hour; } catch { return false; }
    });
    this.calendarViewMessages = hourMsgs.map(m => ({
      ref: `${this.selectedChannelId}/${this.calendarSelectedDate}/${m.filename}`,
      excerpt: `${m.from} · ${new Date(m.timestamp).toLocaleTimeString()}`,
      content: null,
      loading: false,
      messageId: m.id,
      channel: this.selectedChannelId || undefined,
    }));
    this.pinsPaneView = 'calendar-messages';
    this.cdr.markForCheck();
    this.setupCalendarMsgObserver();
  }

  calendarSelectAllDay(): void {
    this.calendarSelectedHour = null;
    this.calendarViewMessages = this.calendarDayMessages.map(m => ({
      ref: `${this.selectedChannelId}/${this.calendarSelectedDate}/${m.filename}`,
      excerpt: `${m.from} · ${new Date(m.timestamp).toLocaleTimeString()}`,
      content: null,
      loading: false,
      messageId: m.id,
      channel: this.selectedChannelId || undefined,
    }));
    this.pinsPaneView = 'calendar-messages';
    this.cdr.markForCheck();
    this.setupCalendarMsgObserver();
  }

  calendarBack(): void {
    this.calendarSelectedHour = null;
    this.calendarViewMessages = [];
    this.teardownCalendarMsgObserver();
    this.pinsPaneView = 'calendar';
    this.cdr.markForCheck();
  }

  private setupCalendarMsgObserver(): void {
    this.teardownCalendarMsgObserver();
    setTimeout(() => {
      const container = document.querySelector('.calendar-results-list') as HTMLElement;
      if (!container) return;
      this.calendarObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          const idx = parseInt(el.getAttribute('data-cal-idx') || '-1', 10);
          if (idx < 0 || idx >= this.calendarViewMessages.length) continue;
          const msg = this.calendarViewMessages[idx];
          if (msg.content !== null || msg.loading) continue;
          msg.loading = true;
          this.cdr.markForCheck();
          const url = `${ConfigService.muxUrl}/knowledge-graph/message?ref=${encodeURIComponent(msg.ref)}&team=${encodeURIComponent(this.teamName || 'local')}`;
          this.http.get<{ content: string | null; message_id?: string; channel?: string }>(url).subscribe({
            next: (res) => {
              msg.content = res.content || null;
              msg.loading = false;
              if (res.message_id) msg.messageId = res.message_id;
              this.cdr.markForCheck();
            },
            error: () => {
              msg.content = null;
              msg.loading = false;
              this.cdr.markForCheck();
            }
          });
          this.calendarObserver?.unobserve(el);
        }
      }, { root: container, rootMargin: '200px' });
      container.querySelectorAll('[data-cal-idx]').forEach(el => this.calendarObserver!.observe(el));
    }, 50);
  }

  private teardownCalendarMsgObserver(): void {
    if (this.calendarObserver) {
      this.calendarObserver.disconnect();
      this.calendarObserver = null;
    }
  }

  formatCalendarDate(date: string): string {
    try {
      const d = new Date(date + 'T00:00:00');
      return d.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' });
    } catch {
      return date;
    }
  }

  formatHourLabel(hour: number): string {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h = hour % 12 || 12;
    return `${h} ${ampm}`;
  }

  formatGraphMsgRef(ref: string): string {
    const parts = ref.split('/');
    if (parts.length >= 3) {
      const channel = parts[0];
      const date = parts[1];
      const file = parts[2].replace('.md', '').replace(/_/g, ' ');
      return `#${channel} · ${date} · ${file}`;
    }
    return ref;
  }

  async loadPinnedMessages(): Promise<void> {
    const channelId = this.activeView === 'dm'
      ? (this.selectedAgentId ? `dm-${this.selectedAgentId}` : null)
      : this.selectedChannelId;
    if (!channelId) return;
    const pins = await this.sessionService.getChannelPinnedMessages(channelId, this.teamName);
    // Newest pin-worthy first; a user message is stored with its <context> envelope.
    this.pinnedMessages = (pins || [])
      .map(p => ({ ...p, content: this._stripContextEnvelope(this.unescapeContent(p.content || '')) }))
      .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
    this.cdr.markForCheck();
  }

  // --- Pinned tab (Search pane) ---

  switchToPinnedTab(): void {
    this.pinsPaneView = 'pinned';
    this.teardownSearchObserver();
    this.cdr.markForCheck();
    this.loadPinnedMessages();
  }

  /** The header pin chip: open the pane straight on the pinned list. */
  openPinnedTab(): void {
    this.pinsPaneOpen = true;
    this.switchToPinnedTab();
  }

  /** Jump to a pinned message; in a channel it is loaded if it has scrolled out of history. */
  openPinned(pin: MuxSessionService.PinnedMessage): void {
    if (this.activeView === 'channel' && this.selectedChannelId) {
      this.navigateToGraphMessage({ ref: '', messageId: pin.id, channel: this.selectedChannelId });
    } else {
      this.seekToMessage(pin);
    }
  }

  formatPinTime(timestamp: string): string {
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  async unpinFromPane(pin: MuxSessionService.PinnedMessage): Promise<void> {
    const channelId = this.activeView === 'dm'
      ? `dm-${this.selectedAgentId}`
      : this.selectedChannelId;
    if (!channelId) return;
    await this.sessionService.unpinChannelMessage(channelId, pin.id, this.teamName);
    this.pinnedMessages = this.pinnedMessages.filter(p => p.id !== pin.id);
    // Also update the message in the chat view
    const msgs = this.activeView === 'dm' ? this.messages : this.channelMessages;
    const msg = msgs.find(m => m.messageId === pin.id);
    if (msg) msg.pinned = false;
    this.cdr.markForCheck();
  }

  hasToolCalls(content: string): boolean {
    if (!content || typeof content !== 'string') return false;
    if (content.includes('\x02TOOLJSON')) return true;
    if (/^TOOLJSON\{/m.test(content)) return true;
    if (/(?<!`)\[TOOL_START\]🔧 Tool:/.test(content)) return true;
    return false;
  }

  isLongContent(content: string): boolean {
    return content && content.length > 400;
  }

  seekToMessage(pin: MuxSessionService.PinnedMessage): void {
    this.scrollToMessageId(pin.id);
  }

  private scrollToMessageId(messageId: string, smooth = true): void {
    const el = document.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement;
    if (el) {
      const scrollContainer = el.closest('.content') as HTMLElement;
      if (scrollContainer) {
        const elRect = el.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const offsetInContainer = elRect.top - containerRect.top + scrollContainer.scrollTop;
        const centeredScroll = offsetInContainer - (containerRect.height / 2) + (elRect.height / 2);
        scrollContainer.scrollTo({ top: centeredScroll, behavior: smooth ? 'smooth' : 'instant' });
      }
      el.classList.add('highlight-flash');
      setTimeout(() => el.classList.remove('highlight-flash'), 2000);
    }
  }

  async navigateToGraphMessage(msg: { ref: string; messageId?: string; channel?: string }): Promise<void> {
    if (!msg.messageId) {
      this._snackBar.open('Cannot navigate — message ID not available', '', { duration: 3000 });
      return;
    }

    const channelId = msg.channel || this.selectedChannelId;
    if (!channelId) return;

    // If the message from a different channel, switch to it first
    if (channelId !== this.selectedChannelId) {
      this._snackBar.open(`Message is in #${channelId} — switching channels not yet supported`, '', { duration: 3000 });
      return;
    }

    // Check if message is already in the DOM
    const existing = document.querySelector(`[data-message-id="${msg.messageId}"]`);
    if (existing) {
      this.scrollToMessageId(msg.messageId);
      return;
    }

    // Message not in loaded history — load around it from the backend
    this._snackBar.open('Loading message context...', '', { duration: 1500 });
    const muxUrl = ConfigService.muxUrl;
    try {
      const resp = await lastValueFrom(
        this.http.get<{ messages: any[]; target_index: number; has_older: boolean; has_newer: boolean; channel_dir?: string }>(
          `${muxUrl}/channels/${channelId}/messages?around=${encodeURIComponent(msg.messageId)}&limit=50${this.teamName ? '&team=' + encodeURIComponent(this.teamName) : ''}`
        )
      );

      const raw = (resp.messages || []).filter((m: any) => m.metadata?.source !== 'reaction');
      this.channelMessages = raw.map((m: any) => {
        let rawContent = m.direction === 'in' ? this._stripContextEnvelope(m.content) : m.content;
        const { cleanContent: content, actions } = m.direction === 'out'
          ? this.extractChannelActions(rawContent)
          : { cleanContent: rawContent, actions: [] };
        const { content: finalContent, links } = ChatScreenComponent.extractLinks(content);
        const isError = m.direction === 'out' && this._isInterruptedContent(rawContent);
        const isCancelled = m.direction === 'out' && (this._isCancelledContent(rawContent) || m.metadata?.cancelled === true);
        return {
          isSelf: m.direction === 'in',
          content: finalContent,
          timestamp: m.timestamp,
          isFinal: true as const,
          messageId: m.id,
          pinned: m.pinned === true,
          reactions: m.reactions,
          ...(links.length ? { links } : {}),
          ...(actions.length ? { channelActions: actions } : {}),
          ...(m.direction === 'in' ? { read: m.read === true, handledIn: m.metadata?.handled_in, handledAt: m.metadata?.handled_at } : {}),
          ...(isError ? { isError: true } : {}),
          ...(isCancelled ? { isCancelled: true } : {}),
          ...(m.file_path ? { turnPath: m.file_path } : {}),
          ...(m.metadata?.session_id ? { sessionId: m.metadata.session_id } : {}),
          ...(m.metadata?.ttfb_ms ? { durationMs: m.metadata.ttfb_ms } : {}),
          ...(m.metadata?.fcp_ms != null ? { fcpMs: m.metadata.fcp_ms } : {}),
          ...(m.metadata?.model ? { model: m.metadata.model } : {}),
          ...(m.metadata?.input_tokens != null ? { inputTokens: m.metadata.input_tokens } : {}),
          ...(m.metadata?.output_tokens != null ? { outputTokens: m.metadata.output_tokens } : {}),
          ...(m.metadata?.cache_tokens != null ? { cacheTokens: m.metadata.cache_tokens } : {}),
          ...(m.metadata?.transcript_path ? { transcriptPath: m.metadata.transcript_path } : {}),
          ...(m.metadata?.orchestration ? { orchestration: m.metadata.orchestration } : {}),
          ...(m.from && m.direction === 'out' ? { agentName: this.agents.find(a => a.agent.id === m.from)?.agent.name ?? m.from } : {}),
          ...(m.from && m.direction === 'in' ? { agentName: m.from } : {}),
          ...(m.content_truncated ? { contentTruncated: true, contentLength: m.content_length } : {}),
        };
      });
      this.channelMessages = HeartbeatWidgetComponent.orderChannelHistory(this.channelMessages);

      this.hasMoreChannelMessages = resp.has_older;
      this.viewingChannelHistory = !resp.has_newer ? false : true;
      this.cdr.markForCheck();

      // After change detection, snap to the target message (instant, not smooth —
      // the content was fully replaced so smooth animation is disorienting)
      setTimeout(() => this.scrollToMessageId(msg.messageId!, false), 100);
    } catch (err) {
      console.error('Failed to load message context:', err);
      this._snackBar.open('Failed to load message context', '', { duration: 3000 });
    }
  }

  // ============= Orchestrations =============

  toggleOrchestrationPanel(): void {
    this.orchestrationPanelOpen = !this.orchestrationPanelOpen;
    if (this.orchestrationPanelOpen && this.orchestrationList.length === 0) {
      this.loadOrchestrations();
    }
    this.cdr.markForCheck();
  }

  async loadOrchestrations(): Promise<void> {
    try {
      const team = this.teamName || 'everyone';
      const resp = await lastValueFrom(
        this.http.get<any[]>(`${ConfigService.muxUrl}/orchestrations?team=${team}`)
      );
      this.orchestrationList = resp || [];
      this.cdr.markForCheck();
    } catch (err) {
      console.error('Failed to load orchestrations:', err);
    }
  }

  async selectOrchestration(slug: string): Promise<void> {
    this.orchestrationSelectedSlug = slug;
    try {
      const team = this.teamName || 'everyone';
      this.orchestrationSchema = await lastValueFrom(
        this.http.get<any>(`${ConfigService.muxUrl}/orchestrations/${slug}/reflect?team=${team}`)
      );
      this.orchestrationParams = {};
      const saved = JSON.parse(localStorage.getItem('lit-orch-params') || '{}');
      for (const field of this.orchestrationSchema.fields || []) {
        if (saved[slug]?.[field.name] !== undefined) {
          this.orchestrationParams[field.name] = saved[slug][field.name];
        } else if (field.default !== undefined) {
          this.orchestrationParams[field.name] = field.default;
        }
      }
      this.cdr.markForCheck();
    } catch (err) {
      console.error('Failed to reflect orchestration:', err);
    }
  }

  isOrchestrationReady(): boolean {
    if (!this.orchestrationSchema?.fields) return false;
    for (const field of this.orchestrationSchema.fields) {
      if (field.default === undefined) {
        const val = this.orchestrationParams[field.name];
        if (val === undefined || val === null || val === '') return false;
      }
    }
    return true;
  }

  async startOrchestration(): Promise<void> {
    console.log('[ORCH] startOrchestration called', {
      slug: this.orchestrationSelectedSlug,
      channelId: this.selectedChannelId,
      params: this.orchestrationParams,
    });
    if (!this.orchestrationSelectedSlug || !this.selectedChannelId) {
      console.error('[ORCH] Cannot start: missing slug or channelId');
      this._snackBar.open('Cannot start: no channel or orchestration selected', '', { duration: 3000 });
      return;
    }
    try {
      const team = this.teamName || 'everyone';
      const saved = JSON.parse(localStorage.getItem('lit-orch-params') || '{}');
      saved[this.orchestrationSelectedSlug!] = { ...this.orchestrationParams };
      localStorage.setItem('lit-orch-params', JSON.stringify(saved));
      await lastValueFrom(
        this.http.post<any>(
          `${ConfigService.muxUrl}/channels/${this.selectedChannelId}/orchestrations/${this.orchestrationSelectedSlug}/start`,
          { team, params: this.orchestrationParams }
        )
      );
      this.cdr.markForCheck();
    } catch (err: any) {
      const detail = err?.error?.detail || 'Failed to start orchestration';
      this._snackBar.open(detail, '', { duration: 3000 });
    }
  }

  /**
   * Start an orchestration in response to a `start_orchestration` chat action
   * (AICS-260). The channel agent emits this via the unauthenticated
   * POST /channels/{id}/actions endpoint; we run the real start under the
   * connected user's authenticated session (same call the launcher panel makes),
   * gated by the chat-startable allowlist, and ack the result back over the
   * channel WebSocket so the agent gets the launcher's status.
   */
  private async _startOrchestrationFromAction(data: any): Promise<void> {
    const slug: string = data.slug;
    const channelId: string | null = data.channel_id || this.selectedChannelId;
    const ack = (payload: any) => {
      if (this.channelWs && this.channelWs.readyState === WebSocket.OPEN && data.action_id) {
        this.channelWs.send(JSON.stringify({
          type: 'action_result',
          action_id: data.action_id,
          action: 'start_orchestration',
          slug,
          ...payload,
        }));
      }
    };

    if (!HeartbeatWidgetComponent.CHAT_STARTABLE_ORCHESTRATIONS.includes(slug)) {
      console.warn(`[start_orchestration] rejected non-allowlisted slug: ${slug}`);
      ack({ success: false, message: `Orchestration '${slug}' is not allowed to start from chat` });
      return;
    }
    if (!channelId) {
      ack({ success: false, message: 'No channel to start the orchestration in' });
      return;
    }

    try {
      const team = data.team || this.teamName || 'everyone';
      const res: any = await lastValueFrom(
        this.http.post<any>(
          `${ConfigService.muxUrl}/channels/${channelId}/orchestrations/${slug}/start`,
          { team, params: data.params || {} }
        )
      );
      ack({ success: true, name: res?.name, state: res?.state });
      this.cdr.markForCheck();
    } catch (err: any) {
      const detail = err?.error?.detail || 'Failed to start orchestration';
      this._snackBar.open(detail, '', { duration: 4000 });
      ack({ success: false, message: detail });
    }
  }

  async abortOrchestration(): Promise<void> {
    if (!this.selectedChannelId) return;
    try {
      await lastValueFrom(
        this.http.post<any>(
          `${ConfigService.muxUrl}/channels/${this.selectedChannelId}/orchestrations/abort`, {}
        )
      );
    } catch (err) {
      console.error('Failed to abort orchestration:', err);
    }
  }

  async resolveOrchestrationGate(choice: string): Promise<void> {
    if (!this.selectedChannelId) return;
    try {
      await lastValueFrom(
        this.http.post<any>(
          `${ConfigService.muxUrl}/channels/${this.selectedChannelId}/orchestrations/gate`,
          { choice, notes: this.orchestrationGateNotes }
        )
      );
      this.orchestrationGateNotes = '';
    } catch (err) {
      console.error('Failed to resolve gate:', err);
    }
  }

  private _orchAgentListCache: { id: string; name: string }[] = [];
  private _orchAgentListKey = '';

  getAgentsForOrchestrationField(): { id: string; name: string }[] {
    const key = this.agents.map(a => `${a.agent.id}:${a.agent.heartbeat_enabled}`).join(',');
    if (key !== this._orchAgentListKey) {
      this._orchAgentListKey = key;
      this._orchAgentListCache = this.agents
        .filter(a => a.agent.heartbeat_enabled)
        .map(a => ({ id: a.agent.id, name: a.agent.name }));
    }
    return this._orchAgentListCache;
  }

  // --- Channel path ---

  openChannelFiles(): void {
    const grid = this._widget.grid;
    if (!grid || !this.selectedChannelFolderPath) return;
    this._ngZone.run(() => {
      (grid as any)._addWidget('FilesWidget',
        { position: { referencePanel: this.id, direction: 'right' } },
        {
          baseInputs: { initialPath: this.selectedChannelFolderPath, host: this.channelHost() },
          extraOutputs: {
            onEditFile: (grid as any).$editFile?.bind(grid),
            onPreviewFile: (grid as any).$previewFile?.bind(grid),
            onViewMarkdown: (grid as any).$viewMarkdown?.bind(grid),
            onViewCsv: (grid as any).$viewCsv?.bind(grid),
          },
        });
    });
  }

  async $copyChannelPath(): Promise<void> {
    const path = this.activeView === 'dm' ? this.channelPath : this.selectedChannelDir;
    if (!path) return;
    try {
      await navigator.clipboard.writeText(path);
      this._snackBar.open('Channel path copied', '', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } catch (err) {
      console.error('Failed to copy path:', err);
    }
  }
}
